import { useState, useEffect, useRef, useCallback, useMemo, memo } from 'react';
import { useParams, useNavigate, useLocation, useBlocker } from 'react-router-dom';
import { useIntl } from 'react-intl';
import PropTypes from 'prop-types';

// material-ui
import Alert from '@mui/material/Alert';
// import AlertTitle from '@mui/material/AlertTitle';
import Badge from '@mui/material/Badge';
import Backdrop from '@mui/material/Backdrop';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
// import InputAdornment from '@mui/material/InputAdornment';
import InputLabel from '@mui/material/InputLabel';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import OutlinedInput from '@mui/material/OutlinedInput';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme, alpha } from '@mui/material/styles';

// ant design icons
import LeftOutlined from '@ant-design/icons/LeftOutlined';
import UserOutlined from '@ant-design/icons/UserOutlined';
import ClockCircleOutlined from '@ant-design/icons/ClockCircleOutlined';
import TableOutlined from '@ant-design/icons/TableOutlined';
// import DownloadOutlined from '@ant-design/icons/DownloadOutlined'; // re-enable with the Export button
import BellOutlined from '@ant-design/icons/BellOutlined';
import PlayCircleOutlined from '@ant-design/icons/PlayCircleOutlined';
import CheckOutlined from '@ant-design/icons/CheckOutlined';
import CloudSyncOutlined from '@ant-design/icons/CloudSyncOutlined';
import SaveOutlined from '@ant-design/icons/SaveOutlined';
import WarningOutlined from '@ant-design/icons/WarningOutlined';
import CloseOutlined from '@ant-design/icons/CloseOutlined';
// import DisconnectOutlined from '@ant-design/icons/DisconnectOutlined';
import CloudUploadOutlined from '@ant-design/icons/CloudUploadOutlined';
import SyncOutlined from '@ant-design/icons/SyncOutlined';
import CalendarOutlined from '@ant-design/icons/CalendarOutlined';
import FileTextOutlined from '@ant-design/icons/FileTextOutlined';
import CheckCircleOutlined from '@ant-design/icons/CheckCircleOutlined';
// import EyeOutlined from '@ant-design/icons/EyeOutlined';
// import EyeInvisibleOutlined from '@ant-design/icons/EyeInvisibleOutlined';
import LoginOutlined from '@ant-design/icons/LoginOutlined';
import StopOutlined from '@ant-design/icons/StopOutlined';
import UpOutlined from '@ant-design/icons/UpOutlined';
import DownOutlined from '@ant-design/icons/DownOutlined';
import MoreOutlined from '@ant-design/icons/MoreOutlined';
import MessageOutlined from '@ant-design/icons/MessageOutlined';
import PlusOutlined from '@ant-design/icons/PlusOutlined';

// project imports
import MainCard from 'components/MainCard';
import useAuth from 'hooks/useAuth';
import useHiddenCatalogue from 'hooks/useHiddenCatalogue';
import usePageAccess from 'hooks/usePageAccess';
import ReadOnlyBanner from 'components/ReadOnlyBanner';
import ServerImage from 'components/ServerImage';
import SpeechTextField from 'components/SpeechTextField';
import CommunicationDiaryTab from 'sections/shared/child-detail/CommunicationDiaryTab';
import { completeSession, getSession } from 'services/api/sessions';
import { listClients, getDatasheetPreferences } from 'services/api/clients';

// Therapist form-type codes don't all match the supervisor config codes (this
// page uses 'abc', 'ot-data-sheet', 'slp-data-sheet' while the supervisor uses
// 'abc-analysis', 'ot', 'speech'). Translate so the visibility filter matches.
// Types not in this map have no supervisor toggle and stay hidden when the
// filter is active.
const FORM_TYPE_TO_CONFIG_CODE = {
  'cold-probe': 'cold-probe',
  'multi-trial': 'multi-trial',
  'daily-mand': 'daily-mand',
  'behavior-tracking': 'behavior-tracking',
  'behavior-interval': 'behavior-interval',
  abc: 'abc-analysis',
  'injury-report': 'injury-report',
  'ot-data-sheet': 'ot',
  'slp-data-sheet': 'speech',
  'maintenance-probe': 'maintenance-probe',
  'target-tracking': 'target-tracking'
};

// Session-mode tabs that stay in the tab strip. Everything else is moved to the
// overflow menu and opens in a modal so the primary strip stays uncluttered.
const PRIMARY_SESSION_TAB_TYPES = new Set(['cold-probe', 'multi-trial', 'daily-mand', 'behavior-tracking']);

// Shortcut sheets that open as a CLOSABLE session tab (beside the four defaults)
// instead of a modal when picked. Session-scoped: the promotion lives in React state
// only (sessionTabTypes), so a fresh session shows these back in the Shortcut menu.
// The rest of the Shortcut — Add Target, Communication Diary, and the other sheets —
// keep the modal. NOTE this screen's ABC type is 'abc' (not 'abc-analysis').
const TABBABLE_OVERFLOW_TYPES = new Set(['behavior-interval', 'abc', 'injury-report', 'ot-data-sheet', 'slp-data-sheet']);

// Sheets with a single layout: they take no `viewMode` prop, so the
// Individual/Grid toggle moved nothing. The toolbar is hidden for them - and
// with it a duplicate title, since each of these renders its own heading.
const NO_VIEW_MODE_TYPES = new Set(['ot-data-sheet', 'slp-data-sheet']);

// sessionStorage key + reader for a session's promoted Shortcut tabs. Keyed by the
// session id so promoted tabs survive a re-render/remount WITHIN the same session
// yet a different session — or direct access with no session — starts fresh.
const sessionTabsStorageKey = (sd) => {
  const sid = sd?.id || sd?.sessionId;
  return sid ? `dataCollection:sessionTabs:${sid}` : null;
};
const readStoredSessionTabs = (sd) => {
  const key = sessionTabsStorageKey(sd);
  if (!key) return [];
  try {
    const raw = sessionStorage.getItem(key);
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
};

/**
 * Sheet type -> catalogue page code (security.rbac_app_page.code), so switching a
 * sheet off in Features and Pages removes its tab here.
 *
 * Distinct from FORM_TYPE_TO_CONFIG_CODE above, which maps to the supervisor's
 * per-child datasheet preference codes. Two different questions: that one is "does
 * this child use this sheet", this one is "does the platform offer it at all".
 *
 * A type absent from this map is never gated. Only the sheets that exist as
 * catalogue pages under Data Tracking are listed — add the page first, then the
 * entry here.
 */
const FORM_TYPE_TO_PAGE_CODE = {
  'cold-probe': 'cold_probe',
  'multi-trial': 'multi_trial',
  'daily-mand': 'daily_mand',
  'behavior-tracking': 'behavior_tracking',
  // The six below open as MODALS from the Shortcut menu and never change the URL, so
  // the route guard cannot reach them — they are gated purely by this code mapping.
  // Every type in FORM_TYPE_TO_CONFIG_CODE must appear here or it is ungateable:
  // an unmapped sheet is deliberately never hidden, which silently made half of them
  // impossible to switch off.
  'behavior-interval': 'behavior_interval',
  abc: 'abc_analysis',
  'injury-report': 'injury_report',
  'ot-data-sheet': 'ot_daily_activity',
  'slp-data-sheet': 'speech_daily_activity',
  'maintenance-probe': 'maintenance_probe'
};
import { createTarget, executeRuleEngine } from 'services/api/iep';
import { listSkills, listSkillDomains } from 'services/api/skills';
import { draftToTargetPayload } from 'sections/shared/child-detail/GoalsTargetsTab';
import SessionBehaviorsDialog from 'sections/shared/data-collection/SessionBehaviorsDialog';
import {
  transformClient,
  transformTargetForColdProbe,
  transformTargetForTracking,
  transformTargetForMaintenance
} from 'utils/apiTransformers';
import { saveDataEntry, updateDataEntry, forceSyncAll } from 'utils/offlineFirstDataLayer';
import { promoteDraftEntries } from 'utils/syncManager';
import { discardSessionCollectedData } from 'utils/discardSessionData';
import { cacheClinicalData, getCachedClinicalData, saveSessionUpdate, updateCachedSession } from 'utils/offlineDB';
import { isFutureSessionDate } from 'utils/normalizeDate';
import { activityLines, formatActivityList } from 'utils/activityList';
import { fetchClinicalBundle, clinicalBundleToCache } from 'services/clinicalBundle';
import { openSnackbar } from 'api/snackbar';
import { getDataCollections } from 'api/dataCollection';

// Import different data collection modes
import ColdProbeMode from 'sections/therapist/data-collection/ColdProbeMode';
import MultiTrialMode from 'sections/therapist/data-collection/MultiTrialMode';
import DailyMandMode from 'sections/therapist/data-collection/DailyMandMode';
import BehaviorTrackingMode from 'sections/therapist/data-collection/BehaviorTrackingMode';
import ABCMode from 'sections/therapist/data-collection/ABCMode';
import MaintenanceProbeMode from 'sections/therapist/data-collection/MaintenanceProbeMode';
import SkillTrackingMode from 'sections/therapist/data-collection/SkillTrackingMode';
import TargetTrackingMode from 'sections/therapist/data-collection/TargetTrackingMode';
import InjuryReportMode from 'sections/therapist/data-collection/InjuryReportMode';
import ParentTrainingMode from 'sections/therapist/data-collection/ParentTrainingMode';
import ToiletingProgramMode from 'sections/therapist/data-collection/ToiletingProgramMode';
import BehaviorIntervalMode from 'sections/therapist/data-collection/BehaviorIntervalMode';
import LessonPlanMode from 'sections/therapist/data-collection/LessonPlanMode';
import SessionPlanMode from 'sections/therapist/data-collection/SessionPlanMode';
import OTDataSheetMode from 'sections/therapist/data-collection/OTDataSheetMode';
import SLPDataSheetMode from 'sections/therapist/data-collection/SLPDataSheetMode';

// Persist a session's start timestamp in sessionStorage so the timer survives
// component unmount/remount (e.g. collapsing the header card) and even page
// reloads within the same browser tab. Keyed by session id so multiple
// sessions / child switches don't clobber each other.
const sessionTimerStorageKey = (sessionKey) => `dataCollection:sessionStart:${sessionKey || 'no-session'}`;

const getOrInitSessionStart = (sessionKey) => {
  const key = sessionTimerStorageKey(sessionKey);
  try {
    const existing = Number(sessionStorage.getItem(key));
    if (existing && !Number.isNaN(existing)) return existing;
    const now = Date.now();
    sessionStorage.setItem(key, String(now));
    return now;
  } catch {
    // sessionStorage can throw in private mode / quota exceeded — fall back to
    // an in-memory start so the timer still ticks for this mount.
    return Date.now();
  }
};

const clearSessionTimerStart = (sessionKey) => {
  try {
    sessionStorage.removeItem(sessionTimerStorageKey(sessionKey));
  } catch {
    // ignore — best-effort cleanup
  }
};

// Isolated timer component — owns its own state so ticking doesn't re-render the parent.
// The start timestamp is read from (and lazily written to) sessionStorage so collapsing
// the header card / re-rendering branches doesn't reset the displayed elapsed time.
const SessionTimer = memo(({ sessionKey, variant = 'default', sx = {} }) => {
  const [elapsed, setElapsed] = useState(() => Math.floor((Date.now() - getOrInitSessionStart(sessionKey)) / 1000));

  useEffect(() => {
    const start = getOrInitSessionStart(sessionKey);
    setElapsed(Math.floor((Date.now() - start) / 1000));
    const id = setInterval(() => {
      setElapsed(Math.floor((Date.now() - start) / 1000));
    }, 1000);
    return () => clearInterval(id);
  }, [sessionKey]);

  const hrs = Math.floor(elapsed / 3600);
  const mins = Math.floor((elapsed % 3600) / 60);
  const secs = elapsed % 60;
  const display = `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;

  return (
    <Typography
      variant={variant === 'large' ? 'h6' : 'body2'}
      sx={{
        fontFamily: 'monospace',
        fontWeight: 700,
        letterSpacing: 1,
        ...sx
      }}
    >
      {display}
    </Typography>
  );
});

SessionTimer.displayName = 'SessionTimer';

const DataCollection = ({
  isOfflineMode = false,
  offlineChildren = [],
  offlineUser = null,
  isOnline = true,
  unsyncedCount = 0,
  onFormSaved = null
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const intl = useIntl();
  const { childId: urlChildId, type } = useParams();
  const { user } = useAuth();
  const downSM = useMediaQuery(theme.breakpoints.down('sm'));
  // Tablet portrait and below: the overflow datasheet dialog goes full-screen so the
  // form has the whole viewport (fields fit, and the content area can scroll a focused
  // input above the on-screen keyboard) instead of a cramped, keyboard-covered modal.
  const downMd = useMediaQuery(theme.breakpoints.down('md'));
  // Touch tablets in landscape sit above `md` (e.g. iPad ~1024px) yet still get their
  // modal swallowed by the on-screen keyboard. Detect a coarse (touch-primary) pointer
  // so those go full-screen too, while mouse-driven laptops keep the centered dialog.
  // any-pointer (not pointer): an iPad with a trackpad reports its primary pointer as
  // `fine`, so `(pointer: coarse)` would miss it. any-pointer is true if ANY input is touch.
  const isTouchTablet = useMediaQuery('(any-pointer: coarse) and (max-width: 1400px)');
  const overflowDialogFullScreen = downMd || isTouchTablet;
  // Individual vs Grid entry, kept PER datasheet type (keyed by tab) so switching
  // to grid on e.g. Mand doesn't flip Multi-Trial too — each sheet remembers its
  // own choice for the session. Derived viewMode/setViewMode live just after
  // `currentTab` is declared (below). Defaults to 'individual'.
  const [viewModeByType, setViewModeByType] = useState({});
  const [actualStartTime] = useState(() => new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }));
  const [child, setChild] = useState(null);
  const [allChildren, setAllChildren] = useState([]);

  // Get therapist info from logged-in user (online mode) or offlineUser (offline mode)
  const therapistName = isOfflineMode ? offlineUser?.first_name || offlineUser?.username || '' : user?.firstName || user?.name || '';
  const therapistId = isOfflineMode ? offlineUser?.id || offlineUser?.user_id || null : user?.id || user?.userId || null;
  const { isLoggedIn, login } = useAuth();
  const [autoSaveStatus, setAutoSaveStatus] = useState('saved'); // 'saving', 'saved'
  const [submitModalOpen, setSubmitModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loginSyncDialogOpen, setLoginSyncDialogOpen] = useState(false);
  // End Session overlay. Its own flag, not isSubmitting: it must drop for good when the
  // offline login-to-sync dialog opens, since dismissing that dialog stays on this page.
  const [endingOverlay, setEndingOverlay] = useState(false);
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginSuccess, setLoginSuccess] = useState(false);
  const [selectedSheetFilter, setSelectedSheetFilter] = useState('all');
  const [switchChildModalOpen, setSwitchChildModalOpen] = useState(false);
  const [pendingChildId, setPendingChildId] = useState(null);
  const [offlineSelectedChildId, setOfflineSelectedChildId] = useState('');
  const [isHeaderCollapsed, setIsHeaderCollapsed] = useState(false);

  // Session details from calendar navigation
  const sessionDetails = location.state?.sessionDetails || null;
  const viewExistingData = location.state?.viewExistingData || false;

  // Navigation guard — block sidebar navigation when session is active
  const [sessionEnded, setSessionEnded] = useState(false);
  const [postSubmitRoute, setPostSubmitRoute] = useState(null);
  const isSessionActive = !!sessionDetails && !viewExistingData && !sessionEnded;
  // Allow same-page redirects (only the :type segment changing for the same
  // :childId, e.g. when the auto-redirect swaps from a disabled datasheet to
  // the first enabled one) without triggering the leave-session dialog. Also
  // always let logout / unauthenticated navigations through — there's nothing
  // for the user to "stay" on once they've signed out.
  const blockNavigation = useCallback(
    ({ currentLocation, nextLocation }) => {
      if (!isSessionActive) return false;
      if (!isLoggedIn) return false;
      if (nextLocation.pathname.startsWith('/login')) return false;
      const samePagePrefix = urlChildId ? `/therapist/data-collection/${urlChildId}/` : null;
      if (samePagePrefix && currentLocation.pathname.startsWith(samePagePrefix) && nextLocation.pathname.startsWith(samePagePrefix)) {
        return false;
      }
      return true;
    },
    [isSessionActive, isLoggedIn, urlChildId]
  );
  const blocker = useBlocker(blockNavigation);
  const [navGuardOpen, setNavGuardOpen] = useState(false);

  // Existing data loaded from backend for completed sessions
  const [existingSessionData, setExistingSessionData] = useState([]);
  const [isLoadingExistingData, setIsLoadingExistingData] = useState(false);
  const [fetchedSessionNotes, setFetchedSessionNotes] = useState(null);

  // Session notes for end session dialog — use ref (not state) so typing
  // doesn't re-render the entire 3000-line component on every keystroke
  const sessionNotesRef = useRef('');
  // The session-note box is DICTATABLE, and SpeechTextField appends the transcript
  // to `value` - so the field has to be controlled. The ref is kept in step because
  // two save paths already read it (session complete, and the auto-save on leave).
  const [sessionNotesDraft, setSessionNotesDraft] = useState('');

  // Use URL childId for online mode, state for offline mode
  const childId = isOfflineMode ? offlineSelectedChildId : urlChildId;

  // Data collection state for each sheet type
  const [dataCollectionState, setDataCollectionState] = useState({
    'cold-probe': { hasData: false },
    'multi-trial': { hasData: false },
    'daily-mand': { hasData: false },
    'behavior-tracking': { hasData: false },
    'behavior-interval': { hasData: false },
    abc: { hasData: false },
    'maintenance-probe': { hasData: false },
    'skill-tracking': { hasData: false },
    'injury-report': { hasData: false },
    'parent-training': { hasData: false },
    'toileting-program': { hasData: false },
    'lesson-plan': { hasData: false },
    'session-plan': { hasData: false }
  });

  // Shared ABC records state - accessible by both ABCMode and InjuryReportMode
  const [abcRecords, setAbcRecords] = useState([]);

  // Set of supervisor-enabled datasheet codes for this child. `null` means we
  // haven't loaded preferences yet — in that state we don't filter (avoid an
  // empty-tab flash). Once loaded, only tabs whose config code is in the set
  // are shown.
  const [enabledConfigCodes, setEnabledConfigCodes] = useState(null);

  // Overflow menu + modal state for non-primary data sheets (Behavior Interval,
  // ABC, Injury Report, OT, Speech, Skill Tracking). The overflow form renders
  // inside a Dialog instead of the main area; we still keep it mounted so
  // form state is preserved when the user closes & reopens.
  const [overflowMenuAnchor, setOverflowMenuAnchor] = useState(null);
  const [overflowFormType, setOverflowFormType] = useState(null);
  // Sheets the user promoted from the Shortcut into the tab strip THIS session, in
  // click order (see TABBABLE_OVERFLOW_TYPES). Seeded from sessionStorage so they
  // survive a re-render/remount within the same session; the load/save effects below
  // keep it in sync and a different session starts fresh.
  const [sessionTabTypes, setSessionTabTypes] = useState(() => readStoredSessionTabs(sessionDetails));
  const [overflowFormDirty, setOverflowFormDirty] = useState(false);
  const [confirmCloseOverflowOpen, setConfirmCloseOverflowOpen] = useState(false);
  // True while an interval-recording session is mid-flight in the overflow
  // modal. Used to gate the outer "Save" with a confirm dialog — clicking
  // Save mid-recording would otherwise persist nothing usable.
  const [overflowRecordingActive, setOverflowRecordingActive] = useState(false);
  // When true, the outer Save click opens a confirm dialog instead of
  // closing the modal. "End & Save" triggers BehaviorIntervalMode's
  // imperative action to commit the recording, then closes.
  const [confirmEndAndSaveOpen, setConfirmEndAndSaveOpen] = useState(false);
  // Plain-ref bridge to BehaviorIntervalMode's imperative actions (currently
  // just endAndSaveCurrentRecording). The child reassigns .current each
  // render so we always get the latest closure.
  const behaviorIntervalActionsRef = useRef(null);

  // Persistent behavior-tracking timer indicator. When the therapist starts a
  // duration timer in Behavior Tracking, BehaviorTrackingMode reports it here
  // so we can show a "recording" chip in the tab strip — even after the
  // therapist switches to Cold Probe or another tab.
  const [activeBehaviorTimer, setActiveBehaviorTimer] = useState(null);
  const handleBehaviorTimerChange = useCallback((info) => {
    setActiveBehaviorTimer(info);
  }, []);

  const touchStartX = useRef(0);
  const touchEndX = useRef(0);
  const touchStartY = useRef(0);
  const touchEndY = useRef(0);
  const autoSaveTimeoutRef = useRef(null);
  // When the OLDEST still-unsaved change happened. Lets the debounce flush on a max-wait
  // (see triggerAutoSave) so a continuously-edited sheet like Daily Mand still persists
  // mid-burst instead of only after a 2s pause — otherwise the live supervisor view lags.
  const autoSaveMaxWaitRef = useRef(null);
  // Ensures the discard-on-reload check (below) runs at most once per mount.
  const discardCheckedRef = useRef(false);
  const entryIdsRef = useRef({}); // Track IndexedDB entry IDs per form type for upsert
  // Latest form data per form type for THIS session. Fed back into a sheet as
  // `initialData` so reopening an overflow sheet (e.g. Maintenance Probe, which
  // remounts via the dialog) restores what was already collected this session.
  const lastFormDataRef = useRef({});
  const pendingSaveRef = useRef(null); // Track latest unsaved form data for flush on End Session
  // Targets the therapist added during THIS session. They're created pending
  // (need supervisor approval for future sessions) but are usable immediately
  // in the current session — so the data-collection gate lets them through
  // even though they're not yet approved. Resets on reload / next session.
  const sessionAddedTargetIdsRef = useRef(new Set());

  // Determine default tab based on context
  const getDefaultTab = () => {
    if (type) return type;
    // If coming from calendar session, default to cold-probe or multi-trial based on child
    if (sessionDetails) {
      const childIdNum = parseInt(childId);
      const useMultiTrial = childIdNum % 2 === 0;
      return useMultiTrial ? 'multi-trial' : 'cold-probe';
    }
    // If accessing directly (not from session), default to maintenance-probe
    return 'maintenance-probe';
  };

  // Which sheets are switched off — GLOBALLY in Features and Pages OR for this therapist's
  // role in Role Definitions. useHiddenCatalogue combines both; the session sheet tabs and
  // the "Collect Additional Data" list (both derived from availableForms below) answer to it.
  const hiddenCatalogue = useHiddenCatalogue();
  // Full-Read on a specific Data Tracking page (e.g. cold_probe) makes THAT sheet
  // view-only for everyone, including the therapist collecting the session — read the
  // existing data, but no add/edit. Keyed per sheet type via FORM_TYPE_TO_PAGE_CODE.
  const { readOnly: isCodeReadOnly } = usePageAccess();
  const sheetReadOnly = useCallback((formType) => isCodeReadOnly(FORM_TYPE_TO_PAGE_CODE[formType]), [isCodeReadOnly]);

  /* THE DIARY, DURING THE SESSION — from the Shortcut menu, and again from the end-of-
   * session summary. Both open it over this screen instead of navigating: a session is
   * running, a form may hold data that has not been flushed yet (pendingSaveRef), and
   * leaving the route to read the diary would put all of that at risk.
   *
   * Gated on `session_communication_diary` — the in-session diary's own switch, under Data
   * Tracking in Features and Components, which is also where the sheets in this session are
   * switched — and on the `communication_diary` FEATURE, which closes every surface at once.
   * Deliberately NOT on the child profile's tab code: closing that tab is a statement about
   * the child profile, not about what a therapist may reach mid-session.
   *
   * Offline sessions never offer it: the diary is server-held and there is nothing cached.
   */
  const diaryHidden = useMemo(
    () => (hiddenCatalogue ? ['session_communication_diary', 'communication_diary'].some((c) => hiddenCatalogue.codeHidden(c)) : false),
    [hiddenCatalogue]
  );
  const [diaryOpen, setDiaryOpen] = useState(false);
  const diaryAvailable = Boolean(childId) && !diaryHidden && !isOfflineMode;
  // Shortcut -> Add Behavior writes the IEP, so it is offered on the online page only
  // (the dialog also says so if the connection drops mid-session).
  const addBehaviorAvailable = Boolean(childId) && !isOfflineMode;
  const [currentTab, setCurrentTab] = useState(getDefaultTab());

  // Individual/Grid choice for the currently active sheet, backed by the
  // per-type map above so each datasheet keeps its own selection.
  // Phones show every sheet in the Individual view only - the Grid layouts are too
  // wide for a phone. The toggle is hidden there; tablets and desktops keep it.
  const gridAllowed = !downSM;
  const viewModeFor = (type) => (gridAllowed ? viewModeByType[type] || 'individual' : 'individual');
  const viewMode = viewModeFor(currentTab);
  const setViewMode = (value) => setViewModeByType((prev) => ({ ...prev, [currentTab]: value }));

  // Data collection forms available for this child
  // Forms shown depend on whether user came from a calendar session or directly
  const allFormsForContext = useMemo(() => {
    const forms = [];

    if (sessionDetails) {
      // Show both Cold Probe and Multi-Trial — both are valid acquisition-data
      // methods and the therapist picks whichever matches the session plan.
      forms.push({
        type: 'cold-probe',
        name: intl.formatMessage({ id: 'dataCollection.formType.coldProbe' }),
        status: 'not-started',
        hasNotification: false
      });
      forms.push({
        type: 'multi-trial',
        name: intl.formatMessage({ id: 'dataCollection.formType.multiTrial' }),
        status: 'not-started',
        hasNotification: false
      });

      // Add session-specific forms
      forms.push(
        {
          type: 'daily-mand',
          name: intl.formatMessage({ id: 'dataCollection.formType.dailyMand' }),
          status: 'not-started',
          hasNotification: false
        },
        {
          type: 'behavior-tracking',
          name: intl.formatMessage({ id: 'dataCollection.formType.behaviorTracking' }),
          status: 'not-started',
          hasNotification: false
        },
        {
          type: 'behavior-interval',
          name: intl.formatMessage({ id: 'dataCollection.formType.behaviorInterval' }),
          status: 'not-started',
          hasNotification: false
        },
        {
          type: 'abc',
          name: intl.formatMessage({ id: 'dataCollection.formType.abcAnalysis' }),
          status: 'not-started',
          hasNotification: false
        },
        {
          type: 'skill-tracking',
          name: intl.formatMessage({ id: 'dataCollection.formType.skillTracking' }),
          status: 'not-started',
          hasNotification: false
        },
        {
          type: 'target-tracking',
          name: intl.formatMessage({ id: 'dataCollection.formType.targetTracking' }),
          status: 'not-started',
          hasNotification: false
        },
        {
          type: 'injury-report',
          name: intl.formatMessage({ id: 'dataCollection.formType.injuryReport' }),
          status: 'not-started',
          hasNotification: false
        },
        // Toileting Program — hidden per request, keep registration commented for easy re-enable.
        // {
        //   type: 'toileting-program',
        //   name: intl.formatMessage({ id: 'dataCollection.formType.toiletingProgram' }),
        //   status: 'not-started',
        //   hasNotification: false
        // },
        {
          type: 'ot-data-sheet',
          name: intl.formatMessage({ id: 'dataCollection.formType.otDataSheet' }),
          status: 'not-started',
          hasNotification: false
        },
        {
          type: 'slp-data-sheet',
          name: intl.formatMessage({ id: 'dataCollection.formType.slpDataSheet' }),
          status: 'not-started',
          hasNotification: false
        },
        // Maintenance Probe — not in PRIMARY_SESSION_TAB_TYPES, so it lands in the
        // overflow "Shortcut" menu rather than the primary tab strip. Still gated by
        // the child's datasheet config via the supervisor's enabled-config set
        // (config code 'maintenance-probe').
        {
          type: 'maintenance-probe',
          name: intl.formatMessage({ id: 'dataCollection.formType.maintenanceProbe' }),
          status: 'not-started',
          hasNotification: false
        }
        // Lesson Tracking (session-plan) — hidden per request, keep registration commented for easy re-enable.
        // ,{
        //   type: 'session-plan',
        //   name: intl.formatMessage({ id: 'dataCollection.formType.lessonTracking' }),
        //   status: 'not-started',
        //   hasNotification: false
        // }
      );
    } else {
      // Direct access (not from session) - show only non-session forms
      forms.push(
        {
          type: 'maintenance-probe',
          name: intl.formatMessage({ id: 'dataCollection.formType.maintenanceProbe' }),
          status: 'not-started',
          hasNotification: false
        },
        {
          type: 'parent-training',
          name: intl.formatMessage({ id: 'dataCollection.formType.parentTraining' }),
          status: 'not-started',
          hasNotification: false
        },
        {
          type: 'lesson-plan',
          name: intl.formatMessage({ id: 'dataCollection.formType.lessonPlan' }),
          status: 'not-started',
          hasNotification: false
        },
        {
          type: 'skill-tracking',
          name: intl.formatMessage({ id: 'dataCollection.formType.skillTracking' }),
          status: 'not-started',
          hasNotification: false
        },
        {
          type: 'target-tracking',
          name: intl.formatMessage({ id: 'dataCollection.formType.targetTracking' }),
          status: 'not-started',
          hasNotification: false
        }
      );
    }

    return forms;
  }, [sessionDetails, intl]);

  // Final tab list = context list ∩ supervisor-enabled set. While preferences
  // are still loading (`enabledConfigCodes === null`), show only the tab that
  // matches the current URL so the strip doesn't flash the full list before
  // filtering. Offline mode skips the API and uses the context list directly.
  // One predicate for "is this sheet switched on", used by the list filter below AND
  // by the modal-opening guard. Keeping them on the same rule is what stops the menu
  // and the modal disagreeing.
  const isSheetSwitchedOn = useCallback(
    (formType) => {
      const code = FORM_TYPE_TO_PAGE_CODE[formType];
      // Null filter means not yet known, so nothing is hidden. A sheet with no
      // catalogue page cannot be switched off at all — every type in
      // FORM_TYPE_TO_CONFIG_CODE should have an entry in FORM_TYPE_TO_PAGE_CODE.
      return !hiddenCatalogue || !code || !hiddenCatalogue.codeHidden(code);
    },
    [hiddenCatalogue]
  );

  const availableForms = useMemo(() => {
    // Sheets switched off in Features and Pages drop out of BOTH the tab strip and
    // the overflow menu, so the filter is applied here rather than further down.
    // This is a separate gate from the supervisor's per-child preferences below:
    // that is "does this child use this sheet", this is "does the platform offer
    // it at all".
    const switchedOn = (f) => isSheetSwitchedOn(f.type);

    if (!enabledConfigCodes) {
      if (isOfflineMode) return allFormsForContext.filter(switchedOn);
      const currentForm = allFormsForContext.find((f) => f.type === type);
      const fallback = currentForm ? [currentForm] : allFormsForContext.slice(0, 1);
      return fallback.filter(switchedOn);
    }
    return allFormsForContext.filter((f) => {
      const code = FORM_TYPE_TO_CONFIG_CODE[f.type];
      return code && enabledConfigCodes.has(code) && switchedOn(f);
    });
  }, [allFormsForContext, enabledConfigCodes, isOfflineMode, type, isSheetSwitchedOn]);

  // Split into primary tabs (rendered in the strip) and overflow (rendered in
  // a popover menu + modal). Only applies when we have a session; direct-access
  // (no sessionDetails) keeps the full strip as before.
  const { primaryForms, overflowForms } = useMemo(() => {
    if (!sessionDetails) return { primaryForms: availableForms, overflowForms: [] };
    const primary = [];
    const overflow = [];
    for (const f of availableForms) {
      if (PRIMARY_SESSION_TAB_TYPES.has(f.type)) primary.push(f);
      else overflow.push(f);
    }
    // Keep the strip full. When one of the four default primary sheets is switched
    // off for this user (Role Definitions / Features and Pages) or this child, its
    // slot would otherwise leave the strip with fewer than four tabs. Promote the
    // next overflow sheet into the freed slot so the strip always shows up to
    // PRIMARY_SESSION_TAB_TYPES.size tabs, with the promoted sheets sitting after
    // the remaining defaults. Every form already renders inline (see the
    // availableForms map further down), so a promoted sheet simply opens in the
    // strip instead of the Shortcut modal — no data path changes.
    while (primary.length < PRIMARY_SESSION_TAB_TYPES.size && overflow.length > 0) {
      primary.push(overflow.shift());
    }
    return { primaryForms: primary, overflowForms: overflow };
  }, [availableForms, sessionDetails]);

  // Sheets promoted from the Shortcut into the tab strip THIS session, resolved to
  // form objects in click order, plus the Shortcut menu list with those removed so a
  // promoted sheet appears in exactly one place. The full strip is the four defaults
  // (or promoted fills) followed by the Shortcut tabs. The form content below already
  // renders every availableForms entry inline, so a promoted sheet simply shows when
  // its tab is selected — no data-path change.
  const addedTabForms = useMemo(
    () => sessionTabTypes.map((t) => overflowForms.find((f) => f.type === t)).filter(Boolean),
    [sessionTabTypes, overflowForms]
  );
  const addedTabTypes = useMemo(() => new Set(addedTabForms.map((f) => f.type)), [addedTabForms]);
  const menuForms = useMemo(() => overflowForms.filter((f) => !addedTabTypes.has(f.type)), [overflowForms, addedTabTypes]);
  const tabForms = useMemo(() => [...primaryForms, ...addedTabForms], [primaryForms, addedTabForms]);

  // Session-scoped, remount-proof. Reload the promoted tabs whenever the session id
  // (encoded in the key) changes — a different session starts empty. Keying off the id
  // rather than a raw reset means a transient location.state blip reloads the SAME
  // session's stored tabs instead of blanking the strip. The save effect persists it.
  const sessionTabsKey = sessionTabsStorageKey(sessionDetails);
  useEffect(() => {
    if (!sessionTabsKey) return;
    setSessionTabTypes(readStoredSessionTabs(sessionDetails));
  }, [sessionTabsKey, sessionDetails]);
  useEffect(() => {
    if (!sessionTabsKey) return;
    try {
      sessionStorage.setItem(sessionTabsKey, JSON.stringify(sessionTabTypes));
    } catch {
      /* sessionStorage unavailable — non-fatal */
    }
  }, [sessionTabTypes, sessionTabsKey]);

  // Close a Shortcut tab (the red ✕): drop it from the strip so it returns to the
  // Shortcut menu, and if it was the active tab fall back to the first default.
  const handleCloseSessionTab = (formType) => {
    setSessionTabTypes((prev) => prev.filter((t) => t !== formType));
    setCurrentTab((cur) => (cur === formType ? primaryForms[0]?.type || '' : cur));
  };

  // Keep the selected tab valid. A promoted Shortcut tab writes its type into the
  // URL (handleTabChange), but the promotion is session-scoped, so on a refresh the
  // strip resets and currentTab could name a sheet no longer in it — leaving nothing
  // highlighted. Fall back to the first tab in that case.
  useEffect(() => {
    if (!tabForms.length) return;
    if (!tabForms.some((f) => f.type === currentTab)) setCurrentTab(tabForms[0].type);
  }, [tabForms, currentTab]);

  // Fetch this child's supervisor-saved datasheet preferences. Skipped in
  // offline mode (no network) — offline therapists see every tab in their
  // cached context list, same as before.
  useEffect(() => {
    if (isOfflineMode || !childId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await getDatasheetPreferences(childId);
        if (cancelled) return;
        setEnabledConfigCodes(new Set(res?.enabled_datasheets || []));
      } catch (error) {
        console.warn('[DataCollection] Could not load datasheet preferences, showing all:', error.message);
        if (!cancelled) setEnabledConfigCodes(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [childId, isOfflineMode]);

  // Initialize offline children — prefer the child from sessionDetails (calendar navigation),
  // then the URL childId, then fall back to the first cached child
  useEffect(() => {
    if (isOfflineMode && offlineChildren.length > 0 && !offlineSelectedChildId) {
      const sessionChildId = sessionDetails?.childId;
      const targetId = sessionChildId || urlChildId;
      const matchedChild = targetId ? offlineChildren.find((c) => String(c.id) === String(targetId)) : null;
      const selectedChild = matchedChild || offlineChildren[0];
      setOfflineSelectedChildId(selectedChild.id);
      setAllChildren(offlineChildren);
      setChild(selectedChild);
    }
  }, [isOfflineMode, offlineChildren, offlineSelectedChildId, sessionDetails, urlChildId]);

  // Load children for online mode
  useEffect(() => {
    if (!isOfflineMode) {
      loadChild();
    }
  }, [childId, isOfflineMode]); // eslint-disable-line react-hooks/exhaustive-deps

  // Update child when offline selection changes
  useEffect(() => {
    if (isOfflineMode && offlineSelectedChildId) {
      const foundChild = offlineChildren.find((c) => c.id === offlineSelectedChildId);
      setChild(foundChild);
      setAllChildren(offlineChildren);
    }
  }, [isOfflineMode, offlineSelectedChildId, offlineChildren]);

  useEffect(() => {
    // Skip tab validation in view mode — tabs are hidden
    if (!isOfflineMode && !viewExistingData) {
      if (type) {
        // Check if the type is valid for current context
        const isValidType = availableForms.some((f) => f.type === type);
        if (isValidType) {
          setCurrentTab(type);
        } else {
          // Type not valid for this context, redirect to first available
          const firstForm = availableForms[0];
          if (firstForm) {
            navigate(`/therapist/data-collection/${childId}/${firstForm.type}`, {
              replace: true,
              state: location.state // Preserve session details if present
            });
          }
        }
      } else if (childId) {
        // If no type specified, navigate to first available form
        const firstForm = availableForms[0];
        if (firstForm) {
          navigate(`/therapist/data-collection/${childId}/${firstForm.type}`, {
            replace: true,
            state: location.state // Preserve session details if present
          });
        }
      }
    }
  }, [type, childId, availableForms, navigate, isOfflineMode, viewExistingData, location.state]);

  // Fetch existing data collections and session notes when viewing a completed session
  useEffect(() => {
    const sessionId = sessionDetails?.id || sessionDetails?.sessionId;
    if (viewExistingData && sessionId) {
      setIsLoadingExistingData(true);

      // Fetch data collections
      const dataPromise = getDataCollections(sessionId)
        .then((data) => {
          const items = data?.items || data || [];
          // Deduplicate: keep only the latest record per datasheet type.
          // Exception: injury reports, session notes, and lesson plans can have
          // multiple entries per session — use record.id as the unique key so
          // each entry is preserved.
          const MULTI_ENTRY_FORMS = new Set(['injury-report', 'session-notes', 'lesson-plan']);
          const latestByType = new Map();
          items.forEach((item) => {
            const formType = item.raw_data?.formType;
            const typeKey = MULTI_ENTRY_FORMS.has(formType)
              ? item.id // unique per record — no deduplication
              : item.datasheet_type_id || item.datasheet_type?.id || formType || item.id;
            const existing = latestByType.get(typeKey);
            if (!existing || new Date(item.collected_at) > new Date(existing.collected_at)) {
              latestByType.set(typeKey, item);
            }
          });
          setExistingSessionData(Array.from(latestByType.values()));
        })
        .catch((err) => {
          console.error('[DataCollection] Failed to load existing data:', err);
          setExistingSessionData([]);
        });

      // Fetch session details for notes
      const sessionPromise = getSession(sessionId)
        .then((session) => {
          setFetchedSessionNotes(session?.notes || null);
        })
        .catch((err) => {
          console.error('[DataCollection] Failed to load session notes:', err);
        });

      Promise.all([dataPromise, sessionPromise]).finally(() => {
        setIsLoadingExistingData(false);
      });
    }
  }, [viewExistingData, sessionDetails]);

  /* DISCARD-ON-RELOAD.
   *
   * Product decision: reloading the collection page mid-session STARTS THE SESSION OVER —
   * prior entries are cleared for the therapist AND any supervisor watching the live view,
   * rather than leaving this screen blank while the supervisor still sees the pre-reload
   * data. (The alternative, restoring the data into the sheets, was not chosen.)
   *
   * How a reload is told apart from a normal open: a clean SPA unmount clears the marker
   * below; a browser REFRESH unloads the page without running React cleanup, so a marker
   * still equal to this session on mount means this very session's page was reloaded.
   * This fires only on a hard refresh — not on first open or SPA navigation.
   */
  useEffect(() => {
    const sid = sessionDetails?.id || sessionDetails?.sessionId;
    if (viewExistingData || !sid || discardCheckedRef.current) return;
    discardCheckedRef.current = true; // check once, when the session id is first known

    // Was THIS page reloaded? The Navigation Timing API reports it directly and
    // survives the reload, unlike in-memory React state. Pair it with a sessionStorage
    // marker (persists across a refresh in the same tab) so we only discard when the page
    // that was reloaded is this very session's — not an unrelated reload elsewhere.
    const navEntry = performance.getEntriesByType && performance.getEntriesByType('navigation')[0];
    const isReload = navEntry ? navEntry.type === 'reload' : performance.navigation?.type === 1;
    const KEY = 'ayro:lastCollectionSession';
    const wasOnThisSession = sessionStorage.getItem(KEY) === String(sid);
    sessionStorage.setItem(KEY, String(sid));

    if (isReload && wasOnThisSession) {
      discardSessionCollectedData(sid, { collectedById: therapistId }).then(({ deleted }) => {
        entryIdsRef.current = {};
        lastFormDataRef.current = {};
        if (deleted > 0) {
          openSnackbar({
            open: true,
            message: intl.formatMessage({
              id: 'dataCollection.reloadCleared',
              defaultMessage: 'Session was reloaded — previously collected data was cleared. Starting fresh.'
            }),
            variant: 'alert',
            alert: { color: 'info' }
          });
        }
      });
    }
  }, [viewExistingData, sessionDetails, therapistId, intl]);

  const loadChild = async () => {
    try {
      // Try real API first
      const personId = user?.person_id;
      const res = await listClients({ therapist_id: personId, is_active: true }).catch(() => null);
      let children;
      // An empty or failed result yields no children. Sample children are
      // never substituted here — this screen writes clinical data.
      children = res && (res.items || res.length) ? (res.items || res).map(transformClient) : [];
      setAllChildren(children);

      if (childId) {
        // If childId is provided, find and set that child (support both UUID and int ID)
        const foundChild = children.find((c) => String(c.id) === String(childId));
        if (foundChild) {
          setChild(foundChild);
        } else {
          // Fallback: build minimal child from session details or childId
          const name = sessionDetails?.childName || intl.formatMessage({ id: 'common.unknown' });
          setChild({
            id: childId,
            name,
            child_name: name,
            firstName: name.split('(')[1]?.replace(')', '').trim() || name.split(' ')[0] || '',
            initials: (name.match(/\((\w)/) || [, name.charAt(0)])[1].toUpperCase()
          });
        }
      } else if (children.length > 0) {
        // No childId provided - redirect to first child with default tab
        const firstChild = children[0];
        const defaultTab = 'maintenance-probe'; // Default tab when accessing directly
        navigate(`/therapist/data-collection/${firstChild.id}/${defaultTab}`, {
          replace: true,
          state: location.state
        });
      }
    } catch (error) {
      console.error('Error loading child:', error);
      // Fallback on total failure: build minimal child so UI doesn't get stuck
      if (childId) {
        const name = sessionDetails?.childName || intl.formatMessage({ id: 'common.unknown' });
        setChild({
          id: childId,
          name,
          child_name: name,
          firstName: name.split('(')[1]?.replace(')', '').trim() || name.split(' ')[0] || '',
          initials: (name.match(/\((\w)/) || [, name.charAt(0)])[1].toUpperCase()
        });
      }
    }
  };

  const openOverflowMenu = (event) => setOverflowMenuAnchor(event.currentTarget);
  const closeOverflowMenu = () => setOverflowMenuAnchor(null);

  const handleOverflowItemSelect = (formType) => {
    setOverflowMenuAnchor(null);
    // Refuse a sheet that is switched off in Features and Pages.
    //
    // The menu is already filtered, so in normal use this cannot fire. It is here
    // because these sheets open as MODALS and never change the URL: the route guard
    // cannot see them, so filtering the menu is the ONLY thing standing between a
    // switched-off sheet and a therapist. Anything that ever opens one by another
    // path — a deep link, a restored session, a keyboard shortcut — would bypass it.
    if (!isSheetSwitchedOn(formType)) {
      openSnackbar({
        open: true,
        message: 'That data sheet has been switched off for this platform.',
        variant: 'alert',
        alert: { color: 'warning' }
      });
      return;
    }
    // The five tabbable sheets open as a closable session tab beside the defaults
    // instead of a modal (session-scoped — see TABBABLE_OVERFLOW_TYPES).
    if (TABBABLE_OVERFLOW_TYPES.has(formType)) {
      setSessionTabTypes((prev) => (prev.includes(formType) ? prev : [...prev, formType]));
      setCurrentTab(formType);
      return;
    }
    setOverflowFormDirty(false);
    setOverflowFormType(formType);
  };

  // Shortcut → Add Target. Opens the supervisor TargetDialog in goal-select
  // mode; on save we POST to the active IEP's chosen goal, then reload clinical
  // data so the new target shows up across Cold Probe / Multi Trial / etc.
  const handleOpenDiary = () => {
    setOverflowMenuAnchor(null);
    setDiaryOpen(true);
  };

  const handleOpenAddTarget = () => {
    setOverflowMenuAnchor(null);
    if (!addTargetCtx.iepId || addTargetCtx.goals.length === 0) {
      openSnackbar({
        open: true,
        message: 'No active IEP goals to attach a target to.',
        variant: 'alert',
        alert: { color: 'warning' }
      });
      return;
    }
    // Open the Add Target view - the Target Tracking datasheet UI plus an "Add Target"
    // button that opens the add dialog.
    setAddTargetViewOpen(true);
  };

  const handleAddTargetSave = async (draft, selectedGoal) => {
    if (!addTargetCtx.iepId || !selectedGoal?.iepGoalRowId) {
      setAddTargetOpen(false);
      return;
    }
    try {
      const payload = draftToTargetPayload(draft);
      const created = await createTarget(addTargetCtx.iepId, selectedGoal.iepGoalRowId, payload);
      // Usable in THIS session immediately (no approval needed for the session
      // it was added in); still pending for future sessions until a supervisor
      // approves it in the Goals & Targets tab.
      if (created?.id) sessionAddedTargetIdsRef.current.add(created.id);
      setAddTargetOpen(false);
      await loadClinicalData(childId);
      openSnackbar({
        open: true,
        message: 'Target added for this session — pending supervisor approval for future sessions.',
        variant: 'alert',
        alert: { color: 'success' }
      });
    } catch (err) {
      console.error('[DataCollection] add target failed:', err);
      openSnackbar({
        open: true,
        message: err?.response?.data?.detail || err?.message || 'Failed to add target.',
        variant: 'alert',
        alert: { color: 'error' }
      });
      // Re-throw so the Add Target dialog keeps itself open for a retry instead of
      // reporting a false success.
      throw err;
    }
  };

  // Backdrop / ESC → always route through the confirm dialog so the user can't
  // dismiss the form accidentally and lose data. Explicit Cancel / Save buttons
  // on the form itself bypass the warning (those are deliberate actions).
  const requestCloseOverflowModal = () => {
    setConfirmCloseOverflowOpen(true);
  };

  const discardOverflowChanges = () => {
    setConfirmCloseOverflowOpen(false);
    setOverflowFormDirty(false);
    setOverflowFormType(null);
    setOverflowRecordingActive(false);
  };

  // Save closes the modal — actual persistence happens through the form's
  // autosave hook (triggerAutoSave). Marking not-dirty avoids the warning on
  // a follow-up close.
  const handleSaveOverflowModal = () => {
    setConfirmCloseOverflowOpen(false);
    setOverflowFormDirty(false);
    setOverflowFormType(null);
    setOverflowRecordingActive(false);
  };

  // Progress for the overflow checklist: count sheets that have data across BOTH the
  // menu and any promoted tabs, so the green badge reflects data entered on a
  // promoted tab too.
  const overflowProgress = useMemo(() => {
    const total = overflowForms.length;
    const done = overflowForms.filter((f) => dataCollectionState[f.type]?.hasData).length;
    return { total, done, pct: total ? Math.round((done / total) * 100) : 0 };
  }, [overflowForms, dataCollectionState]);

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
    if (!isOfflineMode) {
      // Update URL without triggering React Router re-render cycle
      window.history.replaceState(
        { ...window.history.state, usr: location.state },
        '',
        `/therapist/data-collection/${childId}/${newValue}`
      );
    }
    // Scroll to top when tab changes
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleChildChange = (event) => {
    const newChildId = event.target.value;
    if (isOfflineMode) {
      // For offline mode, switch directly without confirmation
      setOfflineSelectedChildId(newChildId);
      // Reset data collection state
      setDataCollectionState({
        'cold-probe': { hasData: false },
        'multi-trial': { hasData: false },
        'daily-mand': { hasData: false },
        'behavior-tracking': { hasData: false },
        'behavior-interval': { hasData: false },
        abc: { hasData: false },
        'maintenance-probe': { hasData: false },
        'skill-tracking': { hasData: false },
        'injury-report': { hasData: false },
        'parent-training': { hasData: false },
        'toileting-program': { hasData: false },
        'lesson-plan': { hasData: false }
      });
      // Reset ABC records for new child
      setAbcRecords([]);
      // Reset to first tab
      const newChildIdNum = parseInt(newChildId) || 0;
      const useMultiTrial = newChildIdNum % 2 === 0;
      setCurrentTab(useMultiTrial ? 'multi-trial' : 'cold-probe');
    } else if (newChildId !== childId) {
      setPendingChildId(newChildId);
      setSwitchChildModalOpen(true);
    }
  };

  const handleConfirmChildSwitch = () => {
    if (pendingChildId) {
      // Get the first available form for the new child
      const newChildIdNum = parseInt(pendingChildId);
      const useMultiTrial = newChildIdNum % 2 === 0;
      const firstFormType = useMultiTrial ? 'multi-trial' : 'cold-probe';

      // Reset data collection state
      setDataCollectionState({
        'cold-probe': { hasData: false },
        'multi-trial': { hasData: false },
        'daily-mand': { hasData: false },
        'behavior-tracking': { hasData: false },
        'behavior-interval': { hasData: false },
        abc: { hasData: false },
        'maintenance-probe': { hasData: false },
        'skill-tracking': { hasData: false },
        'injury-report': { hasData: false },
        'parent-training': { hasData: false },
        'toileting-program': { hasData: false },
        'lesson-plan': { hasData: false }
      });

      // Reset ABC records for new child
      setAbcRecords([]);

      // Navigate to new child's first form
      navigate(`/therapist/data-collection/${pendingChildId}/${firstFormType}`, { replace: true });
      setSwitchChildModalOpen(false);
      setPendingChildId(null);
    }
  };

  const handleCancelChildSwitch = () => {
    setSwitchChildModalOpen(false);
    setPendingChildId(null);
  };

  const handleBackToHub = () => {
    // If user came from the calendar, go back to calendar
    if (sessionDetails) {
      navigate('/therapist/my-calendar');
    } else {
      navigate('/therapist/data-collection');
    }
  };

  const handlePreviousForm = () => {
    const currentIndex = availableForms.findIndex((f) => f.type === currentTab);
    if (currentIndex > 0) {
      const previousForm = availableForms[currentIndex - 1];
      setCurrentTab(previousForm.type);
      if (!isOfflineMode) {
        window.history.replaceState(
          { ...window.history.state, usr: location.state },
          '',
          `/therapist/data-collection/${childId}/${previousForm.type}`
        );
      }
    }
  };

  const handleNextForm = () => {
    const currentIndex = availableForms.findIndex((f) => f.type === currentTab);
    if (currentIndex < availableForms.length - 1) {
      const nextForm = availableForms[currentIndex + 1];
      setCurrentTab(nextForm.type);
      if (!isOfflineMode) {
        window.history.replaceState(
          { ...window.history.state, usr: location.state },
          '',
          `/therapist/data-collection/${childId}/${nextForm.type}`
        );
      }
    }
  };

  // Touch handlers for swipe gestures
  const handleTouchStart = (e) => {
    // Ignore touches on interactive elements (buttons, inputs, tabs, chips, etc.),
    // and anywhere a sheet has opted out with data-swipe-ignore - the ST/OT Daily
    // Activity sheets, where a sideways drag over a goal card or the table is the
    // therapist working the sheet, not asking for the next datasheet.
    const target = e.target;
    const isInteractive = target.closest(
      'button, input, textarea, select, a, [role="button"], [role="tab"], .MuiChip-root, .MuiAutocomplete-root, .MuiToggleButton-root, [data-swipe-ignore]'
    );
    if (isInteractive) {
      touchStartX.current = null;
      return;
    }
    touchStartX.current = e.touches[0].clientX;
    touchEndX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
    touchEndY.current = e.touches[0].clientY;
  };

  const handleTouchMove = (e) => {
    if (touchStartX.current === null) return;
    touchEndX.current = e.touches[0].clientX;
    touchEndY.current = e.touches[0].clientY;
  };

  const handleTouchEnd = () => {
    if (touchStartX.current === null || touchEndX.current === null) return;

    const swipeThreshold = 75; // Minimum horizontal travel for a deliberate swipe
    const diffX = touchStartX.current - touchEndX.current;
    const diffY = touchStartY.current - touchEndY.current;
    const currentIndex = availableForms.findIndex((f) => f.type === currentTab);

    // Only treat as a horizontal tab swipe when the motion is clearly horizontal.
    // A vertical scroll (even one that drifts sideways) must never switch tabs —
    // horizontal travel has to exceed the threshold AND dominate vertical travel.
    const isHorizontalSwipe = Math.abs(diffX) > swipeThreshold && Math.abs(diffX) > Math.abs(diffY) * 1.5;

    if (isHorizontalSwipe) {
      if (diffX > 0) {
        // Swipe left - go to next (only if not on last sheet)
        if (currentIndex < availableForms.length - 1) {
          handleNextForm();
        }
      } else {
        // Swipe right - go to previous (only if not on first sheet)
        if (currentIndex > 0) {
          handlePreviousForm();
        }
      }
    }

    // Reset
    touchStartX.current = null;
    touchEndX.current = null;
    touchStartY.current = null;
    touchEndY.current = null;
  };

  // Helper to persist a single form's data to IndexedDB (no debounce).
  //
  // LIVE SYNC: triggerSync is true, so each auto-save is pushed to the backend a
  // second or two later as well as held locally — this is what lets a supervisor
  // watch a running session's data fill in from their calendar (View Data), instead
  // of seeing nothing until End Session. It is SAFE to push repeatedly: the entry
  // carries a stable offline_id and the backend upserts by (offline_id, session_id)
  // — a re-sync of the same sheet updates its one row rather than adding a duplicate
  // (backend/app/api/v1/data_collection.py create_data_collection). Syncs are
  // single-threaded (syncManager isSyncing guard), so there is no concurrent double
  // POST either. End Session still runs forceSyncAll() as the final flush.
  const persistToIndexedDB = useCallback(
    async (activeForm, formData) => {
      const metadata = {
        childId: child?.id || childId,
        childName: child?.name || child?.child_name || '',
        therapistId,
        therapistName,
        sessionId: sessionDetails?.id || sessionDetails?.sessionId || null,
        sessionDate: sessionDetails?.date ? new Date(sessionDetails.date).toISOString() : new Date().toISOString(),
        collectedAt: sessionDetails?.date ? new Date(sessionDetails.date).toISOString() : new Date().toISOString()
        // No stable offline_id: a refresh discards the session's rows (see the discard-on-
        // reload effect), then re-collection creates fresh rows. A stable key would make
        // re-collection upsert the very rows the discard is deleting — a race. Per-row ids
        // keep the two disjoint.
      };

      if (entryIdsRef.current[activeForm]) {
        await updateDataEntry(entryIdsRef.current[activeForm], formData, { triggerSync: true });
      } else {
        const id = await saveDataEntry(activeForm, formData, metadata, { triggerSync: true });
        entryIdsRef.current[activeForm] = id;
      }
    },
    [child, childId, therapistId, therapistName, sessionDetails]
  );

  // Central auto-save function — called by each form with (formType, formData)
  // Debounces saves, creates on first save, updates on subsequent saves
  const triggerAutoSave = useCallback(
    (formType, formData) => {
      // Determine which form changed — use formType if provided, else currentTab
      const activeForm = formType || currentTab;

      // Full-Read on this sheet: never persist. The UI is already disabled (fieldset +
      // pointer-events at the render site), but this is the definitive backstop so a
      // stray mount-time onDataChange can't autosave a read-only sheet. The backend also
      // refuses the write (data_tracking feature gate + per-sheet check).
      if (sheetReadOnly(activeForm)) return;

      // Clear any existing timeout
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }

      // Mark sheet as having data
      setDataCollectionState((prev) => ({
        ...prev,
        [activeForm]: { hasData: true }
      }));

      // If no formData provided, just update UI status (backward compat)
      if (!formData) {
        setAutoSaveStatus('saved');
        pendingSaveRef.current = null;
        return;
      }

      // Store latest pending data so it can be flushed on End Session
      pendingSaveRef.current = { activeForm, formData };
      // Remember it for in-session restore (immediate, before the debounce) so a
      // remounting sheet can rehydrate from it.
      lastFormDataRef.current[activeForm] = formData;

      setAutoSaveStatus('saving');

      // Debounced save to IndexedDB. A plain debounce RESETS on every change, which for a
      // fast, continuous sheet like Daily Mand (mands tapped in bursts) means it never
      // actually persists until the therapist pauses a full 2s — so a supervisor's live
      // view sees nothing for the length of the burst. MAX_WAIT bounds that: once a change
      // has been pending that long, the next one flushes immediately instead of pushing
      // the timer out again. The first-pending time lives in autoSaveMaxWaitRef, cleared on flush.
      const DEBOUNCE_MS = 2000;
      const MAX_WAIT_MS = 3000;
      if (autoSaveMaxWaitRef.current == null) autoSaveMaxWaitRef.current = Date.now();
      const flushDelay = Math.max(0, Math.min(DEBOUNCE_MS, MAX_WAIT_MS - (Date.now() - autoSaveMaxWaitRef.current)));

      autoSaveTimeoutRef.current = setTimeout(async () => {
        autoSaveMaxWaitRef.current = null;
        try {
          await persistToIndexedDB(activeForm, formData);
          pendingSaveRef.current = null; // Saved — nothing pending
          setAutoSaveStatus('saved');
          if (isOfflineMode && onFormSaved) onFormSaved();
        } catch (error) {
          console.error('[AutoSave] Failed:', error);
          setAutoSaveStatus('saved');
        }
      }, flushDelay);
    },
    [currentTab, persistToIndexedDB, isOfflineMode, onFormSaved, sheetReadOnly]
  );

  // Handle submit data
  const handleSubmitData = () => {
    setSubmitModalOpen(true);
  };

  const handleConfirmSubmit = async () => {
    // Prevent duplicate submissions from multiple rapid clicks
    if (isSubmitting) {
      return;
    }
    setIsSubmitting(true);
    setEndingOverlay(true);
    setAutoSaveStatus('saving');

    // Sync data to backend (non-blocking — session end always runs even if sync fails)
    try {
      // 1. Cancel any pending debounced auto-save
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
        autoSaveTimeoutRef.current = null;
      }

      // 2. Flush any pending unsaved data to IndexedDB immediately
      if (pendingSaveRef.current) {
        const { activeForm, formData } = pendingSaveRef.current;
        await persistToIndexedDB(activeForm, formData);
        pendingSaveRef.current = null;
      }

      // 2b. A Behavior Interval recording still open (its grid filled but "Save
      // Session" not pressed) would otherwise sync as an unfinished, in-progress
      // recording. Commit it exactly as the sheet's own End & Save does, then flush
      // it - after step 2, so it never overwrites another sheet's pending save.
      if (behaviorIntervalActionsRef.current?.hasActiveRecording?.()) {
        const result = behaviorIntervalActionsRef.current.endAndSaveCurrentRecording();
        if (autoSaveTimeoutRef.current) {
          clearTimeout(autoSaveTimeoutRef.current);
          autoSaveTimeoutRef.current = null;
        }
        if (result?.saved && pendingSaveRef.current) {
          const { activeForm, formData } = pendingSaveRef.current;
          await persistToIndexedDB(activeForm, formData);
          pendingSaveRef.current = null;
        }
      }

      // 3. Trigger immediate sync to push all IndexedDB data to PostgreSQL
      // Only sync if actually online with auth — otherwise data stays in IndexedDB
      if (isOnline && isLoggedIn) {
        const syncResult = await forceSyncAll();
        // Show toast based on sync result
        const totalSynced = syncResult?.total?.synced || 0;
        const totalFailed = syncResult?.total?.failed || 0;
        // Entries the server refused because the sheet is Full-Read — excluded from the write,
        // NOT saved. Surfaced honestly so "synced successfully" is never shown for data that
        // was actually read-only (which read as data loss).
        const totalSkipped = syncResult?.total?.skipped || 0;
        if (totalSkipped > 0 && totalSynced === 0) {
          openSnackbar({
            open: true,
            message: intl.formatMessage({
              id: 'dataCollection.toast.sessionEndedReadOnly',
              defaultMessage:
                'This data is read-only for your role, so nothing was saved. Ask an administrator for Full access to collect data.'
            }),
            variant: 'alert',
            alert: { color: 'warning' }
          });
        } else if (totalSkipped > 0 && totalSynced > 0) {
          openSnackbar({
            open: true,
            message: intl.formatMessage(
              {
                id: 'dataCollection.toast.sessionEndedSyncedSomeReadOnly',
                defaultMessage: '{count} entries saved. {skipped} read-only entries were not saved.'
              },
              { count: totalSynced, skipped: totalSkipped }
            ),
            variant: 'alert',
            alert: { color: 'warning' }
          });
        } else if (totalSynced > 0 && totalFailed === 0) {
          openSnackbar({
            open: true,
            message: intl.formatMessage({ id: 'dataCollection.toast.sessionEndedSyncSuccess' }, { count: totalSynced }),
            variant: 'alert',
            alert: { color: 'success' }
          });
        } else if (totalSynced > 0 && totalFailed > 0) {
          openSnackbar({
            open: true,
            message: intl.formatMessage(
              { id: 'dataCollection.toast.sessionEndedPartialSync' },
              { synced: totalSynced, failed: totalFailed }
            ),
            variant: 'alert',
            alert: { color: 'warning' }
          });
        } else if (totalFailed > 0) {
          openSnackbar({
            open: true,
            message: intl.formatMessage({ id: 'dataCollection.toast.sessionEndedSyncFailed' }, { count: totalFailed }),
            variant: 'alert',
            alert: { color: 'error' }
          });
        } else {
          openSnackbar({
            open: true,
            message: intl.formatMessage({ id: 'dataCollection.toast.sessionEndedSuccess' }),
            variant: 'alert',
            alert: { color: 'success' }
          });
        }

        // Clear entry ID refs — entries are marked as 'synced' in IndexedDB
        entryIdsRef.current = {};
        lastFormDataRef.current = {};
      } else {
        // Promote DRAFT entries to PENDING so the sync manager picks them up
        // automatically when internet is restored
        await promoteDraftEntries();
        openSnackbar({
          open: true,
          message: intl.formatMessage({ id: 'dataCollection.toast.sessionEndedOffline' }),
          variant: 'alert',
          alert: { color: 'info' }
        });
      }
    } catch (error) {
      console.error('[EndSession] Flush/sync error:', error);
      // Continue — data is safe in IndexedDB and will sync on next app load
    }

    // 4. Mark session as completed — call API if online, otherwise queue for later sync
    if (sessionDetails?.id) {
      const preNotes = sessionDetails?.preSessionNotes || '';
      const completionNotes = sessionNotesRef.current || '';
      let combinedNotes = null;
      if (preNotes || completionNotes) {
        const parts = {};
        if (preNotes) parts.preSessionNotes = preNotes;
        if (completionNotes) parts.completionNotes = completionNotes;
        combinedNotes = JSON.stringify(parts);
      }

      if (isOnline && isLoggedIn) {
        // Run the mastery rule engine first (data is already synced above) so we
        // can surface what advanced. The /end call below re-runs it idempotently
        // server-side as a safety net.
        let engineChanges = [];
        try {
          const res = await executeRuleEngine(childId, sessionDetails.id);
          engineChanges = res?.changes || [];
        } catch (engineErr) {
          console.warn('[EndSession] Rule engine failed:', engineErr?.message);
        }
        if (engineChanges.length > 0) {
          const summary = engineChanges
            .slice(0, 4)
            .map((c) => `${c.target_title || 'Target'} → ${(c.to || '').replace(/_/g, ' ')}`)
            .join('; ');
          openSnackbar({
            open: true,
            message: `${engineChanges.length} target(s) advanced: ${summary}${engineChanges.length > 4 ? '…' : ''}`,
            variant: 'alert',
            alert: { color: 'info' },
            autoHideDuration: 8000
          });
        }
        // Online with auth — call API directly
        try {
          await completeSession(sessionDetails.id, {
            actual_end: new Date().toISOString(),
            ...(combinedNotes ? { notes: combinedNotes } : {})
          });
        } catch (endErr) {
          console.error('[EndSession] Failed to end session via API:', endErr);
          // Fallback: queue for later sync so the update is never lost
          // (handles Edge reporting online when internet is actually down)
          try {
            await saveSessionUpdate({
              sessionId: sessionDetails.id,
              updateType: 'end-session',
              status: 'completed',
              actualEndTime: new Date().toISOString(),
              notes: combinedNotes,
              therapistId: therapistId
            });
          } catch (queueErr) {
            console.error('[EndSession] Failed to queue session completion fallback:', queueErr);
          }
        }
      } else {
        // Offline — queue session completion for later sync
        try {
          await saveSessionUpdate({
            sessionId: sessionDetails.id,
            updateType: 'end-session',
            status: 'completed',
            actualEndTime: new Date().toISOString(),
            notes: combinedNotes,
            therapistId: therapistId
          });
        } catch (queueErr) {
          console.error('[EndSession] Failed to queue session completion:', queueErr);
        }

        // Update cached session so the calendar shows "completed" immediately
        try {
          await updateCachedSession(sessionDetails.id, {
            status: 'completed',
            completedAt: new Date().toISOString()
          });
        } catch (cacheErr) {
          console.warn('[EndSession] Failed to update cached session:', cacheErr.message);
        }
      }
    }

    // Reset all data collection state
    setDataCollectionState({
      'cold-probe': { hasData: false },
      'multi-trial': { hasData: false },
      'daily-mand': { hasData: false },
      'behavior-tracking': { hasData: false },
      'behavior-interval': { hasData: false },
      abc: { hasData: false },
      'maintenance-probe': { hasData: false },
      'skill-tracking': { hasData: false },
      'injury-report': { hasData: false },
      'parent-training': { hasData: false },
      'toileting-program': { hasData: false },
      'lesson-plan': { hasData: false },
      'session-plan': { hasData: false }
    });

    setAutoSaveStatus('saved');
    setSubmitModalOpen(false);
    setSelectedSheetFilter('all');

    // The session is over — drop the persisted timer start so the next session
    // for this id (or no session) starts fresh.
    clearSessionTimerStart(sessionDetails?.id);

    // Call offline form saved callback if provided
    if (isOfflineMode && onFormSaved) {
      onFormSaved();
    }

    // Disable the navigation blocker — session is intentionally ending
    setSessionEnded(true);

    // Navigate after submit — uses deferred navigation (via useEffect) so the
    // blocker is disabled before the navigate() call fires
    if (blocker.state === 'blocked') {
      // User tried to navigate via sidebar — let the blocked navigation proceed
      blocker.proceed();
    } else if (!isOfflineMode) {
      // Normal online end session — go back to calendar or hub
      setPostSubmitRoute(sessionDetails ? '/therapist/my-calendar' : '/therapist/data-collection');
    } else if (isOfflineMode && isOnline && !isLoggedIn) {
      // Offline mode, internet is available, user isn't logged in
      // Offer to login so data can sync immediately
      setEndingOverlay(false);
      setLoginSyncDialogOpen(true);
    } else if (isOfflineMode) {
      // Offline mode — navigate back to offline calendar
      setPostSubmitRoute('/offline/my-calendar');
    }
  };

  const handleCancelSubmit = () => {
    setSubmitModalOpen(false);
    // If cancel was triggered from nav guard, also reset the blocker
    if (blocker.state === 'blocked') blocker.reset();
  };

  // Handle inline login & sync from the post-submit dialog
  const handleLoginAndSync = async () => {
    setLoginLoading(true);
    setLoginError('');
    try {
      // Authenticate via JWTContext login (sets token + session)
      await login(loginEmail, loginPassword);

      // Now that we have an auth token, push all pending IndexedDB entries to PostgreSQL
      await forceSyncAll();
      entryIdsRef.current = {};
      lastFormDataRef.current = {};
      // Also mark the session as completed now that we have auth
      if (sessionDetails?.id) {
        try {
          const preNotes = sessionDetails?.preSessionNotes || '';
          const completionNotes = sessionNotesRef.current || '';
          let combinedNotes = null;
          if (preNotes || completionNotes) {
            const parts = {};
            if (preNotes) parts.preSessionNotes = preNotes;
            if (completionNotes) parts.completionNotes = completionNotes;
            combinedNotes = JSON.stringify(parts);
          }
          await completeSession(sessionDetails.id, {
            actual_end: new Date().toISOString(),
            ...(combinedNotes ? { notes: combinedNotes } : {})
          });
        } catch (endErr) {
          console.error('[LoginSync] Failed to end session:', endErr);
        }
      }

      setLoginSuccess(true);
    } catch (error) {
      console.error('[LoginSync] Login failed:', error);
      setLoginError(error?.message || intl.formatMessage({ id: 'dataCollection.dialog.loginFailed' }));
    } finally {
      setLoginLoading(false);
    }
  };

  // Check if at least one data sheet has data
  const hasAnyData = Object.values(dataCollectionState).some((sheet) => sheet.hasData);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
    };
  }, []);

  // Navigation guard — show confirmation when user tries to leave during active session
  useEffect(() => {
    if (blocker.state === 'blocked') {
      setNavGuardOpen(true);
    }
  }, [blocker.state]);

  // Deferred navigation after session end — waits for blocker to be disabled
  // (sessionEnded=true disables blocker, then this effect navigates)
  useEffect(() => {
    if (postSubmitRoute && sessionEnded) {
      navigate(postSubmitRoute);
      setPostSubmitRoute(null);
    }
  }, [postSubmitRoute, sessionEnded, navigate]);

  // Browser-level protection (tab close, refresh) during active session.
  // Skipped while a Submit is in-flight — the submit flow drives its own
  // navigation away from the page and we don't want to show the "Leave site?
  // Changes may not be saved" browser dialog on top of that.
  useEffect(() => {
    if (!isSessionActive || isSubmitting) return;
    const handler = (e) => {
      e.preventDefault();
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isSessionActive, isSubmitting]);

  // Nav guard: user clicks "No, stay" — cancel blocked navigation
  const handleNavGuardCancel = () => {
    setNavGuardOpen(false);
    if (blocker.state === 'blocked') blocker.reset();
  };

  // Nav guard: user clicks "Yes, end session" — open the session notes submit modal
  const handleNavGuardConfirm = () => {
    setNavGuardOpen(false);
    setSubmitModalOpen(true);
  };

  // Clinical data from API
  const [maintenanceTargets, setMaintenanceTargets] = useState([]);
  const [targetTrackingTargets, setTargetTrackingTargets] = useState([]);
  const [dailyMandConfigState, setDailyMandConfigState] = useState({ mandItems: [], targets: [] });
  const [coldProbeTargets, setColdProbeTargets] = useState([]);
  const [behaviorList, setBehaviorList] = useState([]);

  // Add-Target shortcut state — opens the supervisor TargetDialog in goal-select
  // mode so therapists can drop a target onto any of the child's existing goals
  // mid-session without leaving the data-collection screen.
  const [, setAddTargetOpen] = useState(false);
  // "Add Target" view: the Target Tracking datasheet UI + an Add Target button.
  const [addTargetViewOpen, setAddTargetViewOpen] = useState(false);
  const [addTargetCtx, setAddTargetCtx] = useState({ iepId: '', goals: [] });
  // Shortcut -> Add Behavior: the IEP version the sheets read (from the clinical bundle).
  const [behaviorsOpen, setBehaviorsOpen] = useState(false);
  const [iepVersionId, setIepVersionId] = useState('');
  // Target Tracking sheet: every non-declined target with its raw row (clinical bundle).
  const [trackingTargets, setTrackingTargets] = useState([]);
  const [targetSkills, setTargetSkills] = useState([]);

  // Skills repository — loaded once for the Add-Target dialog's autocomplete.
  // Each skill row gets `_domainCode` attached so the dialog can filter by the
  // chosen goal's domain (e.g. "DOMAIN-MAND" → "MAND").
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [domainList, skillList] = await Promise.all([listSkillDomains().catch(() => []), listSkills({}).catch(() => [])]);
        if (cancelled) return;
        const codeById = new Map(domainList.map((d) => [d.id, d.code]));
        setTargetSkills(skillList.map((s) => ({ ...s, _domainCode: codeById.get(s.domain_id) || '' })));
      } catch {
        if (!cancelled) setTargetSkills([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Load clinical data from API when childId changes
  useEffect(() => {
    if (!childId) return;
    loadClinicalData(childId, { cacheFirst: true });
  }, [childId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Apply clinical data (IEP targets, behaviors, mand items) to component state
  const applyClinicalData = (iepData, mandItemsRes, behaviorsRes, iepBehaviorsSnapshot, snapshotInfo = {}) => {
    const { snapshot: iepSnapshot, overlay: iepTargetsOverlay, maintenanceHistory = {} } = snapshotInfo;
    // Prefer IEP-snapshot behaviors (the ones the supervisor tracks for this
    // child, e.g. "Biting others", "Pinching"). Fall back to the org-wide ref
    // behaviors only when the snapshot has none — that keeps Behavior Tracking
    // / Behavior Interval functional in pre-IEP demos.
    const fromIep = Array.isArray(iepBehaviorsSnapshot) ? iepBehaviorsSnapshot : [];
    if (fromIep.length > 0) {
      setBehaviorList(
        fromIep.map((b, idx) => {
          const m = b.measurement || {};
          const tracking = [];
          if (m.trackFrequency) tracking.push('frequency');
          if (m.trackDuration) tracking.push('duration');
          if (tracking.length === 0) tracking.push('frequency');
          // IEP-form behaviors don't carry an explicit `id` field (blankBehavior
          // in iep.jsx only stores behaviorName + measurement). Without a
          // unique id every card in BehaviorTrackingMode treats `undefined`
          // as the same behavior — starting the timer on one would
          // simultaneously light up every other card and they'd share the
          // same Total Duration. Derive a stable id from behaviorName + idx.
          return {
            id: b.id || `iep-behavior-${idx}-${(b.behaviorName || '').toLowerCase().replace(/\s+/g, '-')}`,
            name: b.behaviorName || b.name || '',
            code: b.code,
            behavior_type: b.behavior_type,
            // Free-text "Settings in which the behavior occurs" from the IEP PBSP,
            // e.g. "School classroom, therapy sessions, home routines". Carried
            // through so the Behavior Tracking sheet can offer it as the
            // setting/context dropdown when recording incidents.
            settings: b.settings || '',
            tracking
          };
        })
      );
    } else {
      setBehaviorList(
        (behaviorsRes || []).map((b) => ({
          id: b.id,
          name: b.name,
          code: b.code,
          behavior_type: b.behavior_type,
          tracking:
            b.measurement_type === 'duration' ? ['duration'] : b.measurement_type === 'both' ? ['frequency', 'duration'] : ['frequency']
        }))
      );
    }

    // Source the goals + targets the supervisor edits in the "Goals & Targets"
    // tab — those live in the IEP version SNAPSHOT (iepGoals) plus a per-goal
    // targets_overlay map. The active-IEP endpoint's `goals` table is a
    // different/older structure that may be empty for newer clients.
    //
    // Each snapshot target has the shape { id, name, description, skillId,
    // status: 'in_progress' | 'mastered' | ..., dateIntroduced, dateMastered,
    // location, people, stimuli, materials } — already in the shape Cold
    // Probe / Multi Trial want, so we don't need transformTarget on these.
    const snapshotGoals = Array.isArray(iepSnapshot?.iepGoals) ? iepSnapshot.iepGoals : [];
    // If the IEP version snapshot has goals, trust that source — pull targets
    // from the overlay per goal. We previously required the overlay itself to
    // be non-empty before using the snapshot, which made us fall back to
    // active-IEP whenever the overlay payload looked sparse; that fallback
    // was returning only 1 placeholder target per goal (3 of 7).
    const useSnapshot = snapshotGoals.length > 0;

    // Speech and OT goals are dropped here: these are the ABA sheets, and a
    // therapy goal is worked and scored on its own service's data sheet, which
    // seeds itself from the child's goals for that service. Keeping them would
    // score the same goal twice, in two different clinical languages. A goal with
    // NO service stays - everything authored before the field existed has none.
    // Mirrors services/clinicalDatasheets.isTherapyGoal; keep the two in step.
    const THERAPY_SERVICES = ['speech_therapy', 'occupational_therapy'];
    const isTherapyGoal = (g) => THERAPY_SERVICES.includes(String(g?.serviceCode || g?.service_code || '').trim());

    const allTargets = useSnapshot
      ? snapshotGoals
          .filter((g) => !isTherapyGoal(g))
          .flatMap((g) => {
            const ts = (iepTargetsOverlay && iepTargetsOverlay[g.goalId]) || [];
            const domain = g.domain || g.title || g.description || '';
            return ts.map((t) => ({ ...t, _goalDomain: domain }));
          })
      : (iepData?.goals || [])
          .filter((g) => !isTherapyGoal(g))
          .flatMap((g) =>
            // skill_domain NamedReference was removed in mig 20260529_0004;
            // the frontend now reads g.skill_domain_code (string) directly.
            (g.targets || []).map((t) => ({ ...t, _goalDomain: g.skill_domain_code || g.title || g.description || '' }))
          );

    const hasSnapshotTargets = useSnapshot && allTargets.length > 0;

    // Visibility for therapists / devs trying to understand why their Cold
    // Probe / Multi Trial counts don't match the IEP set in the supervisor view.
    if (useSnapshot) {
      console.warn('[DataCollection] IEP snapshot targets:', {
        totalGoals: snapshotGoals.length,
        totalTargets: allTargets.length,
        perGoal: snapshotGoals.map((g) => ({
          goalId: g.goalId,
          domain: g.domain,
          targetCount: ((iepTargetsOverlay && iepTargetsOverlay[g.goalId]) || []).length
        }))
      });
    }

    // Cold probe / Multi Trial — pick targets actively being taught.
    const acquisitionTargets = allTargets.filter((t) => {
      const st = (t.status?.name || t.status || '').toLowerCase();
      return st === 'acquisition' || st === 'in_progress' || st === 'baseline';
    });

    // Cold Probe / Multi Trial show only NON-MAND domain targets. MAND-domain
    // targets belong on the Daily Mand sheet and would otherwise appear in
    // both places. We compare on the same `_goalDomain` string we set above.
    const isMandDomain = (t) => (t._goalDomain || '').toLowerCase().includes('mand');

    // Tracking phase routing (tracking_status set per target in Goals & Targets):
    //   daily_tracking / not_introduced → active sheets (Daily Mand, Cold Probe…)
    //   weekly_maintenance / monthly / long_term → Maintenance Probe only.
    const PROBE_TRACKING_STATUSES = new Set(['weekly_maintenance', 'monthly', 'long_term']);
    const trackingOf = (t) => (t.trackingStatus || t.tracking_status || '').toLowerCase();
    const isProbeMaintenance = (t) => PROBE_TRACKING_STATUSES.has(trackingOf(t));

    const nonMandAcquisitionTargets = acquisitionTargets.filter((t) => !isMandDomain(t) && !isProbeMaintenance(t));

    // Snapshot targets already match the cold-probe shape (id/name/description);
    // active-IEP targets need transformation through the API transformer.
    const toColdProbeShape = (t) =>
      hasSnapshotTargets
        ? {
            id: t.id,
            name: t.name || t.description,
            description: t.description || '',
            domain: t._goalDomain || '',
            image: null,
            materials: Array.isArray(t.materials) ? t.materials : [],
            // Pre-selects the target's usual controlling prompt in the session sheets.
            defaultControllingPrompts: Array.isArray(t.defaultControllingPrompts) ? t.defaultControllingPrompts : [],
            // SD (discriminative stimulus) — shown as a tooltip against the target.
            sdInstruction: t.sdInstruction || ''
          }
        : transformTargetForColdProbe(t, t._goalDomain);

    if (nonMandAcquisitionTargets.length > 0) {
      setColdProbeTargets(nonMandAcquisitionTargets.map(toColdProbeShape));
    } else {
      // No in-progress non-MAND targets — show empty so the therapist sees the
      // IEP needs non-MAND acquisition targets set up (no demo/placeholder data).
      setColdProbeTargets([]);
    }

    // Maintenance Probe = targets in weekly / monthly / long-term maintenance
    // (by tracking_status). Daily-tracking maintenance stays on Daily Mand.
    const maintTargets = allTargets.filter(isProbeMaintenance);
    setMaintenanceTargets(
      maintTargets.map((t) =>
        hasSnapshotTargets
          ? {
              id: t.id,
              name: t.name || t.description,
              description: t.description || '',
              domain: t._goalDomain,
              masteredDate: t.dateMastered || null,
              lastMastered: t.dateMastered || null,
              status: t.status,
              trackingStatus: trackingOf(t),
              lastProbeDate: maintenanceHistory[t.id]?.lastProbeDate || null,
              lastProbeScore: maintenanceHistory[t.id]?.lastProbeScore || null
            }
          : {
              ...transformTargetForMaintenance(t, t._goalDomain),
              trackingStatus: trackingOf(t),
              lastProbeDate: maintenanceHistory[t.id]?.lastProbeDate || null,
              lastProbeScore: maintenanceHistory[t.id]?.lastProbeScore || null
            }
      )
    );

    // Target tracking = all targets
    setTargetTrackingTargets(
      allTargets.map((t) =>
        hasSnapshotTargets
          ? {
              id: t.id,
              target: t.description || t.name,
              domain: t._goalDomain,
              dateIntroduced: t.dateIntroduced || null,
              dateMastered: t.dateMastered || null,
              daysToMastery:
                t.dateIntroduced && t.dateMastered
                  ? Math.max(0, Math.round((new Date(t.dateMastered) - new Date(t.dateIntroduced)) / 86400000))
                  : '-',
              location: t.location || '',
              people: t.people || '',
              stimuli: t.stimuli || ''
            }
          : transformTargetForTracking(t, t._goalDomain)
      )
    );

    // Daily Mand sources both its Quick Add cards AND the Related Target
    // dropdown from MAND-domain IEP targets. The materials-API mandItems are
    // intentionally not used here — therapists configure mand targets in the
    // IEP so the Quick Add cards stay in sync with each child's program.
    // Includes dateIntroduced/dateMastered so the sheet can filter to only
    // currently-in-progress targets (introduced & not yet mastered).
    const toMandTargetShape = (t) => {
      const base = toColdProbeShape(t);
      const dateIntroduced = hasSnapshotTargets ? t.dateIntroduced || null : t.start_date || (t._raw && t._raw.start_date) || null;
      const dateMastered = hasSnapshotTargets ? t.dateMastered || null : t.achieved_date || (t._raw && t._raw.achieved_date) || null;
      return { ...base, dateIntroduced, dateMastered };
    };
    // Daily Mand = MAND-domain targets that are NOT in weekly/monthly/long-term
    // maintenance (those move to Maintenance Probe). Daily-tracking + acquisition
    // mand targets stay here.
    const mandTargets = allTargets.filter((t) => isMandDomain(t) && !isProbeMaintenance(t)).map(toMandTargetShape);
    const mandItems = mandTargets.map((t) => ({ ...t, category: 'mand' }));
    setDailyMandConfigState({ mandItems, targets: mandTargets });
  };

  // `cacheFirst`: draw the child's targets from the device cache straight away,
  // then replace them with the network copy. Session start used to wait 3-4s on
  // the IEP -> goals -> targets chain with an empty sheet; now it is instant for
  // any child cached at login or on an earlier visit. Not used when reloading
  // after Add Target, where the cache is known to be one target behind.
  const loadClinicalData = async (cid, { cacheFirst = false } = {}) => {
    if (cacheFirst) {
      try {
        const cached = await getCachedClinicalData(cid);
        if (cached) {
          if (cached.addTargetCtx) setAddTargetCtx(cached.addTargetCtx);
          if (cached.iepVersionId) setIepVersionId(cached.iepVersionId);
          if (Array.isArray(cached.trackingTargets)) setTrackingTargets(cached.trackingTargets);
          applyClinicalData(cached.iepData, cached.mandItems, cached.behaviors, cached.iepBehaviorsSnapshot, {
            snapshot: cached.iepSnapshot,
            overlay: cached.iepTargetsOverlay,
            maintenanceHistory: cached.maintenanceHistory || {}
          });
        }
      } catch (cacheErr) {
        console.warn('[DataCollection] Failed to read cached clinical data:', cacheErr.message);
      }
    }
    try {
      // Single source of truth for the clinical bundle (snapshot + per-goal
      // targets overlay with names + IEP behaviors + mand items + maintenance
      // history). The same builder runs at login to seed the offline cache.
      const bundle = await fetchClinicalBundle(cid, {
        sessionAddedTargetIds: sessionAddedTargetIdsRef.current,
        // Target visibility is evaluated as of the session's SCHEDULED date, not
        // when the therapist actually opens the sheet.
        sessionDate: sessionDetails?.date || null
      });

      setAddTargetCtx(bundle.addTargetCtx);
      setIepVersionId(bundle.iepVersionId || '');
      setTrackingTargets(bundle.trackingTargets || []);

      applyClinicalData(bundle.iepData, bundle.mandItems, bundle.behaviors, bundle.iepBehaviorsSnapshot, {
        snapshot: bundle.iepSnapshot,
        overlay: bundle.iepTargetsOverlay,
        maintenanceHistory: bundle.maintenanceHistory
      });

      // Cache the FULL bundle for offline use (names + behaviors included).
      try {
        await cacheClinicalData(cid, clinicalBundleToCache(bundle));
      } catch (cacheErr) {
        console.warn('[DataCollection] Failed to cache clinical data:', cacheErr.message);
      }
    } catch (error) {
      console.warn('[DataCollection] Failed to load clinical data from API:', error.message);

      // Fallback: load from IndexedDB cache (for offline mode)
      try {
        const cached = await getCachedClinicalData(cid);
        if (cached) {
          applyClinicalData(cached.iepData, cached.mandItems, cached.behaviors, cached.iepBehaviorsSnapshot, {
            snapshot: cached.iepSnapshot,
            overlay: cached.iepTargetsOverlay,
            maintenanceHistory: cached.maintenanceHistory || {}
          });
          if (cached.addTargetCtx) setAddTargetCtx(cached.addTargetCtx);
          if (cached.iepVersionId) setIepVersionId(cached.iepVersionId);
          if (Array.isArray(cached.trackingTargets)) setTrackingTargets(cached.trackingTargets);
          return;
        }
      } catch (cacheErr) {
        console.warn('[DataCollection] Failed to load cached clinical data:', cacheErr.message);
      }

      // No cache available — leave all sheets empty rather than showing
      // demo/placeholder targets.
      setColdProbeTargets([]);
      setMaintenanceTargets([]);
      setTargetTrackingTargets([]);
      setDailyMandConfigState({ mandItems: [], targets: [] });
      setBehaviorList([]);
      setAddTargetCtx({ iepId: '', goals: [] });
      setIepVersionId('');
      setTrackingTargets([]);
    }
  };

  // Multi-trial targets reuse cold probe targets with trial count
  const multiTrialTargets = useMemo(() => {
    return coldProbeTargets.map((t) => ({ ...t, trials: t.trials || 10 }));
  }, [coldProbeTargets]);

  // Common props for all data collection forms (for offline-first sync)
  const commonFormProps = {
    childId: child?.id || childId,
    childName: child?.name || child?.child_name || '',
    therapistId,
    therapistName,
    sessionId: sessionDetails?.id || sessionDetails?.sessionId || null,
    orgId: user?.org_id || user?.orgId || null,
    centerId: user?.center_id || user?.centerId || null
  };

  // Map form type to its component + extra props
  // All forms are rendered simultaneously (hidden when inactive) to preserve state across tab switches
  // loadClinicalData is re-created every render; read it through a ref so the sheet
  // registry below is not rebuilt (and every sheet re-rendered) on each render.
  const loadClinicalDataRef = useRef(loadClinicalData);
  loadClinicalDataRef.current = loadClinicalData;

  const formComponents = useMemo(
    () => ({
      'cold-probe': { Component: ColdProbeMode, props: { targets: coldProbeTargets } },
      'multi-trial': { Component: MultiTrialMode, props: { targets: multiTrialTargets } },
      'daily-mand': {
        Component: DailyMandMode,
        props: { mandItems: dailyMandConfigState.mandItems, targets: dailyMandConfigState.targets }
      },
      'behavior-tracking': {
        Component: BehaviorTrackingMode,
        props: { behaviors: behaviorList, onTimerStateChange: handleBehaviorTimerChange }
      },
      'behavior-interval': { Component: BehaviorIntervalMode, props: { behaviors: behaviorList } },
      abc: { Component: ABCMode, props: { abcRecords, onRecordsChange: setAbcRecords } },
      'maintenance-probe': { Component: MaintenanceProbeMode, props: { targets: maintenanceTargets } },
      'skill-tracking': { Component: SkillTrackingMode, props: { targets: targetTrackingTargets } },
      'target-tracking': {
        Component: TargetTrackingMode,
        props: {
          trackingTargets,
          sessionDate: sessionDetails?.date || null,
          childId,
          skills: targetSkills,
          onTargetsChanged: () => loadClinicalDataRef.current?.(childId)
        }
      },
      'injury-report': { Component: InjuryReportMode, props: { abcRecords, defaultReportType: 'staff' } },
      'parent-training': { Component: ParentTrainingMode, props: {} },
      'toileting-program': { Component: ToiletingProgramMode, props: {} },
      'ot-data-sheet': { Component: OTDataSheetMode, props: { child } },
      'slp-data-sheet': { Component: SLPDataSheetMode, props: { child } },
      'lesson-plan': { Component: LessonPlanMode, props: {} },
      'session-plan': { Component: SessionPlanMode, props: {} }
    }),
    [
      coldProbeTargets,
      multiTrialTargets,
      dailyMandConfigState,
      abcRecords,
      maintenanceTargets,
      targetTrackingTargets,
      trackingTargets,
      sessionDetails?.date,
      childId,
      targetSkills,
      behaviorList,
      child,
      handleBehaviorTimerChange
    ]
  );

  if (!child) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography>{intl.formatMessage({ id: 'dataCollection.loading.generic' })}</Typography>
      </Box>
    );
  }

  // Defence in depth: block data collection for a future-dated session. The calendar
  // gates entry and the backend rejects every write; this covers a stale deep-link.
  // Viewing already-recorded data (viewExistingData) is still allowed.
  if (sessionDetails?.date && !viewExistingData && isFutureSessionDate(sessionDetails.date)) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h5" gutterBottom>
          {intl.formatMessage({
            id: 'dataCollection.futureBlocked.title',
            defaultMessage: 'This session is scheduled for a future date'
          })}
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          {intl.formatMessage({
            id: 'dataCollection.futureBlocked.body',
            defaultMessage: 'Data cannot be collected before the session’s scheduled date. You can record data on or after that day.'
          })}
        </Typography>
        <Button variant="contained" sx={{ mt: 2 }} onClick={() => navigate(-1)}>
          {intl.formatMessage({ id: 'common.back' })}
        </Button>
      </Box>
    );
  }

  return (
    <Box
      sx={{ width: '100%', maxWidth: '100vw' }}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* End Session in flight (sync, rule engine, completeSession - a few seconds):
          cover the sheet until the calendar takes over. Lifted for the offline
          login-to-sync dialog, which must stay usable. */}
      <Backdrop open={endingOverlay} sx={{ zIndex: (t) => t.zIndex.modal + 1, color: 'common.white', flexDirection: 'column', gap: 2 }}>
        <CircularProgress color="inherit" />
        <Typography color="inherit">
          {intl.formatMessage({ id: 'dataCollection.endingSession', defaultMessage: 'Ending session…' })}
        </Typography>
      </Backdrop>
      {/* Sticky Child Header */}
      <MainCard
        sx={{
          mb: 2,
          position: 'sticky',
          top: isHeaderCollapsed ? 64 : 0,
          zIndex: 1100,
          bgcolor: 'background.paper',
          backgroundImage: activeBehaviorTimer
            ? `linear-gradient(${alpha(theme.palette.error.main, 0.05)}, ${alpha(theme.palette.error.main, 0.05)})`
            : 'none',
          boxShadow: theme.shadows[8],
          maxWidth: '100%',
          border: activeBehaviorTimer ? `2px solid ${theme.palette.error.main}` : undefined,
          transition: 'border-color 0.2s ease, background-color 0.2s ease',
          ...(activeBehaviorTimer && {
            animation: 'recCardPulse 2s ease-in-out infinite',
            '@keyframes recCardPulse': {
              '0%, 100%': { boxShadow: `0 0 0 0 ${alpha(theme.palette.error.main, 0.35)}` },
              '50%': { boxShadow: `0 0 0 6px ${alpha(theme.palette.error.main, 0)}` }
            }
          })
        }}
      >
        {/* Collapsed Header View */}
        {isHeaderCollapsed ? (
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ py: 0.5 }}>
            {/* Child Avatar & Name + Data Sheet Selector */}
            <Stack direction="row" spacing={2} alignItems="center">
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: 1.5,
                    bgcolor: 'primary.main',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 700,
                    fontSize: '1rem',
                    flexShrink: 0
                  }}
                >
                  {child?.initials || child?.name?.charAt(0) || '?'}
                </Box>
                <Box>
                  <Typography variant="subtitle1" fontWeight={600} sx={{ lineHeight: 1.2 }}>
                    {child?.name || intl.formatMessage({ id: 'common.unknown' })}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>
                    {sessionDetails?.program || child?.program || intl.formatMessage({ id: 'dataCollection.header.defaultProgram' })}
                  </Typography>
                </Box>
              </Stack>

              {/* Data Sheet Selector */}
              <Select
                value={currentTab}
                onChange={(e) => {
                  const newTab = e.target.value;
                  setCurrentTab(newTab);
                  if (!isOfflineMode) {
                    window.history.replaceState(
                      { ...window.history.state, usr: location.state },
                      '',
                      `/therapist/data-collection/${childId}/${newTab}`
                    );
                  }
                }}
                size="small"
                startAdornment={
                  <TableOutlined
                    style={{
                      fontSize: '0.9rem',
                      color: theme.palette.primary.main,
                      marginRight: 6,
                      marginLeft: -2
                    }}
                  />
                }
                sx={{
                  width: 200,
                  flexShrink: 0,
                  bgcolor: alpha(theme.palette.primary.main, 0.04),
                  borderRadius: 2,
                  transition: 'background-color 0.2s ease',
                  '& .MuiSelect-select': {
                    py: 0.875,
                    pl: 0.5,
                    pr: 3,
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    color: 'text.primary'
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: alpha(theme.palette.primary.main, 0.2),
                    borderWidth: '1px !important'
                  },
                  '&:hover': {
                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.palette.primary.main
                    }
                  },
                  '&.Mui-focused': {
                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.palette.primary.main
                    }
                  },
                  '& .MuiSelect-icon': {
                    color: theme.palette.primary.main,
                    right: 8
                  }
                }}
                MenuProps={{
                  disableScrollLock: true,
                  anchorOrigin: {
                    vertical: 'bottom',
                    horizontal: 'left'
                  },
                  transformOrigin: {
                    vertical: 'top',
                    horizontal: 'left'
                  },
                  slotProps: {
                    paper: {
                      sx: {
                        mt: 0.5,
                        width: 200,
                        borderRadius: 2,
                        boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                        '& .MuiMenuItem-root': {
                          fontSize: '0.875rem',
                          py: 1.25,
                          px: 2,
                          '&:hover': {
                            bgcolor: alpha(theme.palette.primary.main, 0.08)
                          },
                          '&.Mui-selected': {
                            bgcolor: alpha(theme.palette.primary.main, 0.12),
                            fontWeight: 600,
                            '&:hover': {
                              bgcolor: alpha(theme.palette.primary.main, 0.16)
                            }
                          }
                        }
                      }
                    }
                  }
                }}
              >
                {availableForms.map((form) => (
                  <MenuItem key={form.type} value={form.type} disabled={form.comingSoon}>
                    {form.name}
                  </MenuItem>
                ))}
              </Select>
            </Stack>

            <Stack direction="row" spacing={1.5} alignItems="center">
              {/* Session Timer - Only show when in active session, hidden in view mode */}
              {sessionDetails && !viewExistingData && (
                <Stack
                  direction="row"
                  spacing={0.5}
                  alignItems="center"
                  sx={{
                    px: 1.5,
                    py: 0.5,
                    borderRadius: 1.5,
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
                  }}
                >
                  <ClockCircleOutlined style={{ color: theme.palette.primary.main, fontSize: '0.85rem' }} />
                  <SessionTimer sessionKey={sessionDetails?.id} sx={{ color: 'primary.main' }} />
                </Stack>
              )}

              {/* Auto-save Status - Only for online mode, hidden in view mode.
                  Label is hidden on xs so only the save icon is shown. */}
              {!viewExistingData && !isOfflineMode && autoSaveStatus === 'saved' && (
                <Chip
                  icon={<SaveOutlined />}
                  label={intl.formatMessage({ id: 'dataCollection.status.saved' })}
                  size="small"
                  color="success"
                  variant="outlined"
                  title={intl.formatMessage({ id: 'dataCollection.status.saved' })}
                  sx={{
                    '& .MuiChip-label': {
                      display: { xs: 'none', sm: 'inline-block' }
                    },
                    '& .MuiChip-icon': {
                      mx: { xs: '4px', sm: undefined }
                    }
                  }}
                />
              )}

              {/* End Session / Submit Button - hidden in view mode. On xs the
                  label is hidden but the button is sized slightly larger so
                  the icon-only tap target stays comfortable. */}
              {!viewExistingData && (
                <Button
                  variant="contained"
                  color={sessionDetails ? 'error' : 'success'}
                  size="small"
                  startIcon={sessionDetails ? <StopOutlined /> : <CheckOutlined />}
                  onClick={handleSubmitData}
                  aria-label={
                    sessionDetails
                      ? intl.formatMessage({ id: 'dataCollection.button.endSession' })
                      : intl.formatMessage({ id: 'dataCollection.button.submit' })
                  }
                  sx={{
                    // Mobile: a compact labelled button the height of the Saved chip - an
                    // icon-only red square read as a warning sign, not as "end session".
                    minWidth: 0,
                    height: { xs: 28, sm: 'auto' },
                    px: { xs: 1.25, sm: 2 },
                    fontSize: { xs: '0.8125rem', sm: undefined },
                    whiteSpace: 'nowrap',
                    '& .MuiButton-startIcon': { mr: { xs: 0.5, sm: 1 }, '& > *:first-of-type': { fontSize: '0.875rem' } }
                  }}
                >
                  <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                    {sessionDetails
                      ? intl.formatMessage({ id: 'dataCollection.button.endSession' })
                      : intl.formatMessage({ id: 'dataCollection.button.submit' })}
                  </Box>
                  <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
                    {sessionDetails
                      ? intl.formatMessage({ id: 'dataCollection.button.endShort', defaultMessage: 'End' })
                      : intl.formatMessage({ id: 'dataCollection.button.submit' })}
                  </Box>
                </Button>
              )}

              {/* Expand Button — hidden on xs to keep the child bar uncluttered. */}
              <Button
                variant="outlined"
                size="small"
                onClick={() => setIsHeaderCollapsed(false)}
                sx={{
                  display: { xs: 'none', sm: 'inline-flex' },
                  minWidth: 'auto',
                  px: 1,
                  py: 0.5,
                  borderColor: theme.palette.divider,
                  color: 'text.secondary',
                  '&:hover': {
                    borderColor: 'primary.main',
                    color: 'primary.main',
                    bgcolor: alpha(theme.palette.primary.main, 0.04)
                  }
                }}
                title={intl.formatMessage({ id: 'dataCollection.tooltip.expandHeader' })}
              >
                <DownOutlined style={{ fontSize: '0.875rem' }} />
              </Button>
            </Stack>
          </Stack>
        ) : (
          /* Expanded Header View */
          <Stack spacing={2}>
            {/* Back Button and Submit Data */}
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Button variant="text" startIcon={<LeftOutlined />} onClick={handleBackToHub}>
                {intl.formatMessage({ id: 'dataCollection.button.back' })}
              </Button>

              <Stack direction="row" spacing={1.5} alignItems="center">
                {/* Offline Mode Status */}
                {isOfflineMode && (
                  <>
                    {/* When Offline - Show status chip */}
                    {!isOnline && (
                      <>
                        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ pl: 2 }}>
                          <Box
                            sx={{
                              width: 12,
                              height: 12,
                              borderRadius: '50%',
                              bgcolor: '#d32f2f',
                              flexShrink: 0
                            }}
                          />
                          <Typography variant="body1" color="text.primary" sx={{ fontWeight: 500 }}>
                            {intl.formatMessage({ id: 'dataCollection.status.nowOffline' })}
                          </Typography>
                        </Stack>
                        {unsyncedCount > 0 && (
                          <Chip
                            icon={<CloudUploadOutlined />}
                            label={intl.formatMessage({ id: 'dataCollection.status.pending' }, { count: unsyncedCount })}
                            color="info"
                            size="small"
                            sx={{ fontWeight: 600 }}
                          />
                        )}
                      </>
                    )}

                    {/* When Online - Show status and sync option */}
                    {isOnline && (
                      <>
                        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ pl: 2 }}>
                          <Box
                            sx={{
                              width: 12,
                              height: 12,
                              borderRadius: '50%',
                              bgcolor: '#2e7d32',
                              flexShrink: 0
                            }}
                          />
                          <Typography variant="body1" color="text.primary" sx={{ fontWeight: 500 }}>
                            {intl.formatMessage({ id: 'dataCollection.status.nowOnline' })}
                          </Typography>
                        </Stack>
                        <Button
                          variant="outlined"
                          color="success"
                          size="small"
                          startIcon={<SyncOutlined />}
                          onClick={() => navigate('/login')}
                          sx={{
                            borderWidth: 2,
                            '&:hover': {
                              borderWidth: 2
                            }
                          }}
                        >
                          {unsyncedCount > 0
                            ? intl.formatMessage({ id: 'dataCollection.button.syncNowCount' }, { count: unsyncedCount })
                            : intl.formatMessage({ id: 'dataCollection.button.syncNow' })}
                        </Button>
                      </>
                    )}
                  </>
                )}

                {/* Auto-save Status Indicator - Only for online mode, hidden in
                    view mode. Labels collapse to icon-only on xs. */}
                {!viewExistingData && !isOfflineMode && (
                  <>
                    {autoSaveStatus === 'saving' && (
                      <Chip
                        icon={<CloudSyncOutlined />}
                        label={intl.formatMessage({ id: 'dataCollection.status.saving' })}
                        size="small"
                        color="primary"
                        variant="outlined"
                        title={intl.formatMessage({ id: 'dataCollection.status.saving' })}
                        sx={{
                          '& .MuiChip-label': { display: { xs: 'none', sm: 'inline-block' } },
                          '& .MuiChip-icon': { mx: { xs: '4px', sm: undefined } }
                        }}
                      />
                    )}
                    {autoSaveStatus === 'saved' && (
                      <Chip
                        icon={<SaveOutlined />}
                        label={intl.formatMessage({ id: 'dataCollection.status.saved' })}
                        size="small"
                        color="success"
                        variant="outlined"
                        title={intl.formatMessage({ id: 'dataCollection.status.saved' })}
                        sx={{
                          '& .MuiChip-label': { display: { xs: 'none', sm: 'inline-block' } },
                          '& .MuiChip-icon': { mx: { xs: '4px', sm: undefined } }
                        }}
                      />
                    )}
                  </>
                )}

                {/* Submit Data Button - hidden in view mode. Label collapses
                    to icon-only on xs; the button stays slightly larger than the
                    Saved chip to remain the primary tap target. */}
                {!viewExistingData && (
                  <Button
                    variant="contained"
                    color={sessionDetails ? 'error' : 'success'}
                    size="small"
                    startIcon={sessionDetails ? <StopOutlined /> : <CheckOutlined />}
                    onClick={handleSubmitData}
                    aria-label={
                      sessionDetails
                        ? intl.formatMessage({ id: 'dataCollection.button.endSession' })
                        : intl.formatMessage({ id: 'dataCollection.button.submit' })
                    }
                    sx={{
                      // Mobile: a compact labelled button the height of the Saved chip - an
                      // icon-only red square read as a warning sign, not as "end session".
                      minWidth: 0,
                      height: { xs: 28, sm: 'auto' },
                      px: { xs: 1.25, sm: 2 },
                      fontSize: { xs: '0.8125rem', sm: undefined },
                      whiteSpace: 'nowrap',
                      '& .MuiButton-startIcon': { mr: { xs: 0.5, sm: 1 }, '& > *:first-of-type': { fontSize: '0.875rem' } }
                    }}
                  >
                    <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                      {sessionDetails
                        ? intl.formatMessage({ id: 'dataCollection.button.endSession' })
                        : intl.formatMessage({ id: 'dataCollection.button.submit' })}
                    </Box>
                    <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
                      {sessionDetails
                        ? intl.formatMessage({ id: 'dataCollection.button.endShort', defaultMessage: 'End' })
                        : intl.formatMessage({ id: 'dataCollection.button.submit' })}
                    </Box>
                  </Button>
                )}

                {/* Collapse Button — hidden on xs to keep the child bar uncluttered. */}
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setIsHeaderCollapsed(true)}
                  sx={{
                    display: { xs: 'none', sm: 'inline-flex' },
                    minWidth: 'auto',
                    px: 1,
                    py: 0.5,
                    borderColor: theme.palette.divider,
                    color: 'text.secondary',
                    '&:hover': {
                      borderColor: 'primary.main',
                      color: 'primary.main',
                      bgcolor: alpha(theme.palette.primary.main, 0.04)
                    }
                  }}
                  title={intl.formatMessage({ id: 'dataCollection.tooltip.collapseHeader' })}
                >
                  <UpOutlined style={{ fontSize: '0.875rem' }} />
                </Button>
              </Stack>
            </Stack>

            {/* Child Info with Session Details - Sticky when scrolling */}
            <Box
              sx={{
                position: 'sticky',
                top: 0,
                zIndex: 10,
                bgcolor: 'background.paper',
                py: 1.5,
                mx: -3,
                px: 3,
                borderBottom: sessionDetails ? `1px solid ${alpha(theme.palette.divider, 0.5)}` : 'none'
              }}
            >
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                justifyContent="space-between"
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                spacing={2}
              >
                <Stack direction="row" spacing={2} alignItems="center" sx={{ flex: 1, width: '100%' }}>
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: 2,
                      bgcolor: 'primary.main',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 700,
                      fontSize: '1.5rem',
                      flexShrink: 0
                    }}
                  >
                    {child?.initials || child?.name?.charAt(0) || '?'}
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    {/* Child Name - Show as text when coming from calendar session, otherwise as dropdown */}
                    {sessionDetails ? (
                      // Coming from calendar - show child name as text (no switching allowed)
                      <Typography variant="h6" sx={{ fontWeight: 600, py: 0.5 }}>
                        {child?.name || sessionDetails.childName}
                      </Typography>
                    ) : isOfflineMode ? (
                      // Offline mode - show labeled select
                      <FormControl fullWidth size="small">
                        <InputLabel>{intl.formatMessage({ id: 'dataCollection.field.selectChild' })}</InputLabel>
                        <Select
                          value={childId || ''}
                          onChange={handleChildChange}
                          label={intl.formatMessage({ id: 'dataCollection.field.selectChild' })}
                        >
                          {allChildren.map((c) => (
                            <MenuItem key={c.id} value={c.id}>
                              {c.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    ) : (
                      // Online mode - borderless select
                      <FormControl fullWidth size="small" sx={{ mb: 0.5 }}>
                        <Select
                          value={childId}
                          onChange={handleChildChange}
                          sx={{
                            fontWeight: 600,
                            fontSize: '1.25rem',
                            '& .MuiSelect-select': {
                              py: 0.5
                            },
                            '& fieldset': {
                              border: 'none'
                            }
                          }}
                        >
                          {allChildren.map((c) => (
                            <MenuItem key={c.id} value={c.id.toString()}>
                              {c.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                    <Typography variant="body2" color="text.secondary" sx={{ mt: isOfflineMode ? 0.5 : 0 }}>
                      {isOfflineMode
                        ? offlineUser
                          ? `${intl.formatMessage({ id: 'dataCollection.field.therapist' })}: ${offlineUser.first_name || offlineUser.username}`
                          : intl.formatMessage({ id: 'dataCollection.field.offlineMode' })
                        : child?.program}
                    </Typography>
                    {!isOfflineMode && child?.sessionInProgress && currentTab === 'daily-mand' && (
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mt: 0.5 }}>
                        <Chip
                          label={intl.formatMessage({ id: 'dataCollection.status.sessionInProgress' })}
                          color="success"
                          size="small"
                          icon={<PlayCircleOutlined />}
                        />
                      </Stack>
                    )}
                  </Box>
                </Stack>

                {/* Session Details - shown when coming from calendar */}
                {sessionDetails ? (
                  <>
                    {/* Compact line for xs + sm: MM-DD H-Ham/pm + timer. On sm
                        we additionally include the "Started at" indicator since
                        there's room for one more piece without wrapping; the
                        full verbose layout only kicks in at md and above. */}
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ display: { xs: 'flex', md: 'none' } }}>
                      <Stack direction="row" spacing={0.75} alignItems="center">
                        <CalendarOutlined style={{ color: theme.palette.text.secondary, fontSize: '0.8rem' }} />
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                          {(() => {
                            if (!sessionDetails.date) return '';
                            const datePart = new Date(sessionDetails.date)
                              .toLocaleDateString('en-US', { month: '2-digit', day: '2-digit' })
                              .replace(/\//g, '-');
                            const parseTime = (t) => {
                              if (!t) return null;
                              const m = String(t)
                                .trim()
                                .match(/^(\d+)(?::\d+)?\s*(AM|PM|am|pm)?$/);
                              if (!m) return null;
                              return { hour: parseInt(m[1], 10), period: (m[2] || '').toLowerCase() };
                            };
                            const s = parseTime(sessionDetails.startTime);
                            const e = parseTime(sessionDetails.endTime);
                            if (!s || !e) return datePart;
                            const range =
                              s.period && s.period === e.period
                                ? `${s.hour}-${e.hour}${s.period}`
                                : `${s.hour}${s.period}-${e.hour}${e.period}`;
                            return `${datePart} ${range}`;
                          })()}
                        </Typography>
                      </Stack>
                      {viewExistingData && sessionDetails.actualDuration != null && (
                        <Chip
                          label={intl.formatMessage(
                            { id: 'dataCollection.field.actualDuration' },
                            { duration: sessionDetails.actualDuration }
                          )}
                          size="small"
                          color="success"
                          variant="outlined"
                        />
                      )}
                      {!viewExistingData && (
                        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ display: { xs: 'none', sm: 'flex' } }}>
                          <ClockCircleOutlined style={{ color: theme.palette.text.secondary, fontSize: '0.8rem' }} />
                          <Typography variant="caption" color="text.secondary">
                            {intl.formatMessage({ id: 'dataCollection.field.startedAt' }, { time: actualStartTime })}
                          </Typography>
                        </Stack>
                      )}
                      {!viewExistingData && (
                        <Stack
                          direction="row"
                          spacing={0.5}
                          alignItems="center"
                          sx={{
                            px: 1,
                            py: 0.25,
                            borderRadius: 1,
                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
                          }}
                        >
                          <ClockCircleOutlined style={{ color: theme.palette.primary.main, fontSize: '0.8rem' }} />
                          <SessionTimer sessionKey={sessionDetails?.id} sx={{ color: 'primary.main', fontSize: '0.8rem' }} />
                        </Stack>
                      )}
                    </Stack>

                    {/* Full verbose session details — md and above only */}
                    <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" sx={{ display: { xs: 'none', md: 'flex' } }}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <CalendarOutlined style={{ color: theme.palette.text.secondary, fontSize: '0.9rem' }} />
                        <Typography variant="body2" color="text.secondary">
                          {sessionDetails.date
                            ? new Date(sessionDetails.date).toLocaleDateString('en-US', {
                                weekday: 'short',
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric'
                              })
                            : intl.formatMessage({ id: 'common.notAvailable' })}
                        </Typography>
                      </Stack>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <ClockCircleOutlined style={{ color: theme.palette.text.secondary, fontSize: '0.9rem' }} />
                        <Typography variant="body2" color="text.secondary">
                          {sessionDetails.startTime} - {sessionDetails.endTime} (
                          {intl.formatMessage({ id: 'dataCollection.field.durationMin' }, { duration: sessionDetails.duration })})
                        </Typography>
                      </Stack>
                      {viewExistingData && sessionDetails.actualDuration != null && (
                        <Chip
                          label={intl.formatMessage(
                            { id: 'dataCollection.field.actualDuration' },
                            { duration: sessionDetails.actualDuration }
                          )}
                          size="small"
                          color="success"
                          variant="outlined"
                        />
                      )}
                      {!viewExistingData && (
                        <Stack direction="row" spacing={1} alignItems="center">
                          <ClockCircleOutlined style={{ color: theme.palette.text.secondary, fontSize: '0.9rem' }} />
                          <Typography variant="body2" color="text.secondary">
                            {intl.formatMessage({ id: 'dataCollection.field.startedAt' }, { time: actualStartTime })}
                          </Typography>
                        </Stack>
                      )}
                      {/* Session Timer - hidden in view mode */}
                      {!viewExistingData && (
                        <Stack
                          direction="row"
                          spacing={0.5}
                          alignItems="center"
                          sx={{
                            px: 1.5,
                            py: 0.5,
                            borderRadius: 1.5,
                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
                          }}
                        >
                          <ClockCircleOutlined style={{ color: theme.palette.primary.main, fontSize: '0.9rem' }} />
                          <SessionTimer sessionKey={sessionDetails?.id} sx={{ color: 'primary.main' }} />
                        </Stack>
                      )}
                    </Stack>
                  </>
                ) : currentTab === 'daily-mand' && child.sessionInProgress ? (
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Stack
                      direction="row"
                      spacing={0.5}
                      alignItems="center"
                      sx={{
                        px: 2,
                        py: 1,
                        borderRadius: 2,
                        bgcolor: alpha(theme.palette.success.main, 0.1),
                        color: 'success.main',
                        fontWeight: 600
                      }}
                    >
                      <ClockCircleOutlined />
                      <SessionTimer sessionKey={sessionDetails?.id} variant="large" sx={{ color: 'success.main' }} />
                    </Stack>
                    <Typography variant="body2" color="text.secondary">
                      {intl.formatMessage({ id: 'dataCollection.field.startedAt' }, { time: '15m ago' })}
                    </Typography>
                  </Stack>
                ) : null}
              </Stack>
            </Box>

            {/* Tab Navigation - hidden in view mode */}
            {!viewExistingData && (
              <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ minHeight: 48 }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Tabs
                      value={tabForms.some((f) => f.type === currentTab) ? currentTab : false}
                      onChange={handleTabChange}
                      variant="scrollable"
                      scrollButtons="auto"
                    >
                      {tabForms.map((form) => (
                        <Tab
                          key={form.type}
                          value={form.type}
                          disabled={form.comingSoon}
                          label={
                            <Stack direction="row" spacing={0.5} alignItems="center">
                              <span>{form.name}</span>
                              {/* Only Shortcut-promoted tabs are closable; the four
                                  defaults (and any auto-promoted fill) are not. */}
                              {addedTabTypes.has(form.type) && (
                                <Box
                                  component="span"
                                  role="button"
                                  tabIndex={0}
                                  aria-label={intl.formatMessage({ id: 'common.close' })}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleCloseSessionTab(form.type);
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                      e.stopPropagation();
                                      e.preventDefault();
                                      handleCloseSessionTab(form.type);
                                    }
                                  }}
                                  sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: 18,
                                    height: 18,
                                    borderRadius: '50%',
                                    color: 'error.main',
                                    '&:hover': { bgcolor: alpha(theme.palette.error.main, 0.14) }
                                  }}
                                >
                                  <CloseOutlined style={{ fontSize: '0.65rem' }} />
                                </Box>
                              )}
                              {form.comingSoon && (
                                <Chip
                                  label={intl.formatMessage({ id: 'common.comingSoon' })}
                                  size="small"
                                  color="info"
                                  sx={{
                                    height: 18,
                                    fontSize: '0.65rem',
                                    '& .MuiChip-label': { px: 0.75 }
                                  }}
                                />
                              )}
                              {form.status === 'in-progress' && !form.comingSoon && (
                                <Box
                                  sx={{
                                    width: 6,
                                    height: 6,
                                    borderRadius: '50%',
                                    bgcolor: 'warning.main'
                                  }}
                                />
                              )}
                              {form.hasNotification && (
                                <Badge badgeContent={form.notificationCount} color="error">
                                  <BellOutlined style={{ fontSize: '0.875rem' }} />
                                </Badge>
                              )}
                            </Stack>
                          }
                        />
                      ))}
                    </Tabs>
                  </Box>

                  {activeBehaviorTimer && (
                    <Tooltip
                      title={intl.formatMessage({
                        id: 'dataCollection.behaviorTimer.tooltip',
                        defaultMessage: 'Behavior timer running — click stop to end'
                      })}
                    >
                      <Paper
                        elevation={0}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          px: 1.25,
                          py: 0.5,
                          borderRadius: 1,
                          bgcolor: alpha(theme.palette.error.main, 0.08),
                          border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
                          mr: 1
                        }}
                      >
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            bgcolor: 'error.main',
                            animation: 'recBlink 1s infinite',
                            '@keyframes recBlink': {
                              '0%, 100%': { opacity: 1 },
                              '50%': { opacity: 0.3 }
                            }
                          }}
                        />
                        <Stack direction="column" sx={{ lineHeight: 1.1, minWidth: 0 }}>
                          <Typography
                            variant="caption"
                            sx={{
                              color: 'error.main',
                              fontWeight: 700,
                              fontSize: '0.65rem',
                              textTransform: 'uppercase',
                              letterSpacing: 0.5,
                              lineHeight: 1
                            }}
                          >
                            REC · {activeBehaviorTimer.behaviorName}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              fontFamily: 'monospace',
                              fontWeight: 700,
                              color: 'error.dark',
                              fontSize: '0.8rem',
                              lineHeight: 1.2
                            }}
                          >
                            {`${Math.floor(activeBehaviorTimer.elapsed / 60)}:${String(activeBehaviorTimer.elapsed % 60).padStart(2, '0')}`}
                          </Typography>
                        </Stack>
                        <Button
                          onClick={() => activeBehaviorTimer.stop?.()}
                          size="small"
                          variant="contained"
                          color="error"
                          startIcon={<StopOutlined style={{ fontSize: '0.75rem' }} />}
                          sx={{
                            ml: 0.5,
                            minHeight: 0,
                            py: 0.25,
                            px: 1,
                            fontSize: '0.7rem',
                            textTransform: 'none'
                          }}
                        >
                          {intl.formatMessage({ id: 'dataCollection.behaviorTimer.stop', defaultMessage: 'Stop' })}
                        </Button>
                      </Paper>
                    </Tooltip>
                  )}

                  {overflowForms.length > 0 && (
                    <Tooltip
                      title={intl.formatMessage({
                        id: 'dataCollection.overflow.tooltip',
                        defaultMessage: 'More data sheets'
                      })}
                    >
                      <Badge
                        badgeContent={overflowProgress.done}
                        color="success"
                        max={9}
                        invisible={overflowProgress.done === 0}
                        overlap="circular"
                        sx={{ mr: 1.5 }}
                      >
                        <Button
                          onClick={openOverflowMenu}
                          variant="contained"
                          color="primary"
                          size="small"
                          aria-label="More data sheets"
                          endIcon={<MoreOutlined style={{ fontSize: '0.95rem' }} />}
                          sx={{
                            minWidth: 'auto',
                            px: { xs: 1, sm: 1.5 },
                            py: 0.75,
                            borderRadius: 999,
                            fontWeight: 600,
                            fontSize: '0.75rem',
                            textTransform: 'none',
                            boxShadow: 2,
                            '&:hover': { boxShadow: 4 },
                            '& .MuiButton-endIcon': { ml: { xs: 0, sm: 0.5 } }
                          }}
                        >
                          {/* Label hidden on xs — only the MoreOutlined dots
                              show, keeping the tabs row uncluttered on mobile. */}
                          <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                            {intl.formatMessage({
                              id: 'dataCollection.overflow.moreButton',
                              defaultMessage: 'Shortcut'
                            })}
                            {/* Count = datasheets with data entered this session (menu +
                                promoted tabs), not the number remaining in the menu. */}
                            {overflowProgress.done > 0 ? ` (${overflowProgress.done})` : ''}
                          </Box>
                        </Button>
                      </Badge>
                    </Tooltip>
                  )}
                </Stack>

                <Menu
                  anchorEl={overflowMenuAnchor}
                  open={Boolean(overflowMenuAnchor)}
                  onClose={closeOverflowMenu}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                  // Cap paper height to the viewport (minus the sticky header)
                  // and make the list scroll. Without this, MUI tries to fit
                  // the full menu height and flips/clips when the Shortcut
                  // button sits near the top edge of the viewport.
                  marginThreshold={16}
                  slotProps={{
                    paper: {
                      sx: {
                        minWidth: 260,
                        maxWidth: 320,
                        maxHeight: 'calc(100vh - 96px)',
                        overflowY: 'auto'
                      }
                    }
                  }}
                  MenuListProps={{ sx: { py: 0 } }}
                >
                  {/* QUICK ACTIONS — the things that are not data sheets. The heading now
                      covers two entries and either can be absent, so it is driven by whether
                      anything under it survives rather than by Add Target alone. */}
                  {(addTargetCtx.goals.length > 0 || addBehaviorAvailable || diaryAvailable) && [
                    <Box key="quick-actions-heading" sx={{ px: 2, py: 1 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600 }}>
                        Quick Actions
                      </Typography>
                    </Box>,
                    addTargetCtx.goals.length > 0 && (
                      <MenuItem key="add-target" onClick={handleOpenAddTarget} sx={{ py: 1.25 }}>
                        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ width: '100%' }}>
                          <Box
                            sx={{
                              width: 20,
                              height: 20,
                              borderRadius: '50%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              bgcolor: alpha(theme.palette.primary.main, 0.12),
                              color: theme.palette.primary.main
                            }}
                          >
                            <PlusOutlined style={{ fontSize: '0.7rem' }} />
                          </Box>
                          <Typography variant="body2" sx={{ flex: 1 }}>
                            Add Target
                          </Typography>
                        </Stack>
                      </MenuItem>
                    ),
                    addBehaviorAvailable && (
                      <MenuItem
                        key="add-behavior"
                        onClick={() => {
                          setOverflowMenuAnchor(null);
                          setBehaviorsOpen(true);
                        }}
                        sx={{ py: 1.25 }}
                      >
                        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ width: '100%' }}>
                          <Box
                            sx={{
                              width: 20,
                              height: 20,
                              borderRadius: '50%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              bgcolor: alpha(theme.palette.primary.main, 0.12),
                              color: theme.palette.primary.main
                            }}
                          >
                            <PlusOutlined style={{ fontSize: '0.7rem' }} />
                          </Box>
                          <Typography variant="body2" sx={{ flex: 1 }}>
                            {intl.formatMessage({ id: 'dataCollection.overflow.addBehavior', defaultMessage: 'Add Behavior' })}
                          </Typography>
                        </Stack>
                      </MenuItem>
                    ),
                    diaryAvailable && (
                      <MenuItem key="communication-diary" onClick={handleOpenDiary} sx={{ py: 1.25 }}>
                        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ width: '100%' }}>
                          <Box
                            sx={{
                              width: 20,
                              height: 20,
                              borderRadius: '50%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              bgcolor: alpha(theme.palette.primary.main, 0.12),
                              color: theme.palette.primary.main
                            }}
                          >
                            <MessageOutlined style={{ fontSize: '0.7rem' }} />
                          </Box>
                          <Typography variant="body2" sx={{ flex: 1 }}>
                            {intl.formatMessage({
                              id: 'dataCollection.overflow.communicationDiary',
                              defaultMessage: 'Communication Diary'
                            })}
                          </Typography>
                        </Stack>
                      </MenuItem>
                    ),
                    <Divider key="quick-actions-divider" />
                  ]}
                  {menuForms.length > 0 && (
                    <Box sx={{ px: 2, py: 1 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600 }}>
                        {intl.formatMessage({ id: 'dataCollection.overflow.heading', defaultMessage: 'Collect Additional Data' })}
                      </Typography>
                    </Box>
                  )}
                  {menuForms.length > 0 && <Divider />}
                  {menuForms.map((form) => {
                    const done = !!dataCollectionState[form.type]?.hasData;
                    return (
                      <MenuItem
                        key={form.type}
                        disabled={form.comingSoon}
                        onClick={() => handleOverflowItemSelect(form.type)}
                        sx={{ py: 1.25 }}
                      >
                        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ width: '100%' }}>
                          <Box
                            sx={{
                              width: 20,
                              height: 20,
                              borderRadius: '50%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              bgcolor: done ? 'success.main' : alpha(theme.palette.text.secondary, 0.15),
                              color: 'common.white',
                              fontSize: '0.75rem'
                            }}
                          >
                            {done ? <CheckOutlined style={{ fontSize: '0.7rem' }} /> : null}
                          </Box>
                          <Typography variant="body2" sx={{ flex: 1 }}>
                            {form.name}
                          </Typography>
                          {form.comingSoon && (
                            <Chip
                              label={intl.formatMessage({ id: 'common.comingSoon' })}
                              size="small"
                              color="info"
                              sx={{ height: 18, fontSize: '0.65rem', '& .MuiChip-label': { px: 0.75 } }}
                            />
                          )}
                        </Stack>
                      </MenuItem>
                    );
                  })}
                </Menu>
              </Box>
            )}
          </Stack>
        )}
      </MainCard>

      {/* Toolbar - hidden in view mode, and on sheets with only one layout */}
      {!viewExistingData && !NO_VIEW_MODE_TYPES.has(currentTab) && (
        <MainCard sx={{ mb: 3, maxWidth: '100%', overflowX: 'hidden', display: { xs: 'none', sm: 'block' } }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            spacing={2}
            sx={{ width: '100%' }}
          >
            <Typography variant="h5">
              {availableForms.find((f) => f.type === currentTab)?.name ||
                intl.formatMessage({ id: 'dataCollection.header.dataCollection' })}
            </Typography>

            <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', gap: 1 }}>
              {gridAllowed && (
                <ToggleButtonGroup value={viewMode} exclusive onChange={(_, value) => value && setViewMode(value)} size="small">
                  <ToggleButton value="individual">
                    <UserOutlined style={{ marginRight: 4 }} /> {intl.formatMessage({ id: 'dataCollection.toolbar.individual' })}
                  </ToggleButton>
                  <ToggleButton value="grid">
                    <TableOutlined style={{ marginRight: 4 }} /> {intl.formatMessage({ id: 'dataCollection.toolbar.grid' })}
                  </ToggleButton>
                </ToggleButtonGroup>
              )}

              {/* Export hidden for now — no functionality attached yet. Re-enable (and the
                  DownloadOutlined import) once export is implemented.
              <Button variant="outlined" size="small" startIcon={<DownloadOutlined />}>
                {intl.formatMessage({ id: 'common.export' })}
              </Button>
              */}
            </Stack>
          </Stack>
        </MainCard>
      )}

      {/* Existing Data View — shown when viewing a completed session's collected data */}
      {viewExistingData && (
        <Box sx={{ mb: 3 }}>
          {isLoadingExistingData ? (
            <MainCard>
              <Typography sx={{ textAlign: 'center', py: 4 }}>
                {intl.formatMessage({ id: 'dataCollection.loading.collectedData' })}
              </Typography>
            </MainCard>
          ) : (
            <>
              {/* Session Notes — parse from fetchedSessionNotes (JSON) or location.state */}
              {(() => {
                // Parse notes: could be JSON { preSessionNotes, completionNotes } or plain text
                let preNotes = sessionDetails?.preSessionNotes || '';
                let completionNotes = '';

                if (fetchedSessionNotes) {
                  try {
                    const parsed = JSON.parse(fetchedSessionNotes);
                    preNotes = parsed.preSessionNotes || preNotes;
                    completionNotes = parsed.completionNotes || '';
                  } catch {
                    // Plain text — treat as completion notes
                    completionNotes = fetchedSessionNotes;
                  }
                }

                if (!preNotes && !completionNotes) return null;

                return (
                  <>
                    {preNotes && (
                      <MainCard sx={{ mb: 2 }}>
                        <Stack spacing={1}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <FileTextOutlined style={{ fontSize: 16, color: theme.palette.primary.main }} />
                            <Typography variant="subtitle1" fontWeight={600}>
                              {intl.formatMessage({ id: 'dataCollection.section.preSessionNotes' })}
                            </Typography>
                          </Stack>
                          <Paper elevation={0} sx={{ p: 1.5, bgcolor: alpha(theme.palette.primary.main, 0.06), borderRadius: 1 }}>
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                              {preNotes}
                            </Typography>
                          </Paper>
                        </Stack>
                      </MainCard>
                    )}
                    {completionNotes && (
                      <MainCard sx={{ mb: 2 }}>
                        <Stack spacing={1}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <CheckOutlined style={{ fontSize: 16, color: theme.palette.success.main }} />
                            <Typography variant="subtitle1" fontWeight={600}>
                              {intl.formatMessage({ id: 'dataCollection.section.completionNotes' })}
                            </Typography>
                          </Stack>
                          <Paper elevation={0} sx={{ p: 1.5, bgcolor: alpha(theme.palette.success.main, 0.06), borderRadius: 1 }}>
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                              {completionNotes}
                            </Typography>
                          </Paper>
                        </Stack>
                      </MainCard>
                    )}
                  </>
                );
              })()}

              {/* Data Collection Records */}
              {existingSessionData.length === 0 ? (
                <Alert severity="info" sx={{ mb: 2 }}>
                  {intl.formatMessage({ id: 'dataCollection.alert.noRecordsFound' })}
                </Alert>
              ) : (
                <>
                  <Alert severity="success" sx={{ mb: 2 }}>
                    {intl.formatMessage({ id: 'dataCollection.alert.recordsFound' }, { count: existingSessionData.length })}
                  </Alert>

                  {/* CORRECTED AFTER THE SESSION. A therapist cannot change what they recorded
                      once the session closes — a supervisor can, and the person whose data it
                      was is the first who should be told. Raised once above the list rather
                      than inside each of the ten per-sheet renderers below: one notice naming
                      the sheets reads better than ten badges, and every branch stays untouched. */}
                  {existingSessionData.some((r) => (r.amend_count || 0) > 0) && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      <Stack spacing={0.5}>
                        {existingSessionData
                          .filter((r) => (r.amend_count || 0) > 0)
                          .map((r) => (
                            <Typography key={r.id} variant="body2">
                              <strong>{r.datasheet_type?.name || r.raw_data?.formType}</strong>
                              {' — '}
                              {intl.formatMessage(
                                { id: 'dataCollection.alert.correctedBy', defaultMessage: 'corrected by {name}' },
                                { name: r.amended_by_name || intl.formatMessage({ id: 'common.unknown' }) }
                              )}
                              {r.amend_reason ? `: ${r.amend_reason}` : ''}
                            </Typography>
                          ))}
                      </Stack>
                    </Alert>
                  )}

                  {existingSessionData.map((record) => {
                    const formType = record.raw_data?.formType || 'unknown';
                    const formLabel = record.datasheet_type?.name || availableForms.find((f) => f.type === formType)?.name || formType;
                    const collectedTime = new Date(record.collected_at + (record.collected_at.endsWith('Z') ? '' : 'Z')).toLocaleTimeString(
                      'en-US',
                      { hour: 'numeric', minute: '2-digit', hour12: true }
                    );

                    // ---- Injury Report: custom accordion view ----
                    if (formType === 'injury-report') {
                      const rd = record.raw_data || {};
                      const isClient = rd.reportType === 'client';
                      const injuredName = isClient ? rd.injuredLearnerName : rd.injuredStaffName;
                      const locations = rd.injuryLocations || [];
                      const formatDate = (v) => {
                        try {
                          return new Date(v).toLocaleDateString();
                        } catch {
                          return '';
                        }
                      };
                      const formatTime = (v) => {
                        try {
                          return new Date(v).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
                        } catch {
                          return '';
                        }
                      };

                      return (
                        <Accordion
                          key={record.id}
                          sx={{
                            mb: 2,
                            '&::before': { display: 'none' },
                            boxShadow: theme.customShadows?.z1 || 1,
                            borderRadius: '8px !important',
                            overflow: 'hidden'
                          }}
                        >
                          <AccordionSummary
                            expandIcon={<DownOutlined />}
                            sx={{ px: 2.5, py: 1.5, minHeight: 56, '& .MuiAccordionSummary-content': { my: 1 } }}
                          >
                            <Stack direction="row" spacing={2} alignItems="center" sx={{ width: '100%', pr: 2 }}>
                              <Typography variant="h5">{intl.formatMessage({ id: 'dataCollection.section.injuryReport' })}</Typography>
                              <Chip
                                label={
                                  isClient
                                    ? intl.formatMessage({ id: 'dataCollection.chip.client' })
                                    : intl.formatMessage({ id: 'dataCollection.chip.staff' })
                                }
                                size="small"
                                color="warning"
                              />
                              {injuredName && (
                                <Typography variant="body2" color="text.secondary">
                                  {injuredName}
                                </Typography>
                              )}
                              {locations.length > 0 && (
                                <Chip
                                  label={intl.formatMessage({ id: 'dataCollection.chip.locations' }, { count: locations.length })}
                                  size="small"
                                  variant="outlined"
                                />
                              )}
                              <Box sx={{ flex: 1 }} />
                              <Chip label={collectedTime} size="small" color="primary" variant="outlined" />
                            </Stack>
                          </AccordionSummary>
                          <AccordionDetails sx={{ px: 2.5, pb: 2.5 }}>
                            <Stack spacing={2.5}>
                              {/* Report Info */}
                              <Box>
                                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                                  {intl.formatMessage({ id: 'dataCollection.section.reportInformation' })}
                                </Typography>
                                <Grid container spacing={2}>
                                  <Grid size={{ xs: 6, sm: 3 }}>
                                    <Typography variant="caption" display="block" color="text.secondary" sx={{ mb: 0.5 }}>
                                      {intl.formatMessage({ id: 'dataCollection.field.reportType' })}
                                    </Typography>
                                    <Typography variant="body2" fontWeight={600}>
                                      {isClient
                                        ? intl.formatMessage({ id: 'dataCollection.field.clientInjury' })
                                        : intl.formatMessage({ id: 'dataCollection.field.staffInjury' })}
                                    </Typography>
                                  </Grid>
                                  <Grid size={{ xs: 6, sm: 3 }}>
                                    <Typography variant="caption" display="block" color="text.secondary" sx={{ mb: 0.5 }}>
                                      {intl.formatMessage({ id: 'dataCollection.field.injuredPerson' })}
                                    </Typography>
                                    <Typography variant="body2" fontWeight={600}>
                                      {injuredName || '—'}
                                    </Typography>
                                  </Grid>
                                  <Grid size={{ xs: 6, sm: 3 }}>
                                    <Typography variant="caption" display="block" color="text.secondary" sx={{ mb: 0.5 }}>
                                      {intl.formatMessage({ id: 'dataCollection.field.dateOfInjury' })}
                                    </Typography>
                                    <Typography variant="body2">{formatDate(rd.dateOfInjury)}</Typography>
                                  </Grid>
                                  <Grid size={{ xs: 6, sm: 3 }}>
                                    <Typography variant="caption" display="block" color="text.secondary" sx={{ mb: 0.5 }}>
                                      {intl.formatMessage({ id: 'dataCollection.field.timeOfInjury' })}
                                    </Typography>
                                    <Typography variant="body2">{formatTime(rd.timeOfInjury)}</Typography>
                                  </Grid>
                                  {rd.staffPresent && (
                                    <Grid size={{ xs: 6, sm: 3 }}>
                                      <Typography variant="caption" display="block" color="text.secondary" sx={{ mb: 0.5 }}>
                                        {intl.formatMessage({ id: 'dataCollection.field.staffPresent' })}
                                      </Typography>
                                      <Typography variant="body2">{rd.staffPresent}</Typography>
                                    </Grid>
                                  )}
                                  {rd.witnessesPresent && (
                                    <Grid size={{ xs: 6, sm: 3 }}>
                                      <Typography variant="caption" display="block" color="text.secondary" sx={{ mb: 0.5 }}>
                                        {intl.formatMessage({ id: 'dataCollection.field.witnesses' })}
                                      </Typography>
                                      <Typography variant="body2">{rd.witnessesPresent}</Typography>
                                    </Grid>
                                  )}
                                </Grid>
                              </Box>

                              <Divider />

                              {/* Injury Locations */}
                              {locations.length > 0 && (
                                <Box>
                                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                                    {intl.formatMessage({ id: 'dataCollection.section.injuryLocations' })}
                                  </Typography>
                                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                    {locations.map((loc, idx) => (
                                      <Chip
                                        key={idx}
                                        label={
                                          typeof loc === 'string'
                                            ? loc
                                            : loc.name ||
                                              loc.label ||
                                              intl.formatMessage({ id: 'dataCollection.fallback.locationNumber' }, { number: idx + 1 })
                                        }
                                        size="small"
                                        color="error"
                                        variant="outlined"
                                      />
                                    ))}
                                  </Stack>
                                </Box>
                              )}

                              {/* Incident Description */}
                              {rd.incidentDescription && (
                                <Box>
                                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                                    {intl.formatMessage({ id: 'dataCollection.section.incidentDescription' })}
                                  </Typography>
                                  <Paper elevation={0} sx={{ p: 1.5, bgcolor: alpha(theme.palette.warning.main, 0.06), borderRadius: 1 }}>
                                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                      {rd.incidentDescription}
                                    </Typography>
                                  </Paper>
                                </Box>
                              )}

                              {/* ABC Data */}
                              {(rd.antecedent || rd.behavior || rd.consequence) && (
                                <>
                                  <Divider />
                                  <Box>
                                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                                      {intl.formatMessage({ id: 'dataCollection.section.abcAnalysis' })}
                                    </Typography>
                                    <Grid container spacing={2}>
                                      {rd.antecedent && (
                                        <Grid size={{ xs: 12, sm: 4 }}>
                                          <Typography variant="caption" display="block" color="text.secondary" sx={{ mb: 0.5 }}>
                                            {intl.formatMessage({ id: 'dataCollection.field.antecedent' })}
                                          </Typography>
                                          <Typography variant="body2">{rd.antecedent}</Typography>
                                        </Grid>
                                      )}
                                      {rd.behavior && (
                                        <Grid size={{ xs: 12, sm: 4 }}>
                                          <Typography variant="caption" display="block" color="text.secondary" sx={{ mb: 0.5 }}>
                                            {intl.formatMessage({ id: 'dataCollection.field.behavior' })}
                                          </Typography>
                                          <Typography variant="body2">{rd.behavior}</Typography>
                                        </Grid>
                                      )}
                                      {rd.consequence && (
                                        <Grid size={{ xs: 12, sm: 4 }}>
                                          <Typography variant="caption" display="block" color="text.secondary" sx={{ mb: 0.5 }}>
                                            {intl.formatMessage({ id: 'dataCollection.field.consequence' })}
                                          </Typography>
                                          <Typography variant="body2">{rd.consequence}</Typography>
                                        </Grid>
                                      )}
                                      {rd.duration && (
                                        <Grid size={{ xs: 12, sm: 4 }}>
                                          <Typography variant="caption" display="block" color="text.secondary" sx={{ mb: 0.5 }}>
                                            {intl.formatMessage({ id: 'dataCollection.field.duration' })}
                                          </Typography>
                                          <Typography variant="body2">{rd.duration}</Typography>
                                        </Grid>
                                      )}
                                    </Grid>
                                  </Box>
                                </>
                              )}

                              <Divider />

                              {/* First Aid & Hospital */}
                              <Box>
                                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                                  {intl.formatMessage({ id: 'dataCollection.section.responseNotifications' })}
                                </Typography>
                                <Grid container spacing={2}>
                                  <Grid size={{ xs: 6, sm: 3 }}>
                                    <Typography variant="caption" display="block" color="text.secondary" sx={{ mb: 0.5 }}>
                                      {intl.formatMessage({ id: 'dataCollection.field.firstAid' })}
                                    </Typography>
                                    <Chip
                                      label={
                                        rd.firstAidAdministered === 'yes'
                                          ? intl.formatMessage({ id: 'common.yes' })
                                          : intl.formatMessage({ id: 'common.no' })
                                      }
                                      size="small"
                                      color={rd.firstAidAdministered === 'yes' ? 'success' : 'default'}
                                    />
                                  </Grid>
                                  {rd.firstAidByWhom && (
                                    <Grid size={{ xs: 6, sm: 3 }}>
                                      <Typography variant="caption" display="block" color="text.secondary" sx={{ mb: 0.5 }}>
                                        {intl.formatMessage({ id: 'dataCollection.field.firstAidBy' })}
                                      </Typography>
                                      <Typography variant="body2">{rd.firstAidByWhom}</Typography>
                                    </Grid>
                                  )}
                                  <Grid size={{ xs: 6, sm: 3 }}>
                                    <Typography variant="caption" display="block" color="text.secondary" sx={{ mb: 0.5 }}>
                                      {intl.formatMessage({ id: 'dataCollection.field.hospitalVisit' })}
                                    </Typography>
                                    <Chip
                                      label={
                                        rd.hospitalVisitNeeded === 'yes'
                                          ? intl.formatMessage({ id: 'common.yes' })
                                          : intl.formatMessage({ id: 'common.no' })
                                      }
                                      size="small"
                                      color={rd.hospitalVisitNeeded === 'yes' ? 'error' : 'default'}
                                    />
                                  </Grid>
                                  {rd.hospitalDetails && (
                                    <Grid size={{ xs: 12, sm: 6 }}>
                                      <Typography variant="caption" display="block" color="text.secondary" sx={{ mb: 0.5 }}>
                                        {intl.formatMessage({ id: 'dataCollection.field.hospitalDetails' })}
                                      </Typography>
                                      <Typography variant="body2">{rd.hospitalDetails}</Typography>
                                    </Grid>
                                  )}
                                  {isClient && rd.parentNotified && (
                                    <Grid size={{ xs: 6, sm: 3 }}>
                                      <Typography variant="caption" display="block" color="text.secondary" sx={{ mb: 0.5 }}>
                                        {intl.formatMessage({ id: 'dataCollection.field.parentNotified' })}
                                      </Typography>
                                      <Chip
                                        label={
                                          rd.parentNotified === 'yes'
                                            ? intl.formatMessage({ id: 'common.yes' })
                                            : intl.formatMessage({ id: 'common.no' })
                                        }
                                        size="small"
                                        color={rd.parentNotified === 'yes' ? 'info' : 'default'}
                                      />
                                    </Grid>
                                  )}
                                  {!isClient && rd.administratorNotified && (
                                    <Grid size={{ xs: 6, sm: 3 }}>
                                      <Typography variant="caption" display="block" color="text.secondary" sx={{ mb: 0.5 }}>
                                        {intl.formatMessage({ id: 'dataCollection.field.administratorNotified' })}
                                      </Typography>
                                      <Chip
                                        label={
                                          rd.administratorNotified === 'yes'
                                            ? intl.formatMessage({ id: 'common.yes' })
                                            : intl.formatMessage({ id: 'common.no' })
                                        }
                                        size="small"
                                        color={rd.administratorNotified === 'yes' ? 'info' : 'default'}
                                      />
                                    </Grid>
                                  )}
                                </Grid>
                              </Box>

                              {/* Injury Photos */}
                              {rd.injuryPhotos?.length > 0 && (
                                <>
                                  <Divider />
                                  <Box>
                                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                                      {intl.formatMessage({ id: 'dataCollection.section.injuryPhotos' })}
                                    </Typography>
                                    <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
                                      {rd.injuryPhotos.map((photo, idx) => (
                                        <ServerImage
                                          key={photo.id || idx}
                                          photo={photo}
                                          alt={
                                            photo.name ||
                                            intl.formatMessage({ id: 'dataCollection.field.injuryPhoto' }, { number: idx + 1 })
                                          }
                                          sx={{
                                            width: 120,
                                            height: 120,
                                            objectFit: 'cover',
                                            borderRadius: 1,
                                            border: '1px solid',
                                            borderColor: 'divider',
                                            cursor: 'pointer'
                                          }}
                                          onClick={(resolvedSrc) => resolvedSrc && window.open(resolvedSrc, '_blank')}
                                        />
                                      ))}
                                    </Stack>
                                  </Box>
                                </>
                              )}

                              {/* Environment Damage */}
                              {rd.environmentDamage === 'yes' && (
                                <>
                                  <Divider />
                                  <Box>
                                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                                      {intl.formatMessage({ id: 'dataCollection.section.environmentDamage' })}
                                    </Typography>
                                    {rd.environmentDamageDetails && (
                                      <Paper
                                        elevation={0}
                                        sx={{ p: 1.5, bgcolor: alpha(theme.palette.error.main, 0.06), borderRadius: 1, mb: 1.5 }}
                                      >
                                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                          {rd.environmentDamageDetails}
                                        </Typography>
                                      </Paper>
                                    )}
                                    {rd.environmentDamageImages?.length > 0 && (
                                      <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
                                        {rd.environmentDamageImages.map((photo, idx) => (
                                          <ServerImage
                                            key={photo.id || idx}
                                            photo={photo}
                                            alt={
                                              photo.name ||
                                              intl.formatMessage({ id: 'dataCollection.field.damagePhoto' }, { number: idx + 1 })
                                            }
                                            sx={{
                                              width: 120,
                                              height: 120,
                                              objectFit: 'cover',
                                              borderRadius: 1,
                                              border: '1px solid',
                                              borderColor: 'divider',
                                              cursor: 'pointer'
                                            }}
                                            onClick={(resolvedSrc) => resolvedSrc && window.open(resolvedSrc, '_blank')}
                                          />
                                        ))}
                                      </Stack>
                                    )}
                                  </Box>
                                </>
                              )}

                              {/* Related Injury */}
                              {rd.relatedChildOrStaffInjury === 'yes' && (
                                <Box>
                                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                                    {intl.formatMessage({ id: 'dataCollection.section.relatedInjury' })}
                                  </Typography>
                                  {rd.relatedChildName && (
                                    <Typography variant="body2">
                                      {intl.formatMessage({ id: 'dataCollection.field.relatedChild' }, { name: rd.relatedChildName })}
                                    </Typography>
                                  )}
                                  {rd.relatedStaffName && (
                                    <Typography variant="body2">
                                      {intl.formatMessage({ id: 'dataCollection.field.relatedStaff' }, { name: rd.relatedStaffName })}
                                    </Typography>
                                  )}
                                </Box>
                              )}

                              {/* Signatures */}
                              {(rd.adminSignature || rd.parentSignature || rd.therapistSignature || rd.injuredTherapistSignature) && (
                                <>
                                  <Divider />
                                  <Box>
                                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                                      {intl.formatMessage({ id: 'dataCollection.section.signatures' })}
                                    </Typography>
                                    <Grid container spacing={2}>
                                      {rd.adminSignature && (
                                        <Grid size={{ xs: 12, sm: 4 }}>
                                          <Typography variant="caption" color="text.secondary">
                                            {intl.formatMessage({ id: 'dataCollection.field.administrator' })}
                                          </Typography>
                                          <Box
                                            component="img"
                                            src={rd.adminSignature}
                                            alt={intl.formatMessage({ id: 'dataCollection.field.administrator' })}
                                            sx={{
                                              display: 'block',
                                              height: 80,
                                              border: '1px solid',
                                              borderColor: 'divider',
                                              borderRadius: 1,
                                              bgcolor: 'grey.50'
                                            }}
                                          />
                                        </Grid>
                                      )}
                                      {rd.parentSignature && (
                                        <Grid size={{ xs: 12, sm: 4 }}>
                                          <Typography variant="caption" color="text.secondary">
                                            {intl.formatMessage({ id: 'dataCollection.field.parent' })}
                                          </Typography>
                                          <Box
                                            component="img"
                                            src={rd.parentSignature}
                                            alt={intl.formatMessage({ id: 'dataCollection.field.parent' })}
                                            sx={{
                                              display: 'block',
                                              height: 80,
                                              border: '1px solid',
                                              borderColor: 'divider',
                                              borderRadius: 1,
                                              bgcolor: 'grey.50'
                                            }}
                                          />
                                        </Grid>
                                      )}
                                      {rd.therapistSignature && (
                                        <Grid size={{ xs: 12, sm: 4 }}>
                                          <Typography variant="caption" color="text.secondary">
                                            {intl.formatMessage({ id: 'dataCollection.field.therapist' })}
                                          </Typography>
                                          <Box
                                            component="img"
                                            src={rd.therapistSignature}
                                            alt={intl.formatMessage({ id: 'dataCollection.field.therapist' })}
                                            sx={{
                                              display: 'block',
                                              height: 80,
                                              border: '1px solid',
                                              borderColor: 'divider',
                                              borderRadius: 1,
                                              bgcolor: 'grey.50'
                                            }}
                                          />
                                        </Grid>
                                      )}
                                      {rd.injuredTherapistSignature && (
                                        <Grid size={{ xs: 12, sm: 4 }}>
                                          <Typography variant="caption" color="text.secondary">
                                            {intl.formatMessage({ id: 'dataCollection.field.injuredTherapist' })}
                                          </Typography>
                                          <Box
                                            component="img"
                                            src={rd.injuredTherapistSignature}
                                            alt={intl.formatMessage({ id: 'dataCollection.field.injuredTherapist' })}
                                            sx={{
                                              display: 'block',
                                              height: 80,
                                              border: '1px solid',
                                              borderColor: 'divider',
                                              borderRadius: 1,
                                              bgcolor: 'grey.50'
                                            }}
                                          />
                                        </Grid>
                                      )}
                                    </Grid>
                                  </Box>
                                </>
                              )}

                              {/* Notes */}
                              {record.notes && (
                                <Box>
                                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                                    {intl.formatMessage({ id: 'common.notes' })}
                                  </Typography>
                                  <Paper elevation={0} sx={{ p: 1.5, bgcolor: alpha(theme.palette.info.main, 0.08), borderRadius: 1 }}>
                                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                      {record.notes}
                                    </Typography>
                                  </Paper>
                                </Box>
                              )}
                            </Stack>
                          </AccordionDetails>
                        </Accordion>
                      );
                    }

                    // ---- Behavior Tracking: custom card view ----
                    if (formType === 'behavior-tracking') {
                      const rd = record.raw_data || {};
                      const behaviors = rd.behaviors || [];
                      const occurrences = rd.occurrences || [];
                      // Only show behaviors that have actual data
                      const activeBehaviors = behaviors.filter((b) => (b.frequencyCount || 0) > 0 || (b.totalDuration || 0) > 0);
                      const formatDuration = (seconds) => {
                        if (!seconds) return '0s';
                        const m = Math.floor(seconds / 60);
                        const s = seconds % 60;
                        return m > 0 ? `${m}m ${s}s` : `${s}s`;
                      };

                      return (
                        <Accordion
                          key={record.id}
                          sx={{
                            mb: 2,
                            '&::before': { display: 'none' },
                            boxShadow: theme.customShadows?.z1 || 1,
                            borderRadius: '8px !important',
                            overflow: 'hidden'
                          }}
                        >
                          <AccordionSummary
                            expandIcon={<DownOutlined />}
                            sx={{ px: 2.5, py: 1.5, minHeight: 56, '& .MuiAccordionSummary-content': { my: 1 } }}
                          >
                            <Stack direction="row" spacing={2} alignItems="center" sx={{ width: '100%', mr: 2 }}>
                              <Typography variant="h5">{formLabel}</Typography>
                              <Chip
                                label={intl.formatMessage(
                                  { id: 'dataCollection.chip.totalEvents' },
                                  { count: rd.totalOccurrences || occurrences.length }
                                )}
                                size="small"
                                color="primary"
                                variant="outlined"
                              />
                              <Box sx={{ flex: 1 }} />
                              <Chip label={collectedTime} size="small" color="primary" variant="outlined" />
                            </Stack>
                          </AccordionSummary>
                          <AccordionDetails sx={{ px: 2.5, pb: 2.5 }}>
                            <Stack spacing={2}>
                              {activeBehaviors.length === 0 ? (
                                <Typography variant="body2" color="text.secondary">
                                  {intl.formatMessage({ id: 'dataCollection.empty.noBehaviorEvents' })}
                                </Typography>
                              ) : (
                                <Stack spacing={1.5}>
                                  {activeBehaviors.map((b, idx) => {
                                    const hasFrequency = (b.frequencyCount || 0) > 0;
                                    const hasDuration = (b.totalDuration || 0) > 0;
                                    const behaviorOccurrences = occurrences.filter(
                                      (o) => o.behaviorName === b.behaviorName || o.behaviorId === b.behaviorId
                                    );
                                    return (
                                      <Paper
                                        key={idx}
                                        elevation={0}
                                        sx={{
                                          p: 2,
                                          bgcolor: alpha(theme.palette.primary.main, 0.04),
                                          borderRadius: 1.5,
                                          border: '1px solid',
                                          borderColor: 'divider'
                                        }}
                                      >
                                        <Stack spacing={1}>
                                          <Typography variant="subtitle1" fontWeight={600}>
                                            {b.behaviorName || b.name}
                                          </Typography>
                                          <Stack direction="row" spacing={2} flexWrap="wrap">
                                            {hasFrequency && (
                                              <Chip
                                                label={intl.formatMessage(
                                                  { id: 'dataCollection.chip.counts' },
                                                  { count: b.frequencyCount }
                                                )}
                                                size="small"
                                                color="warning"
                                                variant="filled"
                                              />
                                            )}
                                            {hasDuration && (
                                              <Chip
                                                label={intl.formatMessage(
                                                  { id: 'dataCollection.chip.totalDuration' },
                                                  { duration: formatDuration(b.totalDuration) }
                                                )}
                                                size="small"
                                                color="info"
                                                variant="filled"
                                              />
                                            )}
                                          </Stack>
                                          {hasDuration && behaviorOccurrences.filter((o) => o.type === 'duration').length > 1 && (
                                            <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 0.5 }}>
                                              {behaviorOccurrences
                                                .filter((o) => o.type === 'duration')
                                                .map((o, i) => (
                                                  <Chip
                                                    key={i}
                                                    label={intl.formatMessage(
                                                      { id: 'dataCollection.chip.episode' },
                                                      { number: i + 1, duration: formatDuration(o.duration) }
                                                    )}
                                                    size="small"
                                                    variant="outlined"
                                                  />
                                                ))}
                                            </Stack>
                                          )}
                                        </Stack>
                                      </Paper>
                                    );
                                  })}
                                </Stack>
                              )}

                              {record.notes && (
                                <Box>
                                  <Typography variant="caption" color="text.secondary">
                                    {intl.formatMessage({ id: 'common.notes' })}
                                  </Typography>
                                  <Paper elevation={0} sx={{ p: 1.5, bgcolor: alpha(theme.palette.info.main, 0.08), borderRadius: 1 }}>
                                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                      {record.notes}
                                    </Typography>
                                  </Paper>
                                </Box>
                              )}
                            </Stack>
                          </AccordionDetails>
                        </Accordion>
                      );
                    }

                    // ---- Behavior Interval: custom accordion view ----
                    if (formType === 'behavior-interval') {
                      const rd = record.raw_data || {};
                      const intervalData = rd.data || [];
                      const colorMap = { whole: 'success', part: 'warning', none: 'error', missed: 'default' };
                      const labelMap = {
                        whole: intl.formatMessage({ id: 'dataCollection.intervalLabel.whole' }),
                        part: intl.formatMessage({ id: 'dataCollection.intervalLabel.partial' }),
                        none: intl.formatMessage({ id: 'dataCollection.intervalLabel.none' }),
                        missed: intl.formatMessage({ id: 'dataCollection.intervalLabel.missed' })
                      };

                      return (
                        <Accordion
                          key={record.id}
                          sx={{
                            mb: 2,
                            '&::before': { display: 'none' },
                            boxShadow: theme.customShadows?.z1 || 1,
                            borderRadius: '8px !important',
                            overflow: 'hidden'
                          }}
                        >
                          <AccordionSummary
                            expandIcon={<DownOutlined />}
                            sx={{ px: 2.5, py: 1.5, minHeight: 56, '& .MuiAccordionSummary-content': { my: 1 } }}
                          >
                            <Stack direction="row" spacing={2} alignItems="center" sx={{ width: '100%', mr: 2 }}>
                              <Typography variant="h5">{formLabel}</Typography>
                              <Chip
                                label={rd.behavior || intl.formatMessage({ id: 'dataCollection.field.behavior' })}
                                size="small"
                                color="secondary"
                              />
                              <Chip
                                label={intl.formatMessage(
                                  { id: 'dataCollection.chip.intervals' },
                                  { count: rd.totalIntervals || intervalData.length }
                                )}
                                size="small"
                                variant="outlined"
                              />
                              <Box sx={{ flex: 1 }} />
                              <Chip label={collectedTime} size="small" color="primary" variant="outlined" />
                            </Stack>
                          </AccordionSummary>
                          <AccordionDetails sx={{ px: 2.5, pb: 2.5 }}>
                            <Stack spacing={2.5}>
                              {/* Summary stats */}
                              <Stack direction="row" spacing={2} flexWrap="wrap">
                                {rd.wholeCount > 0 && (
                                  <Paper
                                    elevation={0}
                                    sx={{
                                      p: 1.5,
                                      bgcolor: alpha(theme.palette.success.main, 0.08),
                                      borderRadius: 1,
                                      minWidth: 100,
                                      textAlign: 'center'
                                    }}
                                  >
                                    <Typography variant="h4" color="success.main">
                                      {rd.wholePercent}%
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {intl.formatMessage({ id: 'dataCollection.chip.whole' }, { count: rd.wholeCount })}
                                    </Typography>
                                  </Paper>
                                )}
                                {rd.partialCount > 0 && (
                                  <Paper
                                    elevation={0}
                                    sx={{
                                      p: 1.5,
                                      bgcolor: alpha(theme.palette.warning.main, 0.08),
                                      borderRadius: 1,
                                      minWidth: 100,
                                      textAlign: 'center'
                                    }}
                                  >
                                    <Typography variant="h4" color="warning.main">
                                      {rd.partialPercent}%
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {intl.formatMessage({ id: 'dataCollection.chip.partial' }, { count: rd.partialCount })}
                                    </Typography>
                                  </Paper>
                                )}
                                {rd.noneCount > 0 && (
                                  <Paper
                                    elevation={0}
                                    sx={{
                                      p: 1.5,
                                      bgcolor: alpha(theme.palette.error.main, 0.08),
                                      borderRadius: 1,
                                      minWidth: 100,
                                      textAlign: 'center'
                                    }}
                                  >
                                    <Typography variant="h4" color="error.main">
                                      {rd.nonePercent}%
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {intl.formatMessage({ id: 'dataCollection.chip.none' }, { count: rd.noneCount })}
                                    </Typography>
                                  </Paper>
                                )}
                                {(rd.missedCount || 0) > 0 && (
                                  <Paper
                                    elevation={0}
                                    sx={{
                                      p: 1.5,
                                      bgcolor: alpha(theme.palette.grey[500], 0.08),
                                      borderRadius: 1,
                                      minWidth: 100,
                                      textAlign: 'center'
                                    }}
                                  >
                                    <Typography variant="h4" color="text.disabled">
                                      {rd.missedPercent}%
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {intl.formatMessage({ id: 'dataCollection.chip.missed' }, { count: rd.missedCount })}
                                    </Typography>
                                  </Paper>
                                )}
                              </Stack>

                              <Divider />

                              {/* Interval details */}
                              <Box>
                                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                                  {rd.intervalLength
                                    ? intl.formatMessage({ id: 'dataCollection.field.intervalsWithLength' }, { length: rd.intervalLength })
                                    : intl.formatMessage({ id: 'dataCollection.field.intervalData' })}{' '}
                                  {intl.formatMessage(
                                    { id: 'dataCollection.field.recorded' },
                                    { count: rd.totalRecorded || intervalData.length }
                                  )}
                                </Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                  {intervalData.map((val, i) => (
                                    <Chip
                                      key={i}
                                      label={i + 1}
                                      size="small"
                                      color={colorMap[val] || 'default'}
                                      variant={val === 'none' || val === 'missed' ? 'outlined' : 'filled'}
                                      sx={{ minWidth: 36, fontWeight: 500 }}
                                    />
                                  ))}
                                </Box>
                              </Box>

                              {/* Legend */}
                              <Stack direction="row" spacing={2} flexWrap="wrap">
                                {Object.entries(labelMap).map(([key, label]) => (
                                  <Stack key={key} direction="row" spacing={0.5} alignItems="center">
                                    <Chip
                                      label=""
                                      size="small"
                                      color={colorMap[key]}
                                      variant={key === 'none' || key === 'missed' ? 'outlined' : 'filled'}
                                      sx={{ width: 16, height: 16, '& .MuiChip-label': { display: 'none' } }}
                                    />
                                    <Typography variant="caption" color="text.secondary">
                                      {label}
                                    </Typography>
                                  </Stack>
                                ))}
                              </Stack>

                              {record.notes && (
                                <>
                                  <Divider />
                                  <Box>
                                    <Typography variant="caption" color="text.secondary">
                                      {intl.formatMessage({ id: 'common.notes' })}
                                    </Typography>
                                    <Paper elevation={0} sx={{ p: 1.5, bgcolor: alpha(theme.palette.info.main, 0.08), borderRadius: 1 }}>
                                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                        {record.notes}
                                      </Typography>
                                    </Paper>
                                  </Box>
                                </>
                              )}
                            </Stack>
                          </AccordionDetails>
                        </Accordion>
                      );
                    }

                    // ---- ABC Analysis: custom card view ----
                    if (formType === 'abc') {
                      const rd = record.raw_data || {};
                      const abcRecords = rd.records || [];

                      return (
                        <Accordion
                          key={record.id}
                          sx={{
                            mb: 2,
                            '&::before': { display: 'none' },
                            boxShadow: theme.customShadows?.z1 || 1,
                            borderRadius: '8px !important',
                            overflow: 'hidden'
                          }}
                        >
                          <AccordionSummary
                            expandIcon={<DownOutlined />}
                            sx={{ px: 2.5, py: 1.5, minHeight: 56, '& .MuiAccordionSummary-content': { my: 1 } }}
                          >
                            <Stack direction="row" spacing={2} alignItems="center" sx={{ width: '100%', mr: 2 }}>
                              <Typography variant="h5">{formLabel}</Typography>
                              <Chip
                                label={intl.formatMessage({ id: 'dataCollection.chip.records' }, { count: abcRecords.length })}
                                size="small"
                                color="primary"
                                variant="outlined"
                              />
                              <Box sx={{ flex: 1 }} />
                              <Chip label={collectedTime} size="small" color="primary" variant="outlined" />
                            </Stack>
                          </AccordionSummary>
                          <AccordionDetails sx={{ px: 2.5, pb: 2.5 }}>
                            <Stack spacing={2}>
                              {abcRecords.length === 0 ? (
                                <Typography variant="body2" color="text.secondary">
                                  {intl.formatMessage({ id: 'dataCollection.empty.noAbcRecords' })}
                                </Typography>
                              ) : (
                                <Stack spacing={2}>
                                  {abcRecords.map((r, idx) => (
                                    <Paper
                                      key={idx}
                                      elevation={0}
                                      sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}
                                    >
                                      <Stack spacing={1.5}>
                                        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
                                          {r.time && <Chip icon={<ClockCircleOutlined />} label={r.time} size="small" variant="outlined" />}
                                          {r.setting && <Chip label={r.setting} size="small" color="info" variant="outlined" />}
                                          {r.duration && r.duration !== '0' && (
                                            <Chip
                                              label={intl.formatMessage(
                                                { id: 'dataCollection.chip.durationMin' },
                                                { duration: r.duration }
                                              )}
                                              size="small"
                                              variant="outlined"
                                            />
                                          )}
                                        </Stack>
                                        <Grid container spacing={1.5}>
                                          <Grid size={{ xs: 12, md: 4 }}>
                                            <Paper sx={{ p: 1.5, bgcolor: alpha(theme.palette.primary.main, 0.06), height: '100%' }}>
                                              <Typography
                                                variant="caption"
                                                fontWeight={700}
                                                color="primary.main"
                                                sx={{ mb: 0.5, display: 'block', textTransform: 'uppercase' }}
                                              >
                                                {intl.formatMessage({ id: 'dataCollection.field.antecedent' })}
                                              </Typography>
                                              <Typography variant="body2">{r.antecedent}</Typography>
                                            </Paper>
                                          </Grid>
                                          <Grid size={{ xs: 12, md: 4 }}>
                                            <Paper sx={{ p: 1.5, bgcolor: alpha(theme.palette.warning.main, 0.06), height: '100%' }}>
                                              <Typography
                                                variant="caption"
                                                fontWeight={700}
                                                color="warning.main"
                                                sx={{ mb: 0.5, display: 'block', textTransform: 'uppercase' }}
                                              >
                                                {intl.formatMessage({ id: 'dataCollection.field.behavior' })}
                                              </Typography>
                                              <Typography variant="body2">{r.behavior}</Typography>
                                            </Paper>
                                          </Grid>
                                          <Grid size={{ xs: 12, md: 4 }}>
                                            <Paper sx={{ p: 1.5, bgcolor: alpha(theme.palette.success.main, 0.06), height: '100%' }}>
                                              <Typography
                                                variant="caption"
                                                fontWeight={700}
                                                color="success.main"
                                                sx={{ mb: 0.5, display: 'block', textTransform: 'uppercase' }}
                                              >
                                                {intl.formatMessage({ id: 'dataCollection.field.consequence' })}
                                              </Typography>
                                              <Typography variant="body2">{r.consequence}</Typography>
                                            </Paper>
                                          </Grid>
                                        </Grid>
                                        {r.notes && (
                                          <Paper
                                            elevation={0}
                                            sx={{ p: 1.5, bgcolor: alpha(theme.palette.grey[500], 0.06), borderRadius: 1 }}
                                          >
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                              {intl.formatMessage({ id: 'common.notes' })}
                                            </Typography>
                                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                              {r.notes}
                                            </Typography>
                                          </Paper>
                                        )}
                                      </Stack>
                                    </Paper>
                                  ))}
                                </Stack>
                              )}

                              {record.notes && (
                                <Box>
                                  <Typography variant="caption" color="text.secondary">
                                    {intl.formatMessage({ id: 'dataCollection.field.sessionNotes' })}
                                  </Typography>
                                  <Paper elevation={0} sx={{ p: 1.5, bgcolor: alpha(theme.palette.info.main, 0.08), borderRadius: 1 }}>
                                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                      {record.notes}
                                    </Typography>
                                  </Paper>
                                </Box>
                              )}
                            </Stack>
                          </AccordionDetails>
                        </Accordion>
                      );
                    }

                    // ---- Skill / Target Tracking: custom accordion view ----
                    if (formType === 'skill-tracking') {
                      const rd = record.raw_data || {};
                      const skTargets = rd.targets || [];

                      return (
                        <Accordion
                          key={record.id}
                          sx={{
                            mb: 2,
                            '&::before': { display: 'none' },
                            boxShadow: theme.customShadows?.z1 || 1,
                            borderRadius: '8px !important',
                            overflow: 'hidden'
                          }}
                        >
                          <AccordionSummary
                            expandIcon={<DownOutlined />}
                            sx={{ px: 2.5, py: 1.5, minHeight: 56, '& .MuiAccordionSummary-content': { my: 1 } }}
                          >
                            <Stack direction="row" spacing={2} alignItems="center" sx={{ width: '100%', mr: 2 }}>
                              <Typography variant="h5">{formLabel}</Typography>
                              <Chip
                                label={intl.formatMessage(
                                  { id: 'dataCollection.chip.targets' },
                                  { count: rd.totalTargets || skTargets.length }
                                )}
                                size="small"
                                color="primary"
                                variant="outlined"
                              />
                              {(rd.masteredTargets || 0) > 0 && (
                                <Chip
                                  label={intl.formatMessage({ id: 'dataCollection.chip.mastered' }, { count: rd.masteredTargets })}
                                  size="small"
                                  color="success"
                                />
                              )}
                              <Box sx={{ flex: 1 }} />
                              <Chip label={collectedTime} size="small" color="primary" variant="outlined" />
                            </Stack>
                          </AccordionSummary>
                          <AccordionDetails sx={{ px: 2.5, pb: 2.5 }}>
                            <Stack spacing={2}>
                              {skTargets.length === 0 ? (
                                <Typography variant="body2" color="text.secondary">
                                  {intl.formatMessage({ id: 'dataCollection.empty.noTargets' })}
                                </Typography>
                              ) : (
                                <Stack spacing={1.5}>
                                  {skTargets.map((t, idx) => (
                                    <Paper
                                      key={idx}
                                      elevation={0}
                                      sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}
                                    >
                                      <Stack spacing={1.5}>
                                        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                                          <Typography variant="subtitle1" fontWeight={600}>
                                            {t.targetName ||
                                              intl.formatMessage({ id: 'dataCollection.fallback.targetNumber' }, { number: idx + 1 })}
                                          </Typography>
                                        </Stack>
                                        <Stack direction="row" spacing={1} flexWrap="wrap">
                                          {t.domain && <Chip label={t.domain} size="small" color="secondary" variant="filled" />}
                                          {t.dateIntroduced && (
                                            <Chip
                                              label={intl.formatMessage(
                                                { id: 'dataCollection.chip.introduced' },
                                                { date: new Date(t.dateIntroduced).toLocaleDateString() }
                                              )}
                                              size="small"
                                              color="primary"
                                              variant="outlined"
                                            />
                                          )}
                                          {t.dateMastered && (
                                            <Chip
                                              label={intl.formatMessage(
                                                { id: 'dataCollection.chip.masteredDate' },
                                                { date: new Date(t.dateMastered).toLocaleDateString() }
                                              )}
                                              size="small"
                                              color="success"
                                              variant="outlined"
                                            />
                                          )}
                                          {typeof t.daysToMastery === 'number' && (
                                            <Chip
                                              label={intl.formatMessage(
                                                { id: 'dataCollection.chip.daysToMastery' },
                                                { count: t.daysToMastery }
                                              )}
                                              size="small"
                                              color="warning"
                                              variant="outlined"
                                            />
                                          )}
                                        </Stack>
                                        {(t.location || t.people || t.stimuli) && (
                                          <Stack direction="row" spacing={2} flexWrap="wrap">
                                            {t.location && (
                                              <Typography variant="caption" color="text.secondary">
                                                {intl.formatMessage({ id: 'dataCollection.field.location' })} <strong>{t.location}</strong>
                                              </Typography>
                                            )}
                                            {t.people && (
                                              <Typography variant="caption" color="text.secondary">
                                                {intl.formatMessage({ id: 'dataCollection.field.people' })} <strong>{t.people}</strong>
                                              </Typography>
                                            )}
                                            {t.stimuli && (
                                              <Typography variant="caption" color="text.secondary">
                                                {intl.formatMessage({ id: 'dataCollection.field.stimuli' })} <strong>{t.stimuli}</strong>
                                              </Typography>
                                            )}
                                          </Stack>
                                        )}
                                      </Stack>
                                    </Paper>
                                  ))}
                                </Stack>
                              )}

                              {rd.sessionNotes && (
                                <Box>
                                  <Typography variant="caption" color="text.secondary">
                                    {intl.formatMessage({ id: 'dataCollection.field.sessionNotes' })}
                                  </Typography>
                                  <Paper elevation={0} sx={{ p: 1.5, bgcolor: alpha(theme.palette.info.main, 0.08), borderRadius: 1 }}>
                                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                      {rd.sessionNotes}
                                    </Typography>
                                  </Paper>
                                </Box>
                              )}

                              {record.notes && !rd.sessionNotes && (
                                <Box>
                                  <Typography variant="caption" color="text.secondary">
                                    {intl.formatMessage({ id: 'common.notes' })}
                                  </Typography>
                                  <Paper elevation={0} sx={{ p: 1.5, bgcolor: alpha(theme.palette.info.main, 0.08), borderRadius: 1 }}>
                                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                      {record.notes}
                                    </Typography>
                                  </Paper>
                                </Box>
                              )}
                            </Stack>
                          </AccordionDetails>
                        </Accordion>
                      );
                    }

                    // ---- Toileting Program: custom accordion view ----
                    if (formType === 'toileting-program') {
                      const rd = record.raw_data || {};
                      const tRecords = rd.records || [];
                      const fmtTime = (v) => {
                        try {
                          return new Date(v).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
                        } catch {
                          return '';
                        }
                      };
                      const voidLabel = (v) =>
                        v === 'BM'
                          ? intl.formatMessage({ id: 'dataCollection.toileting.bm' })
                          : v === 'U'
                            ? intl.formatMessage({ id: 'dataCollection.toileting.urine' })
                            : v === 'N'
                              ? intl.formatMessage({ id: 'dataCollection.toileting.noVoid' })
                              : '';
                      const voidColor = (v) => (v === 'BM' ? 'success' : v === 'U' ? 'info' : 'default');
                      const accLabel = (v) =>
                        v === 'S'
                          ? intl.formatMessage({ id: 'dataCollection.toileting.soiled' })
                          : v === 'W'
                            ? intl.formatMessage({ id: 'dataCollection.toileting.wet' })
                            : '';
                      const accColor = (v) => (v === 'S' ? 'error' : v === 'W' ? 'warning' : 'default');
                      const promptLabel = (v) =>
                        v === 'P'
                          ? intl.formatMessage({ id: 'dataCollection.toileting.prompted' })
                          : v === 'U'
                            ? intl.formatMessage({ id: 'dataCollection.toileting.unprompted' })
                            : '';

                      return (
                        <Accordion
                          key={record.id}
                          sx={{
                            mb: 2,
                            '&::before': { display: 'none' },
                            boxShadow: theme.customShadows?.z1 || 1,
                            borderRadius: '8px !important',
                            overflow: 'hidden'
                          }}
                        >
                          <AccordionSummary
                            expandIcon={<DownOutlined />}
                            sx={{ px: 2.5, py: 1.5, minHeight: 56, '& .MuiAccordionSummary-content': { my: 1 } }}
                          >
                            <Stack direction="row" spacing={2} alignItems="center" sx={{ width: '100%', mr: 2 }}>
                              <Typography variant="h5">{formLabel}</Typography>
                              <Chip
                                label={intl.formatMessage({ id: 'dataCollection.status.dataEntry' })}
                                size="small"
                                color="secondary"
                                variant="outlined"
                              />
                              {tRecords.length > 0 && (
                                <Chip
                                  label={intl.formatMessage({ id: 'dataCollection.chip.entries' }, { count: tRecords.length })}
                                  size="small"
                                  color="primary"
                                  variant="outlined"
                                />
                              )}
                              <Box sx={{ flex: 1 }} />
                              <Chip label={collectedTime} size="small" color="primary" variant="outlined" />
                            </Stack>
                          </AccordionSummary>
                          <AccordionDetails sx={{ px: 2.5, pb: 2.5 }}>
                            <Stack spacing={2.5}>
                              {/* Toileting Records */}
                              {tRecords.length > 0 && (
                                <Box>
                                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                                    {intl.formatMessage({ id: 'dataCollection.section.toiletingRecords' })}
                                  </Typography>
                                  <Stack spacing={1}>
                                    {tRecords.map((r, idx) => (
                                      <Paper
                                        key={r.id || idx}
                                        elevation={0}
                                        sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
                                      >
                                        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
                                          <Chip label={fmtTime(r.time)} size="small" variant="outlined" />
                                          {r.toiletVoid && (
                                            <Chip label={voidLabel(r.toiletVoid)} size="small" color={voidColor(r.toiletVoid)} />
                                          )}
                                          {r.accident && r.accident !== 'none' && (
                                            <Chip label={accLabel(r.accident)} size="small" color={accColor(r.accident)} />
                                          )}
                                          {r.prompt && <Chip label={promptLabel(r.prompt)} size="small" color="info" variant="outlined" />}
                                          {r.comments && (
                                            <Typography variant="body2" color="text.secondary">
                                              {r.comments}
                                            </Typography>
                                          )}
                                        </Stack>
                                      </Paper>
                                    ))}
                                  </Stack>
                                </Box>
                              )}

                              {/* Empty state */}
                              {tRecords.length === 0 && (
                                <Typography variant="body2" color="text.secondary">
                                  {intl.formatMessage({ id: 'dataCollection.empty.noToiletingData' })}
                                </Typography>
                              )}

                              {record.notes && (
                                <>
                                  <Divider />
                                  <Box>
                                    <Typography variant="caption" color="text.secondary">
                                      {intl.formatMessage({ id: 'common.notes' })}
                                    </Typography>
                                    <Paper elevation={0} sx={{ p: 1.5, bgcolor: alpha(theme.palette.info.main, 0.08), borderRadius: 1 }}>
                                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                        {record.notes}
                                      </Typography>
                                    </Paper>
                                  </Box>
                                </>
                              )}
                            </Stack>
                          </AccordionDetails>
                        </Accordion>
                      );
                    }

                    // ---- OT Data Sheet: goals + performance level ----
                    if (formType === 'ot-data-sheet') {
                      const rd = record.raw_data || {};
                      const goals = Array.isArray(rd.goals) ? rd.goals.filter((g) => g.performance) : [];
                      /* Localised, and via the same message ids the sheet itself uses, so a
                       * custom area (free text, not a key) falls through to its own text and
                       * an area that is no longer offered still reads as its label. */
                      const otAreaLabel = (a) => (a ? intl.formatMessage({ id: `form.otDataSheet.area.${a}`, defaultMessage: a }) : '—');
                      const otPerfLabel = (p) => (p ? intl.formatMessage({ id: `form.otDataSheet.perf.${p}`, defaultMessage: p }) : '—');
                      const otPerfColor = {
                        i: 'success',
                        minA: 'info',
                        modA: 'warning',
                        maxA: 'error',
                        d: 'default'
                      };
                      return (
                        <Accordion
                          key={record.id}
                          sx={{
                            mb: 2,
                            '&::before': { display: 'none' },
                            boxShadow: theme.customShadows?.z1 || 1,
                            borderRadius: '8px !important',
                            overflow: 'hidden'
                          }}
                          defaultExpanded
                        >
                          <AccordionSummary
                            expandIcon={<DownOutlined />}
                            sx={{ px: 2.5, py: 1.5, minHeight: 56, '& .MuiAccordionSummary-content': { my: 1 } }}
                          >
                            <Stack direction="row" spacing={2} alignItems="center" sx={{ width: '100%', mr: 2 }}>
                              <Typography variant="h5">{formLabel}</Typography>
                              <Chip
                                label={
                                  intl.formatMessage({ id: 'dataCollection.field.goals' }, { count: goals.length }) ||
                                  `${goals.length} goals`
                                }
                                size="small"
                                color="primary"
                                variant="outlined"
                              />
                              <Box sx={{ flex: 1 }} />
                              <Chip label={collectedTime} size="small" color="primary" variant="outlined" />
                            </Stack>
                          </AccordionSummary>
                          <AccordionDetails sx={{ px: 2.5, pb: 2.5 }}>
                            <Stack spacing={1.5}>
                              {goals.length > 0 ? (
                                <Stack spacing={1}>
                                  {goals.map((g, idx) => (
                                    <Paper
                                      key={g.id || idx}
                                      elevation={0}
                                      sx={{ p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1 }}
                                    >
                                      <Stack
                                        direction={{ xs: 'column', sm: 'row' }}
                                        spacing={1.5}
                                        alignItems={{ xs: 'flex-start', sm: 'center' }}
                                      >
                                        <Chip label={otAreaLabel(g.area)} size="small" color="primary" sx={{ minWidth: 56 }} />
                                        <Box sx={{ flex: 1 }}>
                                          <Typography variant="body2" fontWeight={600}>
                                            {g.goal || '—'}
                                          </Typography>
                                          {activityLines(g.activities).length > 0 && (
                                            <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                                              {formatActivityList(g.activities)}
                                            </Typography>
                                          )}
                                        </Box>
                                        <Chip
                                          label={otPerfLabel(g.performance)}
                                          size="small"
                                          color={otPerfColor[g.performance] || 'default'}
                                        />
                                      </Stack>
                                    </Paper>
                                  ))}
                                </Stack>
                              ) : (
                                <Typography variant="body2" color="text.secondary">
                                  {intl.formatMessage({ id: 'form.otDataSheet.empty.title' })}
                                </Typography>
                              )}
                              {(rd.notes || record.notes) && (
                                <Box>
                                  <Typography variant="caption" color="text.secondary">
                                    {intl.formatMessage({ id: 'common.notes' })}
                                  </Typography>
                                  <Paper elevation={0} sx={{ p: 1.5, bgcolor: alpha(theme.palette.info.main, 0.08), borderRadius: 1 }}>
                                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                      {rd.notes || record.notes}
                                    </Typography>
                                  </Paper>
                                </Box>
                              )}
                            </Stack>
                          </AccordionDetails>
                        </Accordion>
                      );
                    }

                    // ---- SLP Data Sheet: goals + correct/total ----
                    if (formType === 'slp-data-sheet') {
                      const rd = record.raw_data || {};
                      const goals = Array.isArray(rd.goals) ? rd.goals.filter((g) => g.total) : [];
                      const slpAreaLabels = {
                        rl: 'RL — Receptive Language',
                        el: 'EL — Expressive Language',
                        art: 'ART — Articulation',
                        flu: 'FLU — Fluency',
                        vo: 'VO — Voice',
                        prag: 'PRAG — Pragmatics',
                        fs: 'FS — Feeding/Swallowing'
                      };
                      const slpAccuracyColor = (correct, total) => {
                        const c = parseInt(correct, 10) || 0;
                        const t = parseInt(total, 10) || 0;
                        if (!t) return 'default';
                        const pct = (c / t) * 100;
                        if (pct >= 80) return 'success';
                        if (pct >= 50) return 'warning';
                        return 'error';
                      };
                      return (
                        <Accordion
                          key={record.id}
                          sx={{
                            mb: 2,
                            '&::before': { display: 'none' },
                            boxShadow: theme.customShadows?.z1 || 1,
                            borderRadius: '8px !important',
                            overflow: 'hidden'
                          }}
                          defaultExpanded
                        >
                          <AccordionSummary
                            expandIcon={<DownOutlined />}
                            sx={{ px: 2.5, py: 1.5, minHeight: 56, '& .MuiAccordionSummary-content': { my: 1 } }}
                          >
                            <Stack direction="row" spacing={2} alignItems="center" sx={{ width: '100%', mr: 2 }}>
                              <Typography variant="h5">{formLabel}</Typography>
                              <Chip label={`${goals.length} goals`} size="small" color="primary" variant="outlined" />
                              <Box sx={{ flex: 1 }} />
                              <Chip label={collectedTime} size="small" color="primary" variant="outlined" />
                            </Stack>
                          </AccordionSummary>
                          <AccordionDetails sx={{ px: 2.5, pb: 2.5 }}>
                            <Stack spacing={1.5}>
                              {goals.length > 0 ? (
                                <Stack spacing={1}>
                                  {goals.map((g, idx) => {
                                    const c = parseInt(g.correct, 10) || 0;
                                    const t = parseInt(g.total, 10) || 0;
                                    const pct = t > 0 ? Math.round((c / t) * 100) : null;
                                    return (
                                      <Paper
                                        key={g.id || idx}
                                        elevation={0}
                                        sx={{ p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1 }}
                                      >
                                        <Stack
                                          direction={{ xs: 'column', sm: 'row' }}
                                          spacing={1.5}
                                          alignItems={{ xs: 'flex-start', sm: 'center' }}
                                        >
                                          <Chip
                                            label={slpAreaLabels[g.area] || g.area || '—'}
                                            size="small"
                                            color="primary"
                                            sx={{ minWidth: 56 }}
                                          />
                                          <Box sx={{ flex: 1 }}>
                                            <Typography variant="body2" fontWeight={600}>
                                              {g.goal || '—'}
                                            </Typography>
                                            {activityLines(g.activities).length > 0 && (
                                              <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                                                {formatActivityList(g.activities)}
                                              </Typography>
                                            )}
                                          </Box>
                                          <Chip label={`${c}/${t}`} size="small" color={slpAccuracyColor(c, t)} />
                                          {pct != null && (
                                            <Chip label={`${pct}%`} size="small" color={slpAccuracyColor(c, t)} variant="outlined" />
                                          )}
                                        </Stack>
                                      </Paper>
                                    );
                                  })}
                                </Stack>
                              ) : (
                                <Typography variant="body2" color="text.secondary">
                                  {intl.formatMessage({ id: 'form.slpDataSheet.empty.title' })}
                                </Typography>
                              )}
                              {(rd.notes || record.notes) && (
                                <Box>
                                  <Typography variant="caption" color="text.secondary">
                                    {intl.formatMessage({ id: 'common.notes' })}
                                  </Typography>
                                  <Paper elevation={0} sx={{ p: 1.5, bgcolor: alpha(theme.palette.info.main, 0.08), borderRadius: 1 }}>
                                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                      {rd.notes || record.notes}
                                    </Typography>
                                  </Paper>
                                </Box>
                              )}
                            </Stack>
                          </AccordionDetails>
                        </Accordion>
                      );
                    }

                    // ---- Generic form card (Cold Probe, Multi-Trial, etc.) ----
                    return (
                      <Accordion
                        key={record.id}
                        sx={{
                          mb: 2,
                          '&::before': { display: 'none' },
                          boxShadow: theme.customShadows?.z1 || 1,
                          borderRadius: '8px !important',
                          overflow: 'hidden'
                        }}
                      >
                        <AccordionSummary
                          expandIcon={<DownOutlined />}
                          sx={{ px: 2.5, py: 1.5, minHeight: 56, '& .MuiAccordionSummary-content': { my: 1 } }}
                        >
                          <Stack direction="row" spacing={2} alignItems="center" sx={{ width: '100%', mr: 2 }}>
                            <Typography variant="h5">{formLabel}</Typography>
                            {record.percentage != null && (
                              <Chip
                                label={`${record.percentage}%`}
                                size="small"
                                color={record.percentage >= 80 ? 'success' : record.percentage >= 50 ? 'warning' : 'error'}
                              />
                            )}
                            {(() => {
                              // Mand count for this record = programme total + any
                              // ad-hoc custom mands (stored separately). Only render
                              // when there's actually something to show.
                              const rd = record.raw_data || {};
                              const custom = Array.isArray(rd.customMands) ? rd.customMands : [];
                              const customTotal = custom.reduce((s, m) => s + (Number(m.prompted) || 0) + (Number(m.independent) || 0), 0);
                              const mandsCount = (rd.totalMands != null ? rd.totalMands : 0) + customTotal;
                              if (rd.totalMands == null && customTotal === 0) return null;
                              return (
                                <Chip
                                  label={intl.formatMessage({ id: 'dataCollection.chip.mands' }, { count: mandsCount })}
                                  size="small"
                                  color="primary"
                                  variant="outlined"
                                />
                              );
                            })()}
                            <Box sx={{ flex: 1 }} />
                            <Chip label={collectedTime} size="small" color="primary" variant="outlined" />
                          </Stack>
                        </AccordionSummary>
                        <AccordionDetails sx={{ px: 2.5, pb: 2.5 }}>
                          <Stack spacing={1.5}>
                            <Stack direction="row" spacing={3} flexWrap="wrap">
                              {record.total_trials != null && (
                                <Box>
                                  <Typography variant="caption" color="text.secondary">
                                    {intl.formatMessage({ id: 'dataCollection.field.totalTrials' })}
                                  </Typography>
                                  <Typography variant="h6">{record.total_trials}</Typography>
                                </Box>
                              )}
                              {record.correct_trials != null && (
                                <Box>
                                  <Typography variant="caption" color="text.secondary">
                                    {intl.formatMessage({ id: 'dataCollection.response.correct' })}
                                  </Typography>
                                  <Typography variant="h6">{record.correct_trials}</Typography>
                                </Box>
                              )}
                              {record.percentage != null && (
                                <Box>
                                  <Typography variant="caption" color="text.secondary">
                                    {intl.formatMessage({ id: 'dataCollection.field.percentage' })}
                                  </Typography>
                                  <Typography
                                    variant="h6"
                                    color={
                                      record.percentage >= 80 ? 'success.main' : record.percentage >= 50 ? 'warning.main' : 'error.main'
                                    }
                                  >
                                    {record.percentage}%
                                  </Typography>
                                </Box>
                              )}
                            </Stack>

                            {record.notes && (
                              <Box>
                                <Typography variant="caption" color="text.secondary">
                                  {intl.formatMessage({ id: 'common.notes' })}
                                </Typography>
                                <Paper elevation={0} sx={{ p: 1.5, bgcolor: alpha(theme.palette.info.main, 0.08), borderRadius: 1 }}>
                                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                    {record.notes}
                                  </Typography>
                                </Paper>
                              </Box>
                            )}

                            {record.raw_data && (
                              <>
                                {record.raw_data.targets && record.raw_data.targets.length > 0 && (
                                  <Box>
                                    <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                                      {intl.formatMessage({ id: 'dataCollection.field.targets' })}
                                    </Typography>
                                    <Stack spacing={0.5}>
                                      {record.raw_data.targets.map((t, idx) => (
                                        <Stack key={idx} direction="row" spacing={1} alignItems="center">
                                          <Chip
                                            label={
                                              t.response === '+'
                                                ? intl.formatMessage({ id: 'dataCollection.response.correct' })
                                                : t.response === '-'
                                                  ? intl.formatMessage({ id: 'dataCollection.response.incorrect' })
                                                  : t.response || '—'
                                            }
                                            size="small"
                                            color={
                                              t.response === '+' || t.response === 'correct'
                                                ? 'success'
                                                : t.response === '-' || t.response === 'incorrect'
                                                  ? 'error'
                                                  : 'default'
                                            }
                                          />
                                          <Typography variant="body2">
                                            {t.targetName ||
                                              intl.formatMessage({ id: 'dataCollection.fallback.targetNumber' }, { number: idx + 1 })}
                                          </Typography>
                                          {t.notes && (
                                            <Typography variant="caption" color="text.secondary">
                                              ({t.notes})
                                            </Typography>
                                          )}
                                        </Stack>
                                      ))}
                                    </Stack>
                                  </Box>
                                )}

                                {record.raw_data.behaviors && record.raw_data.behaviors.length > 0 && (
                                  <Box>
                                    <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                                      {intl.formatMessage({ id: 'dataCollection.field.behaviors' })}
                                    </Typography>
                                    <Stack spacing={0.5}>
                                      {record.raw_data.behaviors.map((b, idx) => (
                                        <Stack key={idx} direction="row" spacing={1} alignItems="center">
                                          <Typography variant="body2">
                                            {b.name ||
                                              b.behaviorName ||
                                              intl.formatMessage({ id: 'dataCollection.fallback.behaviorNumber' }, { number: idx + 1 })}
                                          </Typography>
                                          {b.count != null && <Chip label={`${b.count}x`} size="small" color="primary" />}
                                        </Stack>
                                      ))}
                                    </Stack>
                                  </Box>
                                )}

                                {record.raw_data.occurrences && record.raw_data.occurrences.length > 0 && !record.raw_data.totalMands && (
                                  <Box>
                                    <Typography variant="caption" color="text.secondary">
                                      {intl.formatMessage(
                                        { id: 'dataCollection.chip.occurrences' },
                                        { count: record.raw_data.occurrences.length }
                                      )}
                                    </Typography>
                                  </Box>
                                )}
                              </>
                            )}
                          </Stack>
                        </AccordionDetails>
                      </Accordion>
                    );
                  })}
                </>
              )}
            </>
          )}
        </Box>
      )}

      {/* Data Collection Mode - All forms rendered simultaneously, only active tab visible */}
      {/* This preserves form state (trial data, entries, etc.) when switching between tabs */}
      {!viewExistingData && (
        <Box
          sx={{
            mb: 3,
            overflowX: 'hidden',
            maxWidth: '100%',
            width: '100%'
          }}
        >
          {availableForms.map((form) => {
            const config = formComponents[form.type];
            if (!config) return null;
            const { Component, props: extraProps } = config;
            const isOverflowOpen = overflowFormType === form.type;
            // Overflow forms render inside the Dialog (below) — hide the
            // inline copy so we don't double-mount. When the modal closes,
            // the form re-mounts inline and remains hidden (display: none).
            const visibleInline = !isOverflowOpen && currentTab === form.type;
            if (isOverflowOpen) return null;
            return (
              <Box key={form.type} sx={{ display: visibleInline ? 'block' : 'none' }}>
                {sheetReadOnly(form.type) && <ReadOnlyBanner code={FORM_TYPE_TO_PAGE_CODE[form.type]} />}
                {/* Full-Read on this sheet: disable the whole sheet (native inputs via the
                    disabled fieldset, MUI Selects/click targets via pointerEvents:none) so it
                    is view-only. triggerAutoSave is also guarded, and the backend refuses writes. */}
                <Box
                  component="fieldset"
                  disabled={sheetReadOnly(form.type)}
                  sx={{ border: 0, p: 0, m: 0, minWidth: 0, ...(sheetReadOnly(form.type) && { pointerEvents: 'none' }) }}
                >
                  <Component
                    viewMode={viewModeFor(form.type)}
                    initialData={lastFormDataRef.current[form.type]}
                    onDataChange={(formType, formData) => triggerAutoSave?.(formType || form.type, formData)}
                    {...extraProps}
                    {...commonFormProps}
                  />
                </Box>
              </Box>
            );
          })}
        </Box>
      )}

      {/* Overflow Form Modal — opened from the More menu next to the tabs.
          Renders one of the secondary data sheets in a large popup. Save
          dismisses the dialog (autosave persists data); backdrop/ESC close
          while dirty triggers a confirm dialog so the therapist doesn't lose
          input. */}
      <Dialog
        open={Boolean(overflowFormType)}
        onClose={(_event, reason) => {
          // Treat backdrop/ESC like cancel: warn if dirty.
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
            requestCloseOverflowModal();
            return;
          }
          requestCloseOverflowModal();
        }}
        maxWidth="lg"
        fullWidth
        fullScreen={overflowDialogFullScreen}
        scroll="paper"
      >
        <DialogTitle sx={{ pr: 6 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Typography variant="h4">{overflowForms.find((f) => f.type === overflowFormType)?.name || ''}</Typography>
              {overflowFormDirty && (
                <Chip
                  size="small"
                  color="warning"
                  label={intl.formatMessage({ id: 'dataCollection.overflow.unsaved', defaultMessage: 'Unsaved changes' })}
                />
              )}
            </Stack>
            <IconButton onClick={requestCloseOverflowModal} size="small" aria-label="Close">
              <CloseOutlined />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent dividers sx={{ p: { xs: 2, sm: 3 } }}>
          {availableForms.map((form) => {
            const config = formComponents[form.type];
            if (!config) return null;
            if (overflowFormType !== form.type) return null;
            const { Component, props: extraProps } = config;
            // Force grid view inside the overflow modal — that's the view that
            // exposes the Data Entry / Historical Data tabs and summary cards.
            // The individual view is a stripped-down list that hides historic
            // entries.
            // (Phones: Individual everywhere, like the tabs.)
            const modalViewMode =
              gridAllowed && (form.type === 'behavior-interval' || form.type === 'injury-report' || form.type === 'abc')
                ? 'grid'
                : viewModeFor(form.type);
            const overflowReadOnly = sheetReadOnly(form.type);
            return (
              <Box key={form.type}>
                {overflowReadOnly && <ReadOnlyBanner code={FORM_TYPE_TO_PAGE_CODE[form.type]} />}
                <Box
                  component="fieldset"
                  disabled={overflowReadOnly}
                  sx={{ border: 0, p: 0, m: 0, minWidth: 0, ...(overflowReadOnly && { pointerEvents: 'none' }) }}
                >
                  <Component
                    viewMode={modalViewMode}
                    initialData={lastFormDataRef.current[form.type]}
                    onDataChange={(formType, formData) => {
                      setOverflowFormDirty(true);
                      // Track in-progress recording for behavior-interval so the
                      // outer Save click can prompt the therapist to commit the
                      // recording. Other form types don't surface this flag, so
                      // it stays false for them.
                      if ((formType || form.type) === 'behavior-interval') {
                        setOverflowRecordingActive(Boolean(formData?.recordingInProgress));
                      }
                      // Default the form-type to this overflow form so the
                      // sheet-has-data indicator (the green tick in the More
                      // menu) attaches to the right tile. Some forms — e.g.
                      // InjuryReportMode — call onDataChange() with no args.
                      return triggerAutoSave?.(formType || form.type, formData);
                    }}
                    {...(form.type === 'behavior-interval' ? { actionsRef: behaviorIntervalActionsRef } : {})}
                    {...extraProps}
                    {...commonFormProps}
                  />
                </Box>
              </Box>
            );
          })}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          {overflowFormType === 'injury-report' ? (
            // Injury Report has its own Save button on the form (autosave isn't
            // used here — saves happen explicitly inside the form). Showing
            // another "Save" in the modal footer confuses therapists into
            // thinking it persists their in-progress entry. Replace both
            // footer buttons with a single "Close" so the inner form's Save is
            // the only save affordance.
            <Button onClick={handleSaveOverflowModal} color="inherit">
              {intl.formatMessage({ id: 'common.close' })}
            </Button>
          ) : (
            <>
              <Button onClick={requestCloseOverflowModal} color="inherit">
                {intl.formatMessage({ id: 'common.cancel' })}
              </Button>
              <Button
                onClick={() => {
                  // Recording mid-flight → route through a confirm dialog so
                  // the therapist explicitly chooses to end + commit before
                  // we close. Otherwise the recording would be lost.
                  if (overflowRecordingActive) {
                    setConfirmEndAndSaveOpen(true);
                  } else {
                    handleSaveOverflowModal();
                  }
                }}
                variant="contained"
                startIcon={<SaveOutlined />}
                disabled={Boolean(overflowFormType) && sheetReadOnly(overflowFormType)}
              >
                {intl.formatMessage({ id: 'common.save' })}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Confirm-close dialog: shown whenever the user attempts to dismiss the
          overflow modal via backdrop / ESC. Forces an explicit choice — the
          dialog itself disables backdrop close so the user must pick Cancel
          (discard + close) or Save (persist + close). */}
      <Dialog
        open={confirmCloseOverflowOpen}
        onClose={(_event, reason) => {
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
          setConfirmCloseOverflowOpen(false);
        }}
        disableEscapeKeyDown
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" spacing={1} alignItems="center">
            <WarningOutlined style={{ color: theme.palette.warning.main }} />
            <Typography variant="h5">
              {intl.formatMessage({
                id: 'dataCollection.overflow.discardTitle',
                defaultMessage: 'Data will be lost'
              })}
            </Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {intl.formatMessage({
              id: 'dataCollection.overflow.discardBody',
              defaultMessage: 'You are about to close this data sheet. Click Save to keep your data, or Cancel to discard the changes.'
            })}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setConfirmCloseOverflowOpen(false)} color="inherit">
            {intl.formatMessage({
              id: 'dataCollection.overflow.keepEditing',
              defaultMessage: 'Continue editing'
            })}
          </Button>
          <Button onClick={discardOverflowChanges} color="warning" variant="outlined">
            {intl.formatMessage({ id: 'common.cancel' })}
          </Button>
          <Button onClick={handleSaveOverflowModal} variant="contained" startIcon={<SaveOutlined />}>
            {intl.formatMessage({ id: 'common.save' })}
          </Button>
        </DialogActions>
      </Dialog>

      {/* End-and-Save confirm dialog: opens when the therapist clicks the
          outer Save while a Behavior Interval recording is still running.
          "End & Save" calls the imperative action on BehaviorIntervalMode to
          stop the timer, mark the trailing interval as missed, commit the
          session, and close the modal. */}
      <Dialog open={confirmEndAndSaveOpen} onClose={() => setConfirmEndAndSaveOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>
          <Stack direction="row" spacing={1} alignItems="center">
            <WarningOutlined style={{ color: theme.palette.warning.main }} />
            <Typography variant="h5">
              {intl.formatMessage({
                id: 'dataCollection.overflow.endAndSaveTitle',
                defaultMessage: 'Recording in progress'
              })}
            </Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {intl.formatMessage({
              id: 'dataCollection.overflow.endAndSaveBody',
              defaultMessage:
                'The interval recording is still running. End the session now to save this recording — otherwise nothing will be saved.'
            })}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setConfirmEndAndSaveOpen(false)} color="inherit">
            {intl.formatMessage({
              id: 'dataCollection.overflow.keepRecording',
              defaultMessage: 'Continue recording'
            })}
          </Button>
          <Button
            onClick={async () => {
              const result = behaviorIntervalActionsRef.current?.endAndSaveCurrentRecording?.();
              setConfirmEndAndSaveOpen(false);
              if (result?.saved) {
                // The imperative action fired onDataChange synchronously,
                // which scheduled a debounced auto-save. Flush it now so
                // the record lives in IndexedDB before the modal closes —
                // otherwise reopening within the 2 s debounce window would
                // show no entry.
                try {
                  if (autoSaveTimeoutRef.current) {
                    clearTimeout(autoSaveTimeoutRef.current);
                    autoSaveTimeoutRef.current = null;
                  }
                  if (pendingSaveRef.current) {
                    const { activeForm, formData } = pendingSaveRef.current;
                    await persistToIndexedDB(activeForm, formData);
                    pendingSaveRef.current = null;
                    setAutoSaveStatus('saved');
                  }
                } catch (err) {
                  console.error('[EndAndSave] Failed to flush IndexedDB:', err);
                }
                handleSaveOverflowModal();
              } else {
                // Nothing was recorded — reset the gate but leave the modal
                // open so the therapist can decide what to do next.
                setOverflowRecordingActive(false);
              }
            }}
            variant="contained"
            color="success"
            startIcon={<SaveOutlined />}
          >
            {intl.formatMessage({
              id: 'dataCollection.overflow.endAndSave',
              defaultMessage: 'End & Save'
            })}
          </Button>
        </DialogActions>
      </Dialog>

      {/* CSS for overflow prevention */}
      <style>
        {`
          /* Prevent horizontal scroll on mobile - use on html to not break sticky */
          html {
            overflow-x: hidden !important;
          }
        `}
      </style>

      {/* The diary, over whatever is on screen — the session tabs or the summary above. */}
      {diaryOpen && childId && <CommunicationDiaryTab childId={childId} onClose={() => setDiaryOpen(false)} />}

      {/* Submit Confirmation Modal.

          The focus trap and Escape are lifted while the diary is open over it, or MUI would
          pull focus out of the diary's textarea and Escape would dismiss the summary
          underneath instead of the diary on top. */}
      <Dialog
        open={submitModalOpen}
        onClose={handleCancelSubmit}
        maxWidth="md"
        fullWidth
        fullScreen={downSM}
        disableEnforceFocus={diaryOpen}
        disableEscapeKeyDown={diaryOpen}
      >
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h4">
              {sessionDetails
                ? intl.formatMessage({ id: 'dataCollection.dialog.endSessionTitle' })
                : intl.formatMessage({ id: 'dataCollection.dialog.reviewSubmitTitle' })}
            </Typography>
            <Button variant="text" color="inherit" onClick={handleCancelSubmit} sx={{ minWidth: 'auto', p: 1 }}>
              <CloseOutlined />
            </Button>
          </Stack>
        </DialogTitle>

        <DialogContent dividers>
          <Stack spacing={3}>
            {/* Warning Alert */}
            <Alert severity="warning" icon={<WarningOutlined />}>
              <Typography variant="body2" fontWeight={600}>
                {intl.formatMessage({ id: 'dataCollection.dialog.submitWarning' })}
              </Typography>
            </Alert>

            {/* Session Info - Only show when coming from calendar */}
            {sessionDetails && (
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  bgcolor: alpha(theme.palette.primary.main, 0.05),
                  borderRadius: 2,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`
                }}
              >
                <Stack spacing={2}>
                  {/* Session Details Header */}
                  <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                    <Stack direction="row" spacing={1} alignItems="center">
                      <CalendarOutlined style={{ color: theme.palette.primary.main }} />
                      <Typography variant="body2" fontWeight={500}>
                        {sessionDetails.date
                          ? new Date(sessionDetails.date).toLocaleDateString('en-US', {
                              weekday: 'short',
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric'
                            })
                          : intl.formatMessage({ id: 'common.notAvailable' })}
                      </Typography>
                    </Stack>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <ClockCircleOutlined style={{ color: theme.palette.primary.main }} />
                      <Typography variant="body2" fontWeight={500}>
                        {sessionDetails.startTime} - {sessionDetails.endTime}
                      </Typography>
                    </Stack>
                    <Chip label={sessionDetails.program} size="small" color="primary" variant="outlined" />
                  </Stack>

                  {/* The pre-session note is NOT repeated here. It is already on the session
                      header behind this dialog, and the Communication Diary button below opens
                      the record it came from — a third copy, read-only, only pushed the one
                      field anybody still has to fill in (the session note) further down. */}

                  {/* Session Notes - Editable */}
                  <Box>
                    {/* Dictatable: the therapist is often still with the child when the
                        session ends, so speaking the note beats typing it. Falls back to
                        a plain field where speech recognition is unavailable. */}
                    <SpeechTextField
                      fullWidth
                      multiline
                      rows={3}
                      label={intl.formatMessage({ id: 'dataCollection.field.sessionNotes' })}
                      placeholder={intl.formatMessage({ id: 'dataCollection.placeholder.sessionNotes' })}
                      value={sessionNotesDraft}
                      onChange={(e) => {
                        setSessionNotesDraft(e.target.value);
                        sessionNotesRef.current = e.target.value;
                      }}
                    />
                  </Box>
                </Stack>
              </Paper>
            )}

            {/* Filter Dropdown */}
            <FormControl fullWidth size="small">
              <InputLabel>{intl.formatMessage({ id: 'dataCollection.field.viewDataSheet' })}</InputLabel>
              <Select
                value={selectedSheetFilter}
                label={intl.formatMessage({ id: 'dataCollection.field.viewDataSheet' })}
                onChange={(e) => setSelectedSheetFilter(e.target.value)}
              >
                <MenuItem value="all">{intl.formatMessage({ id: 'dataCollection.field.allDataSheets' })}</MenuItem>
                {availableForms
                  .filter((form) => !form.comingSoon)
                  .map((form) => (
                    <MenuItem key={form.type} value={form.type}>
                      {form.name}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>

            {/* Data Sheet Status List - Filtered by selection (exclude Coming Soon) */}
            {availableForms
              .filter((form) => !form.comingSoon && (selectedSheetFilter === 'all' || selectedSheetFilter === form.type))
              .map((form) => (
                <Box key={form.type}>
                  <MainCard
                    sx={{
                      bgcolor: dataCollectionState[form.type]?.hasData ? alpha(theme.palette.success.main, 0.08) : 'background.default',
                      borderLeft: dataCollectionState[form.type]?.hasData
                        ? `4px solid ${theme.palette.success.main}`
                        : `4px solid ${theme.palette.grey[300]}`
                    }}
                  >
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="h6">{form.name}</Typography>
                      {dataCollectionState[form.type]?.hasData ? (
                        <Chip
                          label={intl.formatMessage({ id: 'dataCollection.status.dataEntered' })}
                          color="success"
                          size="small"
                          icon={<CheckOutlined />}
                        />
                      ) : (
                        <Chip
                          label={intl.formatMessage({ id: 'dataCollection.status.notProcessed' })}
                          color="default"
                          size="small"
                          variant="outlined"
                        />
                      )}
                    </Stack>
                  </MainCard>
                </Box>
              ))}
          </Stack>
        </DialogContent>

        <DialogActions sx={{ p: 2.5, flexDirection: 'column', alignItems: 'stretch', gap: 1 }}>
          {!hasAnyData && !sessionDetails && (
            <Alert severity="info" sx={{ width: '100%' }}>
              <Typography variant="body2">{intl.formatMessage({ id: 'dataCollection.dialog.noDataWarning' })}</Typography>
            </Alert>
          )}
          {/* useFlexGap + wrap for the same reason as the calendar dialog's footer: three
              actions in a row need a real gap that survives wrapping on a narrow screen. */}
          <Stack direction="row" spacing={1.5} useFlexGap flexWrap="wrap" justifyContent="flex-end" sx={{ width: '100%' }}>
            {/* The last moment anyone is looking at this session: what was collected is on
                screen, and the note home is written from the same picture. Left-aligned and
                outlined so it reads as a place to go, not as a step in ending the session. */}
            {diaryAvailable && (
              <Button
                variant="outlined"
                color="primary"
                onClick={() => setDiaryOpen(true)}
                startIcon={<MessageOutlined />}
                sx={{ mr: 'auto' }}
              >
                {intl.formatMessage({ id: 'dataCollection.dialog.openDiary', defaultMessage: 'Communication Diary' })}
              </Button>
            )}
            <Button variant="outlined" onClick={handleCancelSubmit} startIcon={<CloseOutlined />}>
              {intl.formatMessage({ id: 'common.cancel' })}
            </Button>
            <Button
              variant="contained"
              color={sessionDetails ? 'error' : 'success'}
              onClick={handleConfirmSubmit}
              startIcon={sessionDetails ? <StopOutlined /> : <CheckOutlined />}
              disabled={isSubmitting || (!hasAnyData && !sessionDetails)}
            >
              {isSubmitting
                ? intl.formatMessage({ id: 'common.submitting' })
                : sessionDetails
                  ? intl.formatMessage({ id: 'dataCollection.button.endSessionSubmit' })
                  : intl.formatMessage({ id: 'dataCollection.button.confirmSubmit' })}
            </Button>
          </Stack>
        </DialogActions>
      </Dialog>

      {/* Login-to-Sync Dialog — inline login form shown when ending session in offline mode with internet available */}
      <Dialog
        open={loginSyncDialogOpen}
        onClose={() => {
          if (!loginLoading) {
            setLoginSyncDialogOpen(false);
            setLoginError('');
            setLoginEmail('');
            setLoginPassword('');
            setLoginSuccess(false);
          }
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" spacing={1} alignItems="center">
            <CloudUploadOutlined style={{ color: theme.palette.success.main }} />
            <Typography variant="h5">{intl.formatMessage({ id: 'dataCollection.dialog.internetAvailableTitle' })}</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Alert severity="success" icon={<SyncOutlined />}>
              {intl.formatMessage({ id: 'dataCollection.dialog.dataSavedLocally' })}
            </Alert>

            {loginSuccess ? (
              <Alert severity="success" icon={<CheckCircleOutlined />}>
                {intl.formatMessage({ id: 'dataCollection.dialog.loginSyncSuccess' })}
              </Alert>
            ) : (
              <>
                <Typography variant="body2" color="text.secondary">
                  {intl.formatMessage({ id: 'dataCollection.dialog.loginSyncPrompt' })}
                </Typography>

                <Stack spacing={2}>
                  <Stack spacing={0.5}>
                    <InputLabel htmlFor="sync-login-email">{intl.formatMessage({ id: 'dataCollection.field.email' })}</InputLabel>
                    <OutlinedInput
                      id="sync-login-email"
                      type="email"
                      value={loginEmail}
                      onChange={(e) => setLoginEmail(e.target.value)}
                      placeholder={intl.formatMessage({ id: 'dataCollection.placeholder.email' })}
                      fullWidth
                      size="small"
                      disabled={loginLoading}
                      autoComplete="off"
                    />
                  </Stack>
                  <Stack spacing={0.5}>
                    <InputLabel htmlFor="sync-login-password">{intl.formatMessage({ id: 'dataCollection.field.password' })}</InputLabel>
                    <OutlinedInput
                      id="sync-login-password"
                      type="password"
                      value={loginPassword}
                      onChange={(e) => setLoginPassword(e.target.value)}
                      placeholder={intl.formatMessage({ id: 'dataCollection.placeholder.password' })}
                      fullWidth
                      size="small"
                      disabled={loginLoading}
                      autoComplete="off"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && loginEmail && loginPassword && !loginLoading) {
                          handleLoginAndSync();
                        }
                      }}
                    />
                  </Stack>
                </Stack>

                {loginError && <Alert severity="error">{loginError}</Alert>}
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          {loginSuccess ? (
            <Button
              variant="contained"
              onClick={() => {
                setLoginSyncDialogOpen(false);
                setLoginSuccess(false);
                navigate('/therapist/my-calendar');
              }}
            >
              {intl.formatMessage({ id: 'dataCollection.button.goToCalendar' })}
            </Button>
          ) : (
            <>
              <Button
                variant="outlined"
                disabled={loginLoading}
                onClick={() => {
                  setLoginSyncDialogOpen(false);
                  setLoginError('');
                  setLoginEmail('');
                  setLoginPassword('');
                  navigate('/offline/my-calendar');
                }}
              >
                {intl.formatMessage({ id: 'dataCollection.button.saveForLater' })}
              </Button>
              <Button
                variant="contained"
                startIcon={loginLoading ? <CircularProgress size={18} color="inherit" /> : <LoginOutlined />}
                disabled={loginLoading || !loginEmail || !loginPassword}
                onClick={handleLoginAndSync}
              >
                {loginLoading
                  ? intl.formatMessage({ id: 'login.loggingIn' })
                  : intl.formatMessage({ id: 'dataCollection.button.loginAndSync' })}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Switch Child Confirmation Modal */}
      <Dialog open={switchChildModalOpen} onClose={handleCancelChildSwitch} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Stack direction="row" spacing={1} alignItems="center">
            <WarningOutlined style={{ color: theme.palette.warning.main }} />
            <Typography variant="h5">{intl.formatMessage({ id: 'dataCollection.dialog.switchChildTitle' })}</Typography>
          </Stack>
        </DialogTitle>

        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2">{intl.formatMessage({ id: 'dataCollection.dialog.switchChildWarning' })}</Typography>
          </Alert>
          <Typography variant="body2">
            {intl.formatMessage(
              { id: 'dataCollection.dialog.switchChildConfirm' },
              { from: child?.name, to: allChildren.find((c) => c.id.toString() === pendingChildId)?.name }
            )}
          </Typography>
        </DialogContent>

        <DialogActions sx={{ p: 2.5 }}>
          <Button variant="outlined" onClick={handleCancelChildSwitch}>
            {intl.formatMessage({ id: 'common.cancel' })}
          </Button>
          <Button variant="contained" color="warning" onClick={handleConfirmChildSwitch} startIcon={<CheckOutlined />}>
            {intl.formatMessage({ id: 'dataCollection.button.yesSwitchChild' })}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Target view — the Target Tracking datasheet UI plus an "Add Target"
          button that opens the Goals & Targets TargetDialog in goal-select mode. Lets
          a therapist drop a new target onto any of the child's active goals mid-session
          without leaving the data-collection page. */}
      <Dialog open={addTargetViewOpen} onClose={() => setAddTargetViewOpen(false)} maxWidth="lg" fullWidth fullScreen={downMd}>
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h4">
              {intl.formatMessage({ id: 'dataCollection.overflow.addTarget', defaultMessage: 'Add Target' })}
            </Typography>
            <IconButton onClick={() => setAddTargetViewOpen(false)} size="small" aria-label={intl.formatMessage({ id: 'common.close' })}>
              <CloseOutlined />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent dividers sx={{ p: { xs: 2, sm: 3 } }}>
          <TargetTrackingMode
            trackingTargets={trackingTargets}
            sessionDate={sessionDetails?.date || null}
            childId={childId}
            skills={targetSkills}
            onTargetsChanged={() => loadClinicalDataRef.current?.(childId)}
            addTargetGoals={addTargetCtx.goals}
            onAddTarget={handleAddTargetSave}
          />
        </DialogContent>
      </Dialog>

      {/* Add-Behavior shortcut — the child's behaviors (paginated) with the Behaviors
          tab's own Add / Edit dialog. A save reloads the clinical data (and the
          offline cache) so Behavior Tracking / Interval / ABC list it straight away. */}
      <SessionBehaviorsDialog
        open={behaviorsOpen}
        onClose={() => setBehaviorsOpen(false)}
        versionId={iepVersionId}
        onChanged={() => loadClinicalData(childId)}
      />

      {/* Navigation Guard — Confirm before leaving active session */}
      <Dialog open={navGuardOpen} onClose={handleNavGuardCancel} maxWidth="xs" fullWidth>
        <DialogTitle>
          <Stack direction="row" spacing={1} alignItems="center">
            <WarningOutlined style={{ color: theme.palette.warning.main }} />
            <Typography variant="h5">{intl.formatMessage({ id: 'dataCollection.dialog.leaveSessionTitle' })}</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2">{intl.formatMessage({ id: 'dataCollection.dialog.navGuardWarning' })}</Typography>
          </Alert>
          <Typography variant="body2">{intl.formatMessage({ id: 'dataCollection.dialog.navGuardConfirm' })}</Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button variant="outlined" onClick={handleNavGuardCancel}>
            {intl.formatMessage({ id: 'dataCollection.button.noStay' })}
          </Button>
          <Button variant="contained" color="error" onClick={handleNavGuardConfirm} startIcon={<StopOutlined />}>
            {intl.formatMessage({ id: 'dataCollection.button.yesEndSession' })}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

DataCollection.propTypes = {
  isOfflineMode: PropTypes.bool,
  offlineChildren: PropTypes.array,
  offlineUser: PropTypes.object,
  isOnline: PropTypes.bool,
  unsyncedCount: PropTypes.number,
  onFormSaved: PropTypes.func
};

export default DataCollection;
